import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  ListGroup,
  Dropdown,
  Table
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaList,
  FaExchangeAlt,
  FaHistory,
  FaChevronUp,
  FaChevronDown
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import * as constants from '../../constants/CONSTANT';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import SchemaApi from '../../api/SchemaApi';
import PicklistValueManager from './PicklistValueManager';
import DependentFieldManager from './DependentFieldManager';
import FieldHistoryFieldMapping from './FieldHistoryFieldMapping';
import FieldMappingManager from './FieldMappingManager';
import {
  isLeadFieldExcludedFromMapping,
  isStandardConversionSkipsLeadNameMapping,
  normalizeLeadConvertStatusFromLeadConversionConfig,
  normalizeLeadConvertStatusFromApiData,
  leadConvertStatusSelectKeyFromStored,
} from './leadFieldMappingUtils';
import AppToast from '../AppToast';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/FieldManager.css';
import { getEntitlementsSection } from '../../utils/entitlements';
import { useProductBuilderMode } from '../../utils/productBuilderMode';
import { formatDateTime } from '../../utils/formatUtils';
import {
  buildFieldConfigPayload,
  canEnableFieldMasking,
  parseFieldConfig,
} from '../../utils/fieldConfigUtils';
import SharingRuleCriteriaConfig from '../setup/sharingrule/SharingRuleCriteriaConfig';
import ConditionRow from '../common/ConditionRow';
import { useConditionValueFields } from '../../hooks/useConditionValueFields';
import {
  isCompleteListViewFilter,
  isOperatorValueRequired,
} from '../../utils/conditionBuilderUtils';
import {
  defaultCustomFilterLogicExpression,
  isSimpleFilterLogic,
  normalizeStoredLookupFilterLogic,
} from '../../utils/lookupFilterUtils';
import {
  FILTER_VALUE_TYPE_STATIC,
  getFilterValueType,
  withInferredFilterValueType,
} from '../../utils/filterFieldValueUtils';

const emptyLookupFilterRow = () => ({
  field: '',
  operator: 'equals',
  value: '',
  valueType: FILTER_VALUE_TYPE_STATIC,
});

const emptyRollupCriterion = () => ({
  fieldName: '',
  operator: '=',
  value: '',
  valueType: FILTER_VALUE_TYPE_STATIC,
});

const normalizeRollupOperatorForUi = (operator) => {
  const op = String(operator || '=').trim();
  const lower = op.toLowerCase().replace(/\s+/g, '_');
  const map = {
    equals: '=',
    '=': '=',
    not_equals: '!=',
    '!=': '!=',
    greater_than: '>',
    '>': '>',
    greater_than_or_equal: '>=',
    greater_than_or_equals: '>=',
    '>=': '>=',
    less_than: '<',
    '<': '<',
    less_than_or_equal: '<=',
    less_than_or_equals: '<=',
    '<=': '<=',
    in: 'IN',
    not_in: 'NOT IN',
    contains: 'contains',
    not_contains: 'not_contains',
    starts_with: 'starts_with',
    ends_with: 'ends_with',
    is_null: 'is_null',
    is_not_null: 'is_not_null',
  };
  if (map[lower] != null) return map[lower];
  if (op === 'IN' || op === 'NOT IN') return op;
  return op;
};

const NUMERIC_RETURN_TYPES = ['number', 'currency', 'percent'];
/** Max decimal places for number, currency, and percent custom fields (PostgreSQL NUMERIC scale). */
const MAX_NUMERIC_DECIMAL_PLACES = 8;

const sortFieldOptionsByLabelAsc = (options) =>
  [...options].sort((a, b) =>
    String(a.label || a.value).localeCompare(String(b.label || b.value), undefined, {
      sensitivity: 'base',
    })
  );

const CALCULATED_RETURN_TYPE_OPTIONS = sortFieldOptionsByLabelAsc([
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'image', label: 'Image' },
  { value: 'url', label: 'URL' },
  { value: 'qrcode', label: 'QR Code' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date/Time' },
]);

/** Data Type dropdown options (sorted A–Z by label). */
const FIELD_DATA_TYPE_OPTIONS = sortFieldOptionsByLabelAsc([
  { value: 'autonumber', label: 'Auto Number' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'time', label: 'Time' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'email', label: 'Email' },
  { value: 'image', label: 'Image' },
  { value: 'jsonb', label: 'JSONB' },
  { value: 'lookup', label: 'Lookup' },
  { value: 'multipicklist', label: 'Multi Picklist' },
  { value: 'number', label: 'Number' },
  { value: 'password', label: 'Password' },
  { value: 'phone', label: 'Phone' },
  { value: 'picklist', label: 'Picklist' },
  { value: 'qrcode', label: 'QR Code' },
  { value: 'richtext', label: 'Rich Text' },
  { value: 'rollupsummary', label: 'Rollup Summary' },
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'url', label: 'URL' },
]);

const DEFAULT_CURRENCY_SYMBOL = '₹';

/** Build { key, value } rows from a field — uses picklist_values_data and/or picklist_values already loaded in fetchFields. */
function resolveLeadStatusPicklistOptionsFromField(field) {
  if (!field) return [];

  const normalizeRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((pv) => {
        const key = String(pv.key ?? pv.value_key ?? '').trim();
        const value = String(pv.value ?? pv.value_label ?? pv.label ?? '').trim();
        return {
          key: key || value,
          value: value || key,
        };
      })
      .filter((o) => o.key);
  };

  if (field.picklist_values_data && Array.isArray(field.picklist_values_data)) {
    const fromData = normalizeRows(
      field.picklist_values_data.filter((pv) => pv.is_active !== false)
    );
    if (fromData.length > 0) return fromData;
  }

  let raw = field.picklist_values;
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string' && raw.trim()) {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [{ key: raw, value: raw }];
    }
  }
  return normalizeRows(raw);
}

function findLeadStatusFieldForConvertConfig(fields) {
  const list = Array.isArray(fields) ? fields : [];
  const isPick = (x) =>
    x && (x.data_type === 'picklist' || x.data_type === 'multipicklist');
  const byName = (x) => {
    const n = String(x.field_name || '').toLowerCase();
    return n === 'leadstatus' || n === 'lead_status';
  };
  let f = list.find((x) => isPick(x) && byName(x));
  if (!f) {
    f = list.find(
      (x) => isPick(x) && /lead\s*status/i.test(String(x.field_label || ''))
    );
  }
  return f || null;
}

const ensurePlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return { ...value };
};

const clampNumber = (value, min, max) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return Math.min(max, Math.max(min, parsed));
};

/** Default API key suggestion mirrors label text (user can override in the grid). */
const suggestPicklistApiNameFromLabel = (label) => {
  return label == null ? '' : String(label);
};

const normalizePicklistPathColorForPayload = (raw) => {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return null;
  if (s.length === 4) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return s.toLowerCase();
};

const parsePicklistEditorBulkLine = (line, defaultPathColor) => {
  const raw = String(line || '').trim();
  if (!raw) return null;

  let pathColor = normalizePicklistPathColorForPayload(defaultPathColor);
  let rest = raw;
  const m = raw.match(/^(.+?)\s+#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (m) {
    rest = m[1].trim();
    pathColor = normalizePicklistPathColorForPayload(`#${m[2]}`) || pathColor;
  }
  if (!rest) return null;

  if (rest.includes('|')) {
    const parts = rest.split('|');
    const key = String(parts[0] || '').trim();
    const value = String(parts.slice(1).join('|') || '').trim() || key;
    if (!key) return null;
    return {
      key,
      value,
      path_color: pathColor || '',
      _apiFollowsLabel: false
    };
  }

  return {
    key: rest,
    value: rest,
    path_color: pathColor || '',
    _apiFollowsLabel: true
  };
};

const normalizePicklistRowForForm = (v) => {
  if (!v || typeof v !== 'object') {
    const s = v == null ? '' : String(v);
    return {
      key: suggestPicklistApiNameFromLabel(s) || s,
      value: s,
      path_color: '',
      _apiFollowsLabel: false
    };
  }
  const keyRaw = v.key ?? v.value_key ?? v.valueKey ?? '';
  const valueRaw = v.value ?? v.value_label ?? v.valueLabel ?? '';
  const value = valueRaw != null ? String(valueRaw) : '';
  let key = keyRaw != null ? String(keyRaw).trim() : '';
  if (!key && value) key = suggestPicklistApiNameFromLabel(value) || value;
  const pathColorRaw = v.path_color ?? v.pathColor ?? '';
  return {
    key,
    value,
    path_color: pathColorRaw != null ? String(pathColorRaw) : '',
    _apiFollowsLabel: false
  };
};

/** Max stored value length for picklist / multipicklist record columns (matches backend TEXT cap). */
const PICKLIST_FIELD_MAX_LENGTH = 150000;

/** Max stored value length for url / image record columns (matches backend VARCHAR cap). */
const URL_IMAGE_FIELD_MAX_LENGTH = 2000;

const emptyPicklistEditorRow = () => ({
  key: '',
  value: '',
  path_color: '',
  _apiFollowsLabel: true
});

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCalculatedConfigForRender = (displayConfig) => {
  const root = ensurePlainObject(displayConfig);
  const calculated = ensurePlainObject(root.calculated);
  const rawReturnType = typeof calculated.returnType === 'string'
    ? calculated.returnType.trim()
    : '';
  const hasReturnType = rawReturnType !== '';
  const returnType = hasReturnType ? rawReturnType.toLowerCase() : 'text';

  const rawDecimalPlaces = calculated.decimalPlaces ?? calculated.scale;
  const defaultDecimals = returnType === 'currency' || returnType === 'percent' ? 2 : 0;
  const decimalPlaces = NUMERIC_RETURN_TYPES.includes(returnType)
    ? clampNumber(rawDecimalPlaces, 0, 6) ?? defaultDecimals
    : null;

  const rawPrecision = calculated.precision;
  const precision = NUMERIC_RETURN_TYPES.includes(returnType)
    ? clampNumber(rawPrecision, decimalPlaces ? Math.max(decimalPlaces + 1, 4) : 4, 38) ?? Math.max((decimalPlaces ?? 0) + 10, 18)
    : null;

  const currencySymbol = returnType === 'currency'
    ? (typeof calculated.currencySymbol === 'string' && calculated.currencySymbol.trim() !== ''
      ? calculated.currencySymbol.trim()
      : DEFAULT_CURRENCY_SYMBOL)
    : '';

  const formatPattern = typeof calculated.formatPattern === 'string'
    ? calculated.formatPattern
    : '';

  let imageConfig = null;
  let barcodeConfig = null;
  let qrcodeConfig = null;

  if (returnType === 'image') {
    const resolveDimension = (key, defaultValue) => {
      if (!Object.prototype.hasOwnProperty.call(calculated, key)) {
        return defaultValue;
      }
      const numeric = toFiniteNumber(calculated[key]);
      if (numeric === null && calculated[key] !== null) {
        return defaultValue;
      }
      return numeric;
    };

    const width = resolveDimension('width', 300);
    const height = resolveDimension('height', 300);
    const mobileWidth = resolveDimension('mobileWidth', 150);
    const mobileHeight = resolveDimension('mobileHeight', 150);
    const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
    const objectFitRaw = typeof calculated.objectFit === 'string' ? calculated.objectFit.toLowerCase() : '';
    const objectFit = allowedFits.includes(objectFitRaw) ? objectFitRaw : 'contain';

    imageConfig = { width, height, mobileWidth, mobileHeight, objectFit };
  }

  if (returnType === 'barcode') {
    const format = typeof calculated.format === 'string' && calculated.format.trim() !== ''
      ? calculated.format.trim()
      : 'CODE128';
    const rawWidth = clampNumber(calculated.width, 1, 10);
    const rawHeight = typeof calculated.height === 'number' && !Number.isNaN(calculated.height)
      ? calculated.height
      : null;
    const displayValue = calculated.displayValue === false ? false : true;

    barcodeConfig = { format, displayValue };
    if (typeof rawWidth === 'number' && !Number.isNaN(rawWidth)) {
      barcodeConfig.width = rawWidth;
    }
    if (rawHeight !== null) {
      barcodeConfig.height = rawHeight;
    }
  }

  if (returnType === 'qrcode') {
    const rawSize = calculated.size;
    const sizeNumber = rawSize === '' || rawSize === null || rawSize === undefined
      ? null
      : Number(rawSize);
    const validLevels = ['L', 'M', 'Q', 'H'];
    const rawLevel = typeof calculated.errorCorrectionLevel === 'string'
      ? calculated.errorCorrectionLevel.toUpperCase()
      : '';
    const errorCorrectionLevel = validLevels.includes(rawLevel) ? rawLevel : 'M';

    qrcodeConfig = { errorCorrectionLevel };

    if (Number.isFinite(sizeNumber)) {
      qrcodeConfig.size = sizeNumber;
    }
  }

  return {
    returnType,
    decimalPlaces,
    precision,
    currencySymbol,
    formatPattern,
    hasReturnType,
    imageConfig,
    barcodeConfig,
    qrcodeConfig,
    ...(imageConfig || {}),
    ...(barcodeConfig || {}),
    ...(qrcodeConfig || {})
  };
};

const buildCalculatedDisplayConfigForState = (displayConfig) => {
  const base = ensurePlainObject(displayConfig);
  const normalized = normalizeCalculatedConfigForRender(base);

  const nextCalculated = {
    returnType: normalized.returnType
  };

  if (NUMERIC_RETURN_TYPES.includes(normalized.returnType)) {
    nextCalculated.decimalPlaces = normalized.decimalPlaces ?? 0;
    nextCalculated.scale = normalized.decimalPlaces ?? 0;
    nextCalculated.precision = normalized.precision ?? Math.max((normalized.decimalPlaces ?? 0) + 10, 18);
    if (normalized.returnType === 'currency') {
      nextCalculated.currencySymbol = normalized.currencySymbol || DEFAULT_CURRENCY_SYMBOL;
    }
  }

  if (normalized.returnType === 'text' && normalized.formatPattern) {
    nextCalculated.formatPattern = normalized.formatPattern;
  }

  if (normalized.returnType === 'image' && normalized.imageConfig) {
    Object.assign(nextCalculated, normalized.imageConfig);
  }

  if (normalized.returnType === 'barcode' && normalized.barcodeConfig) {
    Object.assign(nextCalculated, normalized.barcodeConfig);
  }

  if (normalized.returnType === 'qrcode' && normalized.qrcodeConfig) {
    Object.assign(nextCalculated, normalized.qrcodeConfig);
  }

  return {
    displayConfig: {
      ...ensurePlainObject(displayConfig),
      calculated: nextCalculated
    },
    hasReturnType: normalized.hasReturnType
  };
};

const sanitizeCalculatedDisplayConfigForSave = (displayConfig) => {
  const base = ensurePlainObject(displayConfig);
  const normalized = normalizeCalculatedConfigForRender(base);

  const otherKeys = ensurePlainObject(base);
  delete otherKeys.calculated;

  const sanitizedCalculated = {
    returnType: normalized.returnType
  };

  if (NUMERIC_RETURN_TYPES.includes(normalized.returnType)) {
    const scale = normalized.decimalPlaces ?? 0;
    sanitizedCalculated.scale = scale;
    sanitizedCalculated.precision = normalized.precision ?? Math.max(scale + 10, 18);
  }

  if (normalized.returnType === 'text' && normalized.formatPattern) {
    sanitizedCalculated.formatPattern = normalized.formatPattern;
  }

  if (normalized.returnType === 'image' && normalized.imageConfig) {
    const { width, height, mobileWidth, mobileHeight, objectFit } = normalized.imageConfig;
    sanitizedCalculated.width = width;
    sanitizedCalculated.height = height;
    sanitizedCalculated.mobileWidth = mobileWidth;
    sanitizedCalculated.mobileHeight = mobileHeight;
    sanitizedCalculated.objectFit = objectFit;
  }

  if (normalized.returnType === 'barcode' && normalized.barcodeConfig) {
    const { format, width, height, displayValue } = normalized.barcodeConfig;
    sanitizedCalculated.format = format;
    if (width !== null && width !== undefined && width !== '') {
      sanitizedCalculated.width = width;
    }
    if (height !== null && height !== undefined && height !== '') {
      sanitizedCalculated.height = height;
    }
    sanitizedCalculated.displayValue = displayValue;
  }

  if (normalized.returnType === 'qrcode' && normalized.qrcodeConfig) {
    const { size, errorCorrectionLevel } = normalized.qrcodeConfig;
    if (Number.isFinite(size)) {
      sanitizedCalculated.size = size;
    }
    sanitizedCalculated.errorCorrectionLevel = errorCorrectionLevel;
  }

  const calculatedPayload = { calculated: sanitizedCalculated };

  if (Object.keys(otherKeys).length > 0) {
    return { ...otherKeys, ...calculatedPayload };
  }

  return calculatedPayload;
};

const stripCalculatedDisplayConfig = (displayConfig) => {
  const base = ensurePlainObject(displayConfig);
  if (!base.calculated) {
    return Object.keys(base).length > 0 ? base : null;
  }
  const { calculated, ...rest } = base;
  return Object.keys(rest).length > 0 ? rest : null;
};

/** Default lookup search/display field when reference object has a `name` column. */
const DEFAULT_LOOKUP_FIELD = 'name';

const lookupFieldKeysEqual = (a, b) => {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripFormulaPlaceholders = (expression = '') => {
  if (typeof expression !== 'string' || expression.indexOf('{{') === -1) {
    return expression;
  }

  return expression.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, '$1');
};

const isEntitlementEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const parseObjectConfig = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
};

/** When true, `public.standard_object.config` disallows adding fields on this standard object. */
const isConfigCreateFieldsDisabled = (configObj) => {
  if (!configObj || typeof configObj !== 'object') return false;
  const v = configObj.create_fields;
  return (
    v === false ||
    v === 'false' ||
    v === 0 ||
    v === '0'
  );
};

const renderFieldReferenceSections = (references = {}, fieldLabel = 'This field') => (
  <>
    <Alert variant="warning">
      <strong>{fieldLabel}</strong>{' '}
      cannot be deleted because it is referenced in other configurations. Please remove the references below and
      try again.
    </Alert>
    {(() => {
      const calculatedRefs = references.calculatedFields || [];
      const workflowRefs = references.workflows || [];
      const validationRefs = references.validations || [];
      const reportRefs = references.reports || [];
      const sections = [];

      if (calculatedRefs.length > 0) {
        sections.push(
          <div key="calculated">
            <h6 className="text-dark mt-3">Calculated Fields</h6>
            <ul className="ps-3">
              {calculatedRefs.map(ref => (
                <li key={`${ref.id}-${ref.parent_object_name || 'local'}`}>
                  <strong>{ref.field_label || ref.field_name}</strong>
                  {ref.parent_object_name && (
                    <div className="text-muted small">
                      Object: {ref.parent_object_label || ref.parent_object_name}
                      {ref.lookup_field_name && ref.referenced_field_name && (
                        <> · via {ref.lookup_field_name}.{ref.referenced_field_name}</>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (workflowRefs.length > 0) {
        sections.push(
          <div key="workflows">
            <h6 className="text-dark mt-3">Workflows</h6>
            <ul className="ps-3">
              {workflowRefs.map(workflow => (
                <li key={workflow.workflow_id}>
                  <strong>{workflow.workflow_name || 'Untitled Workflow'}</strong>
                  {Array.isArray(workflow.usages) && workflow.usages.length > 0 && (
                    <div className="text-muted small">
                      Used in: {workflow.usages.join('; ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (validationRefs.length > 0) {
        sections.push(
          <div key="validations">
            <h6 className="text-dark mt-3">Validation Rules</h6>
            <ul className="ps-3">
              {validationRefs.map(rule => (
                <li key={rule.validation_id}>
                  <strong>{rule.validation_name || 'Untitled Validation'}</strong>
                  {Array.isArray(rule.usages) && rule.usages.length > 0 && (
                    <div className="text-muted small">
                      Used in: {rule.usages.join('; ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (reportRefs.length > 0) {
        sections.push(
          <div key="reports">
            <h6 className="text-dark mt-3">Reports</h6>
            <ul className="ps-3">
              {reportRefs.map(report => (
                <li key={`${report.report_id}-${report.usage_type || 'field'}`}>
                  <strong>{report.report_name || 'Untitled Report'}</strong>
                  {(report.usage_label || report.usage_type) && (
                    <div className="text-muted small">
                      Used as: {report.usage_label || report.usage_type}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (sections.length === 0) {
        return (
          <p className="mb-0 text-muted">
            This field is still referenced elsewhere. Please review calculated fields, workflows, validation rules, or reports.
          </p>
        );
      }

      return sections;
    })()}
  </>
);

const FieldManager = ({ show = true, onHide, objectName, objectId, onNavigate, selectedObject }) => {
  const productBuilderMode = useProductBuilderMode();
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  // Show "Standard Field" checkbox only when license explicitly has enable_standard: true (admin "Enable Standard")
  const allowStandardField =
    isEntitlementEnabled(objectManagerConfig?.enable_standard) ||
    isEntitlementEnabled(objectManagerConfig?.enableStandard);

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [deleteModalChecking, setDeleteModalChecking] = useState(false);
  const [deleteModalDeleting, setDeleteModalDeleting] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState(null);
  const [deleteModalBlockedReferences, setDeleteModalBlockedReferences] = useState(null);
  const [deleteFlowBusy, setDeleteFlowBusy] = useState(false);
  const deleteFlowActiveRef = useRef(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [fieldToPermanentlyDelete, setFieldToPermanentlyDelete] = useState(null);
  const [permanentDeleteModalDeleting, setPermanentDeleteModalDeleting] = useState(false);
  const [permanentDeleteModalError, setPermanentDeleteModalError] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [filter, setFilter] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalTouchedSave, setModalTouchedSave] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldForm, setFieldForm] = useState({
    field_name: '',
    field_label: '',
    data_type: 'text',
    is_required: false,
    is_readonly: false,
    is_unique: false,
    is_reference: false,
    reference_object: '',
    picklist_values: [],
    picklist_default_index: null,
    max_length: '255', // Default for text
    calculated_expression: '', // For calculated fields
    precision: '',
    scale: '',
    start_number: 1,
    prefix: '', // For autonumber
    format_mask: '', // For autonumber
    lookup_search_fields: '',
    display_fields: '',
    lookup_filter: { logic: 'AND', filters: [] }, // Structure: { logic: "AND|OR", filters: [...] }
    rollup_field: '',
    rollup_operation: '',
    rollup_criteria: { logic: 'AND', conditions: [] }, // Structure: { logic: "AND|OR", conditions: [...] }
    is_standard: false,
    is_path_field: false, // For marking picklist fields as path fields
    display_config: {}, // For barcode, qrcode, and image field configurations
    config: {},
    calculated_has_return_type: false
  });

  const [objectOptions, setObjectOptions] = useState([]); // {name, label}
  const [lookupFieldOptions, setLookupFieldOptions] = useState([]); // [{key,value}] - for display fields
  const [lookupSearchFieldOptions, setLookupSearchFieldOptions] = useState([]); // [{key,value}] - lookup search fields (includes calculated)
  const [lookupFilterFieldOptions, setLookupFilterFieldOptions] = useState([]); // [{key,value,data_type}] - for filter fields
  /** Full reference-object fields for lookup filter ConditionRow (picklist load, operators). */
  const [lookupFilterFields, setLookupFilterFields] = useState([]);
  /** Full child-object fields for rollup ConditionRow (needs real field id for picklist load). */
  const [rollupCriteriaFields, setRollupCriteriaFields] = useState([]);
  const { valueFields: lookupFilterValueFields } = useConditionValueFields(objectName);
  const [currentObjectConfig, setCurrentObjectConfig] = useState(null); // Current object's config
  const [selectedRollupField, setSelectedRollupField] = useState(null); // Selected rollup field info
  const [showPicklistManager, setShowPicklistManager] = useState(false);
  const [selectedPicklistField, setSelectedPicklistField] = useState(null);
  const [validatingFormula, setValidatingFormula] = useState(false);
  const [formulaValidationResult, setFormulaValidationResult] = useState(null);
  const [autoValidationTimeout, setAutoValidationTimeout] = useState(null);
  const [parentObjectFieldsCache, setParentObjectFieldsCache] = useState({});
  const [showDependentFieldManager, setShowDependentFieldManager] = useState(false);
  const [showFieldMappingModal, setShowFieldMappingModal] = useState(false);
  const [accountFields, setAccountFields] = useState([]);
  const [contactFields, setContactFields] = useState([]);
  const [opportunityFields, setOpportunityFields] = useState([]);
  const [mappingTargets, setMappingTargets] = useState([]);
  const [loadingMappingFields, setLoadingMappingFields] = useState(false);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [mappingData, setMappingData] = useState({});
  const [savingMappings, setSavingMappings] = useState(false);
  /** Per-target toggles for lead field mapping; keys are only subscription-eligible targets (see mappingTargets). */
  const [enabledMappingObjects, setEnabledMappingObjects] = useState({});
  const [configuredMappingObjects, setConfiguredMappingObjects] = useState([]);
  const [objectIds, setObjectIds] = useState({
    account: null,
    contact: null,
    opportunity: null
  });
  const [redirectPreference, setRedirectPreference] = useState('opportunity'); // Default to opportunity
  const [defaultOpportunityStage, setDefaultOpportunityStage] = useState(''); // Default opportunity stage
  // When true, Convert Lead won't mark is_converted true; lead `id` becomes mappable to a lookup on target
  const [dontMarkConverted, setDontMarkConverted] = useState(false);
  /** Configured post-convert lead status ({ valueKey, label }) — drives detail hide + conversion update */
  const [savedLeadConvertStatus, setSavedLeadConvertStatus] = useState(null);
  const [leadConvertStatusSelectKey, setLeadConvertStatusSelectKey] = useState('');
  const [opportunityStageOptions, setOpportunityStageOptions] = useState([]); // Picklist options for opportunity stage
  const [loadingStageOptions, setLoadingStageOptions] = useState(false);
  const [trashedFields, setTrashedFields] = useState([]);
  const [showTrashedFields, setShowTrashedFields] = useState(false);
  const [loadingTrashedFields, setLoadingTrashedFields] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [bulkPicklistValues, setBulkPicklistValues] = useState('');
  const [bulkPicklistPathColor, setBulkPicklistPathColor] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const mergedObjectConfig = useMemo(() => {
    const src = currentObjectConfig || selectedObject;
    if (!src?.config) return {};
    return parseObjectConfig(src.config);
  }, [currentObjectConfig, selectedObject]);

  const objectIsStandard = Boolean(
    currentObjectConfig ? currentObjectConfig.is_standard : selectedObject?.is_standard
  );

  /** Human-readable object title (prefer object_label from config over API object_name). */
  const objectDisplayLabel = useMemo(() => {
    const pick = v =>
      v != null && String(v).trim() !== '' ? String(v).trim() : '';
    return (
      pick(currentObjectConfig?.object_label) ||
      pick(selectedObject?.object_label) ||
      pick(selectedObject?.object_name) ||
      pick(objectName) ||
      'Object'
    );
  }, [currentObjectConfig, selectedObject, objectName]);

  /** create_fields only applies when "Enable standard" is on in subscription (same as ObjectManager). */
  const standardObjectBlocksNewFields =
    !productBuilderMode &&
    allowStandardField &&
    objectIsStandard &&
    isConfigCreateFieldsDisabled(mergedObjectConfig);

  const fieldConfig = getEntitlementsSection('field', {});
  const canEditStandard =
    productBuilderMode || isEntitlementEnabled(fieldConfig?.allowStandardEdit);
  const canEditLabel =
    canEditStandard || isEntitlementEnabled(fieldConfig?.allowEditLabel);
  const leadMappingConfig = getEntitlementsSection('leadConversionMapping', {});
  const migrationConfig = getEntitlementsSection('migration', {});
  const allowLeadMapping = leadMappingConfig?.allow !== false;
  const allowCustomMappingObjects = isEntitlementEnabled(leadMappingConfig?.allowCustomObject);
  const allowMigrationConfig = migrationConfig?.allow !== false;
  const leadStatusPicklistForConvertHide = useMemo(() => {
    if (String(objectName || '').toLowerCase() !== 'lead') return [];
    const f = findLeadStatusFieldForConvertConfig(fields);
    return resolveLeadStatusPicklistOptionsFromField(f);
  }, [fields, objectName]);
  const maxCustomFields = Number(fieldConfig?.maxCustom);
  const customFieldCount = fields.filter(field => !field?.is_standard).length;
  const isCustomFieldLimitReached = Number.isFinite(maxCustomFields)
    && maxCustomFields >= 0
    && customFieldCount >= maxCustomFields;
  const allowFieldCreate = !(
    fieldConfig?.allowCreate === false
    || fieldConfig?.allowCreate === 'false'
    || fieldConfig?.allowCreate === 0
    || fieldConfig?.allowCreate === '0'
  );
  const canCreateField = productBuilderMode
    ? allowFieldCreate
    : (
      allowFieldCreate &&
      !isCustomFieldLimitReached &&
      !standardObjectBlocksNewFields
    );
  const allowStandardFieldEdit =
    productBuilderMode || fieldConfig?.allowStandardEdit !== false;
  const allowStandardFieldDelete =
    productBuilderMode || fieldConfig?.allowStandardDelete === true;
  // When editing: on standard objects only, standard fields get label-only or full edit per entitlement.
  // User-created fields on custom objects are stored with is_standard false so the backend appends __x (schema.model.js).
  const isEditingStandardField =
    editingField && !!editingField.is_standard && objectIsStandard;
  const canEditFieldProperties =
    !editingField ||
    !editingField.is_standard ||
    canEditStandard ||
    !objectIsStandard;

  /**
   * Picklist / multipicklist: always allow Manage Values (add rows, edit API keys, etc.) for standard and custom fields,
   * independent of object-manager enableStandard and field allowStandardEdit / allowEditLabel entitlements.
   */
  const canManagePicklistValuesForField = (fld) => {
    if (!fld) return false;
    return fld.data_type === 'picklist' || fld.data_type === 'multipicklist';
  };

  // Field History states
  const [fieldHistoryEnabled, setFieldHistoryEnabled] = useState(false);
  const [showFieldHistoryModal, setShowFieldHistoryModal] = useState(false);

  const isFieldDeleteBusy =
    deleteFlowBusy ||
    deleteModalChecking ||
    deleteModalDeleting ||
    showDeleteModal ||
    showPermanentDeleteModal ||
    permanentDeleteModalDeleting;

  const calculatedConfig = useMemo(
    () => normalizeCalculatedConfigForRender(fieldForm.display_config),
    [fieldForm.display_config]
  );

  const updateCalculatedConfig = (updates) => {
    setFieldForm(prev => {
      const baseDisplayConfig = ensurePlainObject(prev.display_config);
      const currentConfig = normalizeCalculatedConfigForRender(baseDisplayConfig);
      const merged = { ...currentConfig, ...updates };

      if (updates.returnType && updates.returnType !== currentConfig.returnType) {
        if (NUMERIC_RETURN_TYPES.includes(merged.returnType)) {
          const defaultDecimals = merged.returnType === 'currency' || merged.returnType === 'percent' ? 2 : 0;
          if (merged.decimalPlaces === null || merged.decimalPlaces === undefined) {
            merged.decimalPlaces = defaultDecimals;
          }
          if (merged.precision === null || merged.precision === undefined) {
            merged.precision = Math.max(defaultDecimals + 10, 18);
          }
        } else {
          merged.decimalPlaces = null;
          merged.precision = null;
        }

        if (merged.returnType === 'currency') {
          merged.currencySymbol = merged.currencySymbol || DEFAULT_CURRENCY_SYMBOL;
        } else {
          merged.currencySymbol = '';
        }

        if (merged.returnType !== 'text') {
          merged.formatPattern = '';
        }

        if (merged.returnType === 'image') {
          merged.width = 300;
          merged.height = 300;
          merged.mobileWidth = 150;
          merged.mobileHeight = 150;
          merged.objectFit = 'contain';
        } else if (merged.returnType === 'barcode') {
          merged.format = 'CODE128';
          merged.width = 2;
          merged.height = 100;
          merged.displayValue = true;
        } else if (merged.returnType === 'qrcode') {
          merged.size = 200;
          merged.errorCorrectionLevel = 'M';
        } else {
          delete merged.width;
          delete merged.height;
          delete merged.mobileWidth;
          delete merged.mobileHeight;
          delete merged.objectFit;
          delete merged.format;
          delete merged.displayValue;
          delete merged.size;
          delete merged.errorCorrectionLevel;
        }
      }

      if (NUMERIC_RETURN_TYPES.includes(merged.returnType)) {
        merged.decimalPlaces = clampNumber(
          merged.decimalPlaces,
          0,
          6
        ) ?? (merged.returnType === 'currency' || merged.returnType === 'percent' ? 2 : 0);
        merged.precision = clampNumber(
          merged.precision,
          merged.decimalPlaces ? Math.max(merged.decimalPlaces + 1, 4) : 4,
          38
        ) ?? Math.max((merged.decimalPlaces ?? 0) + 10, 18);
      }

      if (updates.returnType && updates.returnType !== currentConfig.returnType) {
        if (updates.returnType === 'image') {
          merged.width = 300;
          merged.height = 300;
          merged.mobileWidth = 150;
          merged.mobileHeight = 150;
          merged.objectFit = 'contain';
        } else if (updates.returnType === 'barcode') {
          merged.format = 'CODE128';
          merged.width = 2;
          merged.height = 100;
          merged.displayValue = true;
        } else if (updates.returnType === 'qrcode') {
          merged.size = 200;
          merged.errorCorrectionLevel = 'M';
        }
      }

      if (merged.returnType === 'image') {
        const normalizeDimension = (value) => {
          if (value === null || value === undefined || value === '') {
            return null;
          }
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        merged.width = normalizeDimension(merged.width);
        merged.height = normalizeDimension(merged.height);
        merged.mobileWidth = normalizeDimension(merged.mobileWidth);
        merged.mobileHeight = normalizeDimension(merged.mobileHeight);

        const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
        if (!allowedFits.includes((merged.objectFit || '').toLowerCase())) {
          merged.objectFit = 'contain';
        } else {
          merged.objectFit = merged.objectFit.toLowerCase();
        }
      }

      if (merged.returnType === 'barcode') {
        merged.format = typeof merged.format === 'string' && merged.format.trim() !== ''
          ? merged.format.trim().toUpperCase()
          : 'CODE128';
        if (merged.width === null || merged.width === undefined || merged.width === '') {
          delete merged.width;
        }
        if (merged.height === null || merged.height === undefined || merged.height === '') {
          delete merged.height;
        }
        merged.displayValue = merged.displayValue === false ? false : true;
      }

      if (merged.returnType === 'qrcode') {
        if (merged.size === null || merged.size === undefined || merged.size === '') {
          delete merged.size;
        } else if (!Number.isFinite(merged.size)) {
          delete merged.size;
        }
        const validLevels = ['L', 'M', 'Q', 'H'];
        const rawLevel = typeof merged.errorCorrectionLevel === 'string'
          ? merged.errorCorrectionLevel.toUpperCase()
          : '';
        merged.errorCorrectionLevel = validLevels.includes(rawLevel) ? rawLevel : 'M';
      } else {
        delete merged.size;
        delete merged.errorCorrectionLevel;
      }

      const nextCalculated = {
        returnType: merged.returnType
      };

      if (NUMERIC_RETURN_TYPES.includes(merged.returnType)) {
        const decimalPlaces = merged.decimalPlaces ?? 0;
        nextCalculated.decimalPlaces = decimalPlaces;
        nextCalculated.scale = decimalPlaces;
        nextCalculated.precision = merged.precision ?? Math.max(decimalPlaces + 10, 18);
      }

      if (merged.returnType === 'text') {
        if (merged.formatPattern) {
          nextCalculated.formatPattern = merged.formatPattern;
        } else {
          nextCalculated.formatPattern = '';
        }
      }

      if (merged.returnType === 'image') {
        nextCalculated.width = merged.width;
        nextCalculated.height = merged.height;
        nextCalculated.mobileWidth = merged.mobileWidth;
        nextCalculated.mobileHeight = merged.mobileHeight;
        nextCalculated.objectFit = merged.objectFit;
      } else if (merged.returnType === 'barcode') {
        nextCalculated.format = merged.format;
        if (merged.width !== undefined) {
          nextCalculated.width = merged.width;
        } else {
          delete nextCalculated.width;
        }
        if (merged.height !== undefined) {
          nextCalculated.height = merged.height;
        } else {
          delete nextCalculated.height;
        }
        nextCalculated.displayValue = merged.displayValue;
      } else if (merged.returnType === 'qrcode') {
        if (merged.size !== undefined) {
          nextCalculated.size = merged.size;
        } else {
          delete nextCalculated.size;
        }
        nextCalculated.errorCorrectionLevel = merged.errorCorrectionLevel;
      } else {
        delete nextCalculated.width;
        delete nextCalculated.height;
        delete nextCalculated.mobileWidth;
        delete nextCalculated.mobileHeight;
        delete nextCalculated.objectFit;
        delete nextCalculated.format;
        delete nextCalculated.size;
        delete nextCalculated.errorCorrectionLevel;
        delete nextCalculated.displayValue;
      }

      const nextDisplayConfig = {
        ...baseDisplayConfig,
        calculated: nextCalculated
      };

      return {
        ...prev,
        display_config: nextDisplayConfig,
        calculated_has_return_type: updates.returnType ? true : prev.calculated_has_return_type
      };
    });
  };

  useEffect(() => {
    if (!Array.isArray(mappingTargets) || mappingTargets.length === 0) {
      return;
    }

    const enabledTargetKeys = mappingTargets
      .filter(target => enabledMappingObjects?.[target.key])
      .map(target => target.key);

    if (enabledTargetKeys.length === 0) {
      return;
    }

    if (!enabledTargetKeys.includes(redirectPreference)) {
      setRedirectPreference(enabledTargetKeys[0]);
    }
  }, [mappingTargets, enabledMappingObjects, redirectPreference]);

  const isCalculatedField = fieldForm.data_type === 'calculated';
  const calculatedReturnType = calculatedConfig.returnType;

  const getDisplayConfigValue = (key, fallback = undefined) => {
    if (isCalculatedField) {
      return calculatedConfig && calculatedConfig[key] !== undefined
        ? calculatedConfig[key]
        : fallback;
    }

    return fieldForm.display_config && fieldForm.display_config[key] !== undefined
      ? fieldForm.display_config[key]
      : fallback;
  };

  const setDisplayConfigValue = (key, value) => {
    if (isCalculatedField) {
      updateCalculatedConfig({ [key]: value });
      return;
    }

    setFieldForm(prev => {
      const base = ensurePlainObject(prev.display_config);
      const nextConfig = { ...base };

      if (value === null || value === undefined || value === '') {
        delete nextConfig[key];
      } else {
        nextConfig[key] = value;
      }

      return {
        ...prev,
        display_config: Object.keys(nextConfig).length > 0 ? nextConfig : undefined
      };
    });
  };

  // Header component for FieldManager
  const renderHeader = () => (
    <div className="view-header mb-4">
      {/* Top Back Button - Always Visible */}
      <div className="mb-3">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => onNavigate('object-details')}
        >
          ← Back to Object
        </Button>
      </div>

      {/* Main Header Content */}
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h2>
            <FaList className="me-3" />
            Field Management - {objectDisplayLabel}
          </h2>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowDependentFieldManager(true)}
          >
            <FaPlus className="me-1" />
            Dependent Picklist
          </Button>
          {objectName && objectName.toLowerCase() === 'lead' && allowLeadMapping && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setShowFieldMappingModal(true);
                fetchMappingFields();
                fetchFieldMappings();
                refreshLeadConversionConfigFromServer();
              }}
            >
              <FaExchangeAlt className="me-1" />
              Field Mapping
            </Button>
          )}
        </div>
      </div>
      {allowFieldCreate && !canCreateField && standardObjectBlocksNewFields && (
        <Alert variant="info" className="mt-3 mb-0">
          Field creation is turned off for this object (<code>standard_object.config.create_fields</code> is false).
        </Alert>
      )}
      {allowFieldCreate && !canCreateField && !standardObjectBlocksNewFields && isCustomFieldLimitReached && (
        <Alert variant="warning" className="mt-3 mb-0">
          Custom field limit reached ({maxCustomFields}). You cannot create more custom fields.
        </Alert>
      )}
    </div>
  );

  const generateApiName = (label) => {
    if (!label) return '';
    let name = label
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    if (name && !/^[a-z_]/.test(name)) name = '_' + name;
    return name;
  };

  // Generate autonumber preview based on prefix and format mask
  const generateAutonumberPreview = (prefix = '', formatMask = '', startNumber = 1) => {
    let preview = prefix || '';

    if (formatMask) {
      // Replace format mask with padded number
      const numberStr = startNumber.toString();
      let formattedNumber = numberStr;

      // If format mask has more digits than the number, pad with zeros
      if (formatMask.length > numberStr.length) {
        formattedNumber = numberStr.padStart(formatMask.length, '0');
      }

      preview += formattedNumber;
    } else {
      preview += startNumber.toString();
    }

    return preview || '1';
  };

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  // Field History handlers
  const handleOpenFieldHistory = () => {
    setShowFieldHistoryModal(true);
  };

  const handleCloseFieldHistory = () => {
    setShowFieldHistoryModal(false);
  };

  const handleFieldHistorySave = async (result) => {
    if (result.success) {
      // Refresh field history enabled state by checking current mappings
      if (currentObjectConfig?.id) {
        try {
          const response = await ObjectConfigApi.getFieldHistoryMapping(currentObjectConfig.id);
          const mappings = response?.data || [];
          setFieldHistoryEnabled(mappings.length > 0);
        } catch (e) {
          console.error('Failed to refresh field history status:', e);
        }
      }
    }
    // Show toast message
    if (result.message) {
      showToastMessage(result.message, result.variant || 'success');
    }
  };

  // Function to format field size based on data type (returns just the size part)
  const formatFieldSize = (field) => {
    const dataType = (field.data_type || '').toLowerCase();

    switch (dataType) {
      case 'text':
      case 'textarea':
      case 'richtext':
        return field.max_length ? field.max_length : null;

      case 'number':
      case 'currency':
      case 'percent':
      case 'decimal':
      case 'float':
        if (field.precision && field.scale) {
          return `${field.precision},${field.scale}`;
        } else if (field.precision) {
          return field.precision;
        } else if (field.max_length) {
          return field.max_length;
        }
        return null;

      case 'autonumber':
        return field.start_number ? field.start_number : null;

      case 'lookup':
      case 'rollupsummary':
        return null; // No size info for reference fields

      case 'picklist':
      case 'multipicklist':
        return field.max_length ? field.max_length : PICKLIST_FIELD_MAX_LENGTH;

      case 'checkbox':
      case 'boolean':
      case 'date':
      case 'datetime':
        return null; // No size info for these types

      case 'email':
      case 'phone':
        return field.max_length ? field.max_length : null;

      case 'url':
      case 'image':
        return field.max_length ? field.max_length : URL_IMAGE_FIELD_MAX_LENGTH;

      default:
        return null;
    }
  };

  // Function to handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // If clicking the same column, toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it as sort column with ascending order
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Function to get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-muted">↕</span>;
    }
    return sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  useEffect(() => {
    if (show && objectName) {
      fetchFields();
      fetchTrashedFields(); // Load trashed fields count on page load
    }
  }, [show, objectName]);

  // Cleanup auto-validation timeout and clear cache on component unmount
  useEffect(() => {
    return () => {
      if (autoValidationTimeout) {
        clearTimeout(autoValidationTimeout);
      }
      // Clear parent object fields cache on unmount
      //setParentObjectFieldsCache({});
    };
  }, [autoValidationTimeout]);

  // Clear parent object fields cache when object changes and preload parent object fields
  useEffect(() => {
    console.log('🔍 Object changed, clearing cache and preloading for:', objectName);
    setParentObjectFieldsCache({});
    if (objectName) {
      // Add a small delay to ensure the cache is cleared first
      setTimeout(() => {
        preloadParentObjectFields();
      }, 100);
    }
  }, [objectName]);

  // Additional effect to handle URL-based restoration
  useEffect(() => {
    if (objectName) {
      console.log('FieldManager - Loading data for object:', objectName);
      setInitialLoading(true);
      fetchFields();
      fetchTrashedFields();
    } else {
      console.log('FieldManager - No objectName provided, skipping data fetch');
    }
  }, [objectName]);

  // Load current object's configuration to get related_objects
  useEffect(() => {
    const loadCurrentObjectConfig = async () => {
      if (!objectName) return;

      try {
        const res = await ObjectConfigApi.fetchObjectConfigs();
        const list = res?.data?.data || res?.data || [];
        const currentConfig = list.find(obj => obj.object_name === objectName);
        setCurrentObjectConfig(currentConfig);
      } catch (e) {
        console.error('Failed to load current object config:', e);
      }
    };

    loadCurrentObjectConfig();
  }, [objectName]);

  // Check if field history tracking is already enabled for this object
  useEffect(() => {
    const checkFieldHistoryEnabled = async () => {
      if (!currentObjectConfig?.id) return;

      try {
        const response = await ObjectConfigApi.getFieldHistoryMapping(currentObjectConfig.id);
        const mappings = response?.data || [];
        // If there are any field history mappings, field history is enabled
        setFieldHistoryEnabled(mappings.length > 0);
      } catch (e) {
        console.error('Failed to check field history status:', e);
        // On error, assume not enabled
        setFieldHistoryEnabled(false);
      }
    };

    checkFieldHistoryEnabled();
  }, [currentObjectConfig]);

  // Load object list for lookup selection when modal opens or data_type changes to lookup
  useEffect(() => {
    const loadObjects = async () => {
      try {
        const res = await ObjectConfigApi.fetchObjectConfigs();
        const list = res?.data?.data || res?.data || [];

        // Filter objects based on related_objects for rollup summary
        let filteredList = list;
        if (fieldForm.data_type === 'rollupsummary') {
          // Exclude history objects from rollup summary reference objects
          filteredList = list.filter(obj => !obj.is_history_object);

          // Further filter by related_objects if available
          if (currentObjectConfig?.related_objects) {
            const relatedObjects = currentObjectConfig.related_objects.split(',').map(obj => obj.trim()).filter(Boolean);
            filteredList = filteredList.filter(obj => relatedObjects.includes(obj.object_name));
          }
        }

        const options = filteredList.map(o => ({ name: o.object_name, label: o.object_label || o.object_name }));
        setObjectOptions(options);
      } catch (e) {
        console.error('Failed to load objects for lookup:', e);
      }
    };
    if (showFieldModal && (fieldForm.data_type === 'lookup' || fieldForm.data_type === 'rollupsummary')) {
      loadObjects();
    }
  }, [showFieldModal, fieldForm.data_type, currentObjectConfig]);

  // Load fields of selected reference object to offer as search/display fields
  useEffect(() => {
    const loadLookupFields = async () => {
      try {
        if (!fieldForm.reference_object) return;
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(fieldForm.reference_object);
        const flds = res?.data?.fields || res?.fields || [];

        let allowedTypes;
        if (fieldForm.data_type === 'rollupsummary') {
          // For rollup summary: only numeric, currency, date fields plus ID
          allowedTypes = ['number', 'decimal', 'currency', 'percent', 'date', 'datetime', 'id'];
        } else {
          // For lookup: text-like fields plus calculated (search uses expression underlying fields on API)
          allowedTypes = ['email', 'text', 'phone', 'number', 'autonumber'];
        }

        const isRollup = fieldForm.data_type === 'rollupsummary';
        const lookupSelectableTypes = isRollup ? allowedTypes : [...allowedTypes, 'calculated'];

        const displayOpts = flds
          .filter(f => !!f.field_name)
          .filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            if (isRollup && f.field_name === 'id') {
              return true;
            }
            return lookupSelectableTypes.includes(dataType);
          })
          .map(f => ({
            key: f.field_name,
            value: f.field_label || f.field_name,
            data_type: f.data_type
          }));

        const searchOpts = flds
          .filter(f => !!f.field_name)
          .filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            if (isRollup && f.field_name === 'id') {
              return true;
            }
            return lookupSelectableTypes.includes(dataType);
          })
          .map(f => ({ key: f.field_name, value: f.field_label || f.field_name }));

        setLookupFieldOptions(displayOpts);
        setLookupSearchFieldOptions(searchOpts);

        // Create options for filter fields (all non-calculated fields from reference object)
        const nonCalculatedFields = flds
          .filter(f => !!f.field_name)
          .filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            // Exclude calculated fields from filter fields
            return dataType !== 'calculated';
          });
        const filterOpts = nonCalculatedFields.map(f => ({
          key: f.field_name,
          value: f.field_label || f.field_name,
          data_type: f.data_type,
          id: f.id
        }));
        setLookupFilterFieldOptions(filterOpts);
        setLookupFilterFields(nonCalculatedFields);
        // Full field objects for SharingRuleCriteriaConfig / ConditionRow picklist loading
        setRollupCriteriaFields(isRollup ? nonCalculatedFields : []);

        const allowedSearchKeys = new Set(searchOpts.map(o => o.key));
        const allowedDisplayKeys = new Set(displayOpts.map(o => o.key));
        const currentSearch = Array.isArray(fieldForm.lookup_search_fields)
          ? fieldForm.lookup_search_fields
          : (fieldForm.lookup_search_fields || '').split(',').map(s => s.trim()).filter(Boolean);
        const currentDisplay = Array.isArray(fieldForm.display_fields)
          ? fieldForm.display_fields
          : (fieldForm.display_fields || '').split(',').map(s => s.trim()).filter(Boolean);
        let filteredSearch = currentSearch.filter(k => allowedSearchKeys.has(k));
        let filteredDisplay = currentDisplay.filter(k => allowedDisplayKeys.has(k));

        if (!isRollup) {
          if (filteredSearch.length === 0 && allowedSearchKeys.has(DEFAULT_LOOKUP_FIELD)) {
            filteredSearch = [DEFAULT_LOOKUP_FIELD];
          }
          if (filteredDisplay.length === 0 && allowedDisplayKeys.has(DEFAULT_LOOKUP_FIELD)) {
            filteredDisplay = [DEFAULT_LOOKUP_FIELD];
          }
        }

        if (
          !lookupFieldKeysEqual(filteredSearch, currentSearch) ||
          !lookupFieldKeysEqual(filteredDisplay, currentDisplay)
        ) {
          setFieldForm(prev => ({
            ...prev,
            lookup_search_fields: filteredSearch,
            display_fields: filteredDisplay
          }));
        }
      } catch (e) {
        console.error('Failed to load lookup field options:', e);
        setLookupFieldOptions([]);
        setLookupSearchFieldOptions([]);
        setLookupFilterFieldOptions([]);
        setLookupFilterFields([]);
        setRollupCriteriaFields([]);
      }
    };
    if ((fieldForm.data_type === 'lookup' || fieldForm.data_type === 'rollupsummary') && fieldForm.reference_object) {
      loadLookupFields();
    } else {
      setLookupFieldOptions([]);
      setLookupSearchFieldOptions([]);
      setLookupFilterFieldOptions([]);
      setLookupFilterFields([]);
      setRollupCriteriaFields([]);
    }
  }, [fieldForm.data_type, fieldForm.reference_object]);

  // Track selected rollup field to determine available operations and auto-detect output type
  useEffect(() => {
    if (fieldForm.data_type === 'rollupsummary' && fieldForm.rollup_field && lookupFieldOptions.length > 0) {
      const selectedField = lookupFieldOptions.find(f => f.key === fieldForm.rollup_field);
      setSelectedRollupField(selectedField);

      // If ID field is selected, set operation to COUNT and disable other options
      if (fieldForm.rollup_field === 'id' && fieldForm.rollup_operation !== 'COUNT') {
        setFieldForm(prev => ({ ...prev, rollup_operation: 'COUNT' }));
      }

      // Auto-detect output type based on selected rollup field's data type
      if (selectedField && selectedField.data_type && !editingField) {
        const rollupFieldType = (selectedField.data_type || '').toLowerCase();
        let detectedOutputType = 'number'; // Default to number

        if (rollupFieldType === 'currency') {
          detectedOutputType = 'currency';
        } else if (rollupFieldType === 'percent') {
          detectedOutputType = 'percent';
        } else if (rollupFieldType === 'number' || rollupFieldType === 'decimal' || rollupFieldType === 'float') {
          detectedOutputType = 'number';
        } else if (rollupFieldType === 'date') {
          detectedOutputType = 'date';
        } else if (rollupFieldType === 'datetime') {
          detectedOutputType = 'datetime';
        } else if (rollupFieldType === 'id' || fieldForm.rollup_operation === 'COUNT') {
          detectedOutputType = 'number'; // COUNT always returns number
        }

        // Update display_config with detected output type
        const currentDisplayConfig = ensurePlainObject(fieldForm.display_config);
        const currentRollupConfig = ensurePlainObject(currentDisplayConfig.rollupSummary);

        // Only auto-set if not already set
        if (!currentRollupConfig.outputType) {
          setFieldForm(prev => {
            const prevDisplayConfig = ensurePlainObject(prev.display_config);
            return {
              ...prev,
              display_config: {
                ...prevDisplayConfig,
                rollupSummary: {
                  ...currentRollupConfig,
                  outputType: detectedOutputType
                }
              }
            };
          });
        }
      }
    } else {
      setSelectedRollupField(null);
    }
  }, [fieldForm.rollup_field, fieldForm.data_type, lookupFieldOptions, fieldForm.rollup_operation]);

  // Smart field type matching function
  const getCompatibleFields = (leadFieldType, targetFields) => {
    const leadType = leadFieldType?.toLowerCase();

    // Define compatible field types for each lead field type
    const compatibilityMap = {
      // Date types - only show date fields
      'date': ['date'],
      'datetime': ['datetime', 'date'],

      // Text types - can map to text, textarea, phone, email, autonumber
      'text': ['text', 'textarea', 'phone', 'email', 'autonumber'],
      'textarea': ['text', 'textarea'],
      'phone': ['text', 'phone'],
      'email': ['text', 'email'],
      'autonumber': ['text', 'autonumber'],

      // Number types - can map to number, currency, percent, text
      'number': ['number', 'currency', 'percent', 'text'],
      'currency': ['currency', 'number', 'percent', 'text'],
      'percent': ['percent', 'number', 'currency', 'text'],

      // Boolean types - only checkbox
      'checkbox': ['checkbox'],

      // Picklist types - can map to picklist, text
      'picklist': ['picklist', 'text'],
      'multipicklist': ['multipicklist', 'picklist', 'text'],

      // Lookup types - can map to lookup, text
      'lookup': ['lookup', 'text'],

      // UUID types - can map to uuid, text
      'uuid': ['uuid', 'text']
    };

    const compatibleTypes = compatibilityMap[leadType] || ['text']; // Default to text if unknown type

    return targetFields.filter(field =>
      compatibleTypes.includes(field.data_type?.toLowerCase())
    );
  };

  // Fetch fields for mapping objects (Account, Contact, Opportunity)
  const fetchMappingFields = async () => {
    try {
      setLoadingMappingFields(true);

      // Enable: localStorage.setItem('DEBUG_LEAD_MAPPING', '1') then reload (Lead object → Field Mapping).
      const DEBUG_LEAD_MAPPING =
        typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_LEAD_MAPPING') === '1';
      const logLeadMapping = (label, payload) => {
        if (!DEBUG_LEAD_MAPPING) return;
        console.log(`[leadFieldMapping] ${label}`, payload);
      };

      if (!allowLeadMapping) {
        logLeadMapping('early-exit', { reason: 'allowLeadMapping is false', leadMappingConfig });
        setMappingTargets([]);
        return;
      }

      const allowedMappingObjects = new Set(
        (leadMappingConfig?.allowedObjects || []).map(name => String(name).toLowerCase())
      );
      const mappingMode = String(leadMappingConfig?.mode || '').toLowerCase();
      const restrictAllowlist = mappingMode === 'allowlist' && allowedMappingObjects.size > 0;
      const restrictDenylist = mappingMode === 'denylist' && allowedMappingObjects.size > 0;
      const coreMappingNames = new Set(['account', 'contact', 'opportunity']);

      logLeadMapping('entitlements', {
        allowLeadMapping,
        allowCustomObject: allowCustomMappingObjects,
        allowCustomObjectRaw: leadMappingConfig?.allowCustomObject,
        mode: mappingMode,
        allowedObjectsFromSession: [...allowedMappingObjects],
        restrictAllowlist,
        restrictDenylist,
        hintDenylist:
          mappingMode === 'denylist' && allowedMappingObjects.size > 0
            ? 'denylist: names in allowedObjects are BLOCKED for mapping (not allowed).'
            : null,
        objectName,
      });

      // Allowlist/denylist applies to core conversion objects. Custom targets come only from
      // GET lead-mapping-configs (enableLeadMapping on object config) and appear when
      // allowCustomObject is true — they are not required to be listed in allowedObjects.
      const isMappingObjectAllowed = (target) => {
        const normalized = String(target?.objectName || target?.key || '').toLowerCase();
        const isCore = coreMappingNames.has(normalized);

        if (isCore) {
          if (restrictAllowlist) return allowedMappingObjects.has(normalized);
          if (restrictDenylist) return !allowedMappingObjects.has(normalized);
          return true;
        }

        const isCustomTarget = target?.isStandard === false;

        if (!allowCustomMappingObjects) {
          if (!isCustomTarget) {
            if (restrictAllowlist) return allowedMappingObjects.has(normalized);
            if (restrictDenylist) return !allowedMappingObjects.has(normalized);
            return true;
          }
          return false;
        }

        // allowCustomObject true: custom rows only from lead-mapping API (enableLeadMapping on object config)
        if (isCustomTarget) {
          if (target?.fromEntitlement) return false;
          if (restrictDenylist && allowedMappingObjects.has(normalized)) return false;
          return true;
        }

        // Non-core standard objects still follow subscription allowlist/denylist
        if (restrictAllowlist) return allowedMappingObjects.has(normalized);
        if (restrictDenylist) return !allowedMappingObjects.has(normalized);
        return true;
      };

      const explainMappingAllowed = (target) => {
        const normalized = String(target?.objectName || target?.key || '').toLowerCase();
        const isCore = coreMappingNames.has(normalized);
        const isCustomTarget = target?.isStandard === false;
        const allowed = isMappingObjectAllowed(target);
        let reason = allowed ? 'allowed' : 'blocked';
        if (!allowed && isCore && restrictAllowlist) reason = 'core not in allowlist';
        if (!allowed && isCore && restrictDenylist && allowedMappingObjects.has(normalized)) {
          reason = 'core listed under denylist (mode=denylist)';
        }
        if (!allowed && !isCore && isCustomTarget && !allowCustomMappingObjects) reason = 'custom needs allowCustomObject';
        if (!allowed && !isCore && isCustomTarget && target?.fromEntitlement) reason = 'custom fromEntitlement only (invalid)';
        if (!allowed && !isCore && isCustomTarget && restrictDenylist && allowedMappingObjects.has(normalized)) {
          reason = 'custom in denylist';
        }
        if (!allowed && !isCore && !isCustomTarget && restrictAllowlist && !allowedMappingObjects.has(normalized)) {
          reason = 'non-core standard not in allowlist';
        }
        return { objectName: normalized, isCore, isCustomTarget, allowed, reason };
      };

      const baseTargets = [
        { key: 'account', objectName: 'account', label: 'Account', isCore: true },
        { key: 'contact', objectName: 'contact', label: 'Contact', isCore: true },
        { key: 'opportunity', objectName: 'opportunity', label: 'Opportunity', isCore: true }
      ];

      let relatedTargets = [];
      if (objectName && objectName.toLowerCase() === 'lead') {
        try {
          const configResponse = await ObjectConfigApi.fetchLeadMappingObjects();
          logLeadMapping('api lead-mapping-configs', {
            success: configResponse?.success,
            message: configResponse?.message,
            rowCount: Array.isArray(configResponse?.data) ? configResponse.data.length : 0,
            sample: Array.isArray(configResponse?.data)
              ? configResponse.data.slice(0, 30).map((c) => ({
                object_name: c?.object_name,
                is_standard: c?.is_standard,
                is_history_object: c?.is_history_object,
              }))
              : null,
          });
          if (configResponse?.success && Array.isArray(configResponse.data)) {
            const baseKeys = new Set(baseTargets.map(base => base.objectName.toLowerCase()));
            const afterHistory = configResponse.data.filter((config) => !config?.is_history_object);
            const afterCustomGate = afterHistory.filter(
              (config) => allowCustomMappingObjects || config?.is_standard === true
            );
            const droppedByCustomGate = afterHistory.filter(
              (config) => !(allowCustomMappingObjects || config?.is_standard === true)
            );
            relatedTargets = afterCustomGate
              .filter(config => !baseKeys.has(config.object_name?.toLowerCase()))
              .map(config => ({
                key: config.object_name,
                objectName: config.object_name,
                label: config.object_label || config.object_name,
                isRelated: true,
                isStandard: !!config.is_standard
              }));
            logLeadMapping('relatedTargets pipeline', {
              afterHistoryCount: afterHistory.length,
              afterCustomGateCount: afterCustomGate.length,
              droppedByCustomGate: droppedByCustomGate.map((c) => ({
                object_name: c?.object_name,
                is_standard: c?.is_standard,
              })),
              relatedTargetKeys: relatedTargets.map((t) => t.objectName),
            });
          } else {
            logLeadMapping('api lead-mapping-configs empty or failed', {
              success: configResponse?.success,
              dataType: typeof configResponse?.data,
            });
          }
        } catch (error) {
          console.error('Error fetching lead mapping targets:', error);
          logLeadMapping('api lead-mapping-configs error', { message: error?.message });
        }
      } else {
        logLeadMapping('skip lead-mapping API', {
          reason: 'Field mapping targets API only runs when objectName === "lead"',
          objectName,
        });
      }

      const combined = [...baseTargets, ...relatedTargets];
      logLeadMapping('allowance per target', combined.map(explainMappingAllowed));

      const targets = combined.filter(target => isMappingObjectAllowed(target));
      logLeadMapping('final mappingTargets', {
        keys: targets.map((t) => t.objectName),
        count: targets.length,
      });
      const targetFieldMap = {};
      const targetIdMap = {};

      const promises = targets.map(async (target) => {
        try {
          const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(target.objectName);
          let fields = [];
          let objectId = null;
          console.log('## response', response.data);

          if (response?.success && response?.data) {
            fields = Array.isArray(response.data.fields) ? response.data.fields : [];
            objectId = response.data.id;
          }

          // Filter out system fields and standard mapping fields
          console.log('## All Lead fields before filtering:', fields.map(f => ({ name: f.field_name, label: f.field_label })));
          const filteredFields = fields.filter(field =>
            !['id', 'createddate', 'createdbyid', 'lastmodifiedbyid', 'lastmodifieddate', 'is_deleted', 'company', 'first_name', 'last_name']
              .includes(field.field_name?.toLowerCase())
          );
          console.log('## filteredFields after filtering:', filteredFields.map(f => ({ name: f.field_name, label: f.field_label })));
          targetFieldMap[target.key] = filteredFields;

          if (target.key === 'account') {
            setAccountFields(filteredFields);
          } else if (target.key === 'contact') {
            setContactFields(filteredFields);
          } else if (target.key === 'opportunity') {
            setOpportunityFields(filteredFields);
          }

          // Store object ID
          targetIdMap[target.key] = objectId;
        } catch (error) {
          console.error(`Error fetching ${target.objectName} fields:`, error);
          targetFieldMap[target.key] = [];
          if (target.key === 'account') {
            setAccountFields([]);
          } else if (target.key === 'contact') {
            setContactFields([]);
          } else if (target.key === 'opportunity') {
            setOpportunityFields([]);
          }
        }
      });

      await Promise.all(promises);

      setObjectIds(prev => ({
        ...prev,
        ...targetIdMap
      }));

      setMappingTargets(targets.map(target => ({
        ...target,
        fields: targetFieldMap[target.key] || []
      })));

      // Selection vs subscription allowed list is applied in useEffect when mappingTargets/configuredMappingObjects update.
    } catch (error) {
      console.error('Error fetching mapping fields:', error);
    } finally {
      setLoadingMappingFields(false);
    }
  };

  // Fetch existing field mappings
  const fetchFieldMappings = async () => {
    try {
      const dataApi = new DataApi('leadmapping');
      const response = await dataApi.fetchAll();

      console.log('## Fetch response:', response.data);

      if (response.data.success) {
        setFieldMappings(response.data.data || []);
        console.log('Field mappings fetched:', response.data.data);
      } else {
        console.error('Failed to fetch field mappings:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching field mappings:', error);
    }
  };

  // Extract lead conversion configuration from already-fetched object config
  // No API call needed - data is already available from selectedObject or currentObjectConfig
  const fetchRedirectPreference = () => {
    // Check if we have the lead object config with config field
    // selectedObject comes from ObjectManager which already fetches config via fetchConfigsWithFields
    if (objectName && objectName.toLowerCase() === 'lead' && selectedObject && selectedObject.config) {
      const config = selectedObject.config || {};
      const leadConversionConfig = config.lead_conversion_config || {};

      setRedirectPreference(leadConversionConfig.redirectPreference || 'opportunity');
      setDefaultOpportunityStage(leadConversionConfig.defaultOpportunityStage || '');
      setConfiguredMappingObjects(Array.isArray(leadConversionConfig.mappingObjects)
        ? leadConversionConfig.mappingObjects
        : []);
      setDontMarkConverted(leadConversionConfig.dontMarkConverted === true);
      setSavedLeadConvertStatus(
        normalizeLeadConvertStatusFromLeadConversionConfig(leadConversionConfig)
      );
    } else if (currentObjectConfig && currentObjectConfig.config) {
      // Fallback: try to get from currentObjectConfig (fetched in fetchFields via fetchByObjectNameWithFields)
      const config = currentObjectConfig.config || {};
      const leadConversionConfig = config.lead_conversion_config || {};

      setRedirectPreference(leadConversionConfig.redirectPreference || 'opportunity');
      setDefaultOpportunityStage(leadConversionConfig.defaultOpportunityStage || '');
      setConfiguredMappingObjects(Array.isArray(leadConversionConfig.mappingObjects)
        ? leadConversionConfig.mappingObjects
        : []);
      setDontMarkConverted(leadConversionConfig.dontMarkConverted === true);
      setSavedLeadConvertStatus(
        normalizeLeadConvertStatusFromLeadConversionConfig(leadConversionConfig)
      );
    } else {
      // Default values if no config found
      setRedirectPreference('opportunity');
      setDefaultOpportunityStage('');
      setConfiguredMappingObjects([]);
      setDontMarkConverted(false);
      setSavedLeadConvertStatus(null);
    }
  };

  /** Same rules as backend GET /lead/conversion-config: subscription ∩ enableLeadMapping, then saved mappingObjects. */
  const refreshLeadConversionConfigFromServer = async () => {
    if (objectName && objectName.toLowerCase() !== 'lead') return;
    try {
      const api = new DataApi('objectconfig');
      const res = await api.get('/lead/conversion-config');
      if (res?.data?.success && res.data.data) {
        const d = res.data.data;
        setRedirectPreference(d.redirectPreference || 'opportunity');
        setDefaultOpportunityStage(d.defaultOpportunityStage || '');
        setConfiguredMappingObjects(Array.isArray(d.mappingObjects) ? d.mappingObjects : []);
        setDontMarkConverted(d.dontMarkConverted === true);
        setSavedLeadConvertStatus(normalizeLeadConvertStatusFromApiData(d));
        return;
      }
    } catch (e) {
      console.warn('refreshLeadConversionConfigFromServer failed:', e?.message || e);
    }
    fetchRedirectPreference();
  };

  // Auto-extract config when selectedObject or currentObjectConfig changes (not while field-mapping UI is open — that uses GET /lead/conversion-config)
  useEffect(() => {
    if (objectName && objectName.toLowerCase() === 'lead') {
      if (showFieldMappingModal) {
        return;
      }
      // Extract from selectedObject (from ObjectManager) or currentObjectConfig (from fetchFields)
      if (selectedObject && selectedObject.config) {
        const config = selectedObject.config || {};
        const leadConversionConfig = config.lead_conversion_config || {};
        setRedirectPreference(leadConversionConfig.redirectPreference || 'opportunity');
        setDefaultOpportunityStage(leadConversionConfig.defaultOpportunityStage || '');
        setConfiguredMappingObjects(Array.isArray(leadConversionConfig.mappingObjects)
          ? leadConversionConfig.mappingObjects
          : []);
        setDontMarkConverted(leadConversionConfig.dontMarkConverted === true);
        setSavedLeadConvertStatus(
          normalizeLeadConvertStatusFromLeadConversionConfig(leadConversionConfig)
        );
      } else if (currentObjectConfig && currentObjectConfig.config) {
        const config = currentObjectConfig.config || {};
        const leadConversionConfig = config.lead_conversion_config || {};
        setRedirectPreference(leadConversionConfig.redirectPreference || 'opportunity');
        setDefaultOpportunityStage(leadConversionConfig.defaultOpportunityStage || '');
        setConfiguredMappingObjects(Array.isArray(leadConversionConfig.mappingObjects)
          ? leadConversionConfig.mappingObjects
          : []);
        setDontMarkConverted(leadConversionConfig.dontMarkConverted === true);
        setSavedLeadConvertStatus(
          normalizeLeadConvertStatusFromLeadConversionConfig(leadConversionConfig)
        );
      } else {
        setRedirectPreference('opportunity');
        setDefaultOpportunityStage('');
        setConfiguredMappingObjects([]);
        setDontMarkConverted(false);
        setSavedLeadConvertStatus(null);
      }
    }
  }, [selectedObject, currentObjectConfig, objectName, showFieldMappingModal]);

  useEffect(() => {
    if (!showFieldMappingModal || objectName?.toLowerCase() !== 'lead') return;
    setLeadConvertStatusSelectKey(
      leadConvertStatusSelectKeyFromStored(savedLeadConvertStatus, leadStatusPicklistForConvertHide)
    );
  }, [showFieldMappingModal, savedLeadConvertStatus, leadStatusPicklistForConvertHide, objectName]);

  /**
   * Sync checkboxes from saved lead_conversion_config.mappingObjects only when the modal is open and
   * the eligible target list or saved mapping *content* changes — not on unrelated selectedObject ref churn.
   */
  const lastFieldMappingSyncKeyRef = useRef('');

  useEffect(() => {
    if (!showFieldMappingModal) {
      lastFieldMappingSyncKeyRef.current = '';
      return;
    }
    if (!Array.isArray(mappingTargets) || mappingTargets.length === 0) {
      return;
    }

    const targetKeySig = mappingTargets
      .map((t) => String(t.key).toLowerCase())
      .sort()
      .join('|');
    const saved = Array.isArray(configuredMappingObjects) ? configuredMappingObjects : [];
    const savedSig = saved.map((k) => String(k).toLowerCase()).sort().join('|');
    const syncKey = `${targetKeySig}::${savedSig}`;

    if (syncKey === lastFieldMappingSyncKeyRef.current) {
      return;
    }
    lastFieldMappingSyncKeyRef.current = syncKey;

    const configuredSet = new Set(saved.map((key) => String(key).toLowerCase()));

    setEnabledMappingObjects(() => {
      const next = {};
      mappingTargets.forEach((target) => {
        const key = target.key;
        next[key] =
          configuredSet.size > 0 && configuredSet.has(String(key).toLowerCase());
      });
      return next;
    });
  }, [mappingTargets, showFieldMappingModal, configuredMappingObjects]);

  // Extract opportunity stage picklist values from already fetched opportunityFields
  useEffect(() => {
    if (opportunityFields && opportunityFields.length > 0) {
      // Find the stage field
      const stageField = opportunityFields.find(
        field => field.field_name === 'stage' && (field.data_type === 'picklist' || field.data_type === 'multipicklist')
      );

      if (stageField) {
        // Use picklist_values_data if available (from updated fetchConfigsWithFields)
        if (stageField.picklist_values_data && Array.isArray(stageField.picklist_values_data)) {
          const options = stageField.picklist_values_data
            .filter(pv => pv.is_active !== false)
            .map(pv => ({
              value: pv.key || pv.value_key || pv.value,
              label: pv.label || pv.value_label || pv.value
            }))
            .sort((a, b) => {
              // Sort by sort_order if available
              const aOrder = stageField.picklist_values_data.find(pv => (pv.key || pv.value_key) === a.value)?.sort_order || 0;
              const bOrder = stageField.picklist_values_data.find(pv => (pv.key || pv.value_key) === b.value)?.sort_order || 0;
              return aOrder - bOrder;
            });

          setOpportunityStageOptions(options);
          setLoadingStageOptions(false);
        } else if (stageField.id) {
          // Fallback: fetch picklist values using PicklistApi only if picklist_values_data is not available
          setLoadingStageOptions(true);
          const PicklistApi = require('../../api/PicklistApi').default;
          const picklistApi = new PicklistApi();

          picklistApi.getActivePicklistValues(stageField.id)
            .then(picklistResponse => {
              if (picklistResponse.data.success && picklistResponse.data.data) {
                const options = picklistResponse.data.data
                  .map(pv => ({
                    value: pv.value_key || pv.key,
                    label: pv.value_label || pv.label
                  }))
                  .sort((a, b) => {
                    const aOrder = picklistResponse.data.data.find(pv => (pv.value_key || pv.key) === a.value)?.sort_order || 0;
                    const bOrder = picklistResponse.data.data.find(pv => (pv.value_key || pv.key) === b.value)?.sort_order || 0;
                    return aOrder - bOrder;
                  });

                setOpportunityStageOptions(options);
              }
            })
            .catch(picklistError => {
              console.error('Error fetching opportunity stage picklist values:', picklistError);
              setOpportunityStageOptions([]);
            })
            .finally(() => {
              setLoadingStageOptions(false);
            });
        } else {
          setOpportunityStageOptions([]);
          setLoadingStageOptions(false);
        }
      } else {
        setOpportunityStageOptions([]);
        setLoadingStageOptions(false);
      }
    }
  }, [opportunityFields]);

  // Handle modal close - clear temporary mapping data
  const handleCloseFieldMappingModal = () => {
    setShowFieldMappingModal(false);
    setMappingData({}); // Clear temporary mapping data
  };

  // Handle dropdown change for field mapping
  const handleMappingChange = (leadFieldId, targetObjectId, targetFieldId) => {
    setMappingData(prev => ({
      ...prev,
      [leadFieldId]: {
        ...prev[leadFieldId],
        [targetObjectId]: targetFieldId
      }
    }));
  };

  const removeMappingDataForTargetIds = (targetObjectIds) => {
    if (!Array.isArray(targetObjectIds) || targetObjectIds.length === 0) {
      return;
    }

    const targetIdSet = new Set(targetObjectIds.filter(Boolean));
    if (targetIdSet.size === 0) {
      return;
    }

    setMappingData(prevData => {
      const nextData = {};
      Object.keys(prevData).forEach(leadFieldId => {
        const fieldMappingsForLead = prevData[leadFieldId] || {};
        const filteredMappings = {};
        Object.keys(fieldMappingsForLead).forEach(targetObjectId => {
          if (!targetIdSet.has(targetObjectId)) {
            filteredMappings[targetObjectId] = fieldMappingsForLead[targetObjectId];
          }
        });
        if (Object.keys(filteredMappings).length > 0) {
          nextData[leadFieldId] = filteredMappings;
        }
      });
      return nextData;
    });
  };

  const handleMappingObjectSelection = (selectedKeys) => {
    const normalizedKeys = Array.isArray(selectedKeys) ? selectedKeys : [];
    const maxObjects = Number(leadMappingConfig?.maxObjects);
    if (Number.isFinite(maxObjects) && maxObjects > 0 && normalizedKeys.length > maxObjects) {
      showToastMessage(`You can enable up to ${maxObjects} mapping object${maxObjects > 1 ? 's' : ''}.`, 'danger');
      return;
    }
    const targetKeys = mappingTargets.map((target) => target.key);
    const disabledKeys = targetKeys.filter((key) => !normalizedKeys.includes(key));
    const disabledIds = disabledKeys.map((key) => objectIds[key]).filter(Boolean);

    removeMappingDataForTargetIds(disabledIds);

    setEnabledMappingObjects(() => {
      const next = {};
      targetKeys.forEach((key) => {
        next[key] = normalizedKeys.includes(key);
      });
      return next;
    });
  };

  // Get current mapping value for a field
  const getCurrentMapping = (leadFieldId, targetObjectId) => {
    // First check if there's a pending change
    if (mappingData[leadFieldId] && mappingData[leadFieldId][targetObjectId] !== undefined) {
      return mappingData[leadFieldId][targetObjectId];
    }

    // Then check existing mappings
    const existingMapping = fieldMappings.find(mapping =>
      mapping.lead_field_id === leadFieldId &&
      mapping.target_object_id === targetObjectId
    );

    return existingMapping ? existingMapping.target_field_id : '';
  };

  // Check if a mapping is completed (has a value)
  const isMappingCompleted = (leadFieldId, targetObjectId) => {
    const currentMapping = getCurrentMapping(leadFieldId, targetObjectId);
    return currentMapping && currentMapping !== '';
  };

  // Get available fields for dropdown (excluding already selected ones)
  const getAvailableFields = (leadFieldId, targetObjectId, targetFields) => {
    const leadField = fields.find(f => f.id === leadFieldId);
    const isLeadIdField = String(leadField?.field_name || '').toLowerCase() === 'id';

    // Special case: the Lead `id` field can only map to lookup target fields whose
    // reference_object points at `lead` (or is null/empty for an "open" lookup).
    // Other data-type compatibility rules don't apply here.
    const compatibleFields = isLeadIdField
      ? (targetFields || []).filter((f) => {
        const dataType = String(f.data_type || '').toLowerCase();
        if (dataType !== 'lookup') return false;
        const ref = f.reference_object;
        if (ref === null || ref === undefined || String(ref).trim() === '') {
          return true;
        }
        return String(ref).toLowerCase() === 'lead';
      })
      : getCompatibleFields(leadField?.data_type, targetFields);

    // Fields to exclude from dropdown options
    const excludedFieldNames = ['name'];

    // Get all currently selected field IDs for this target object
    const selectedFieldIds = new Set();

    // Check existing mappings
    fieldMappings.forEach(mapping => {
      if (mapping.target_object_id === targetObjectId) {
        selectedFieldIds.add(mapping.target_field_id);
      }
    });

    // Check pending changes in mappingData
    Object.keys(mappingData).forEach(leadFieldIdKey => {
      if (mappingData[leadFieldIdKey] && mappingData[leadFieldIdKey][targetObjectId]) {
        selectedFieldIds.add(mappingData[leadFieldIdKey][targetObjectId]);
      }
    });

    // Filter out selected fields, but keep the currently selected field for this specific mapping
    const currentMapping = getCurrentMapping(leadFieldId, targetObjectId);

    return compatibleFields.filter(field =>
      // Exclude specific field names
      !excludedFieldNames.includes(field.field_name) &&
      // Exclude readonly fields
      !field.is_readonly &&
      // Exclude already selected fields, but keep current mapping
      (!selectedFieldIds.has(field.id) || field.id === currentMapping)
    );
  };

  // Save field mappings and redirect preference
  const saveFieldMappings = async () => {
    try {
      setSavingMappings(true);

      // Save lead conversion configuration (redirect preference and default opportunity stage)
      try {
        const dataApi = new DataApi('objectconfig');
        const isTargetEnabled = (t) => {
          if (enabledMappingObjects[t.key]) return true;
          const want = String(t.key).toLowerCase();
          return Object.entries(enabledMappingObjects).some(
            ([k, v]) => v && String(k).toLowerCase() === want
          );
        };
        const enabledObjectKeys = mappingTargets.filter(isTargetEnabled).map((t) => t.key);
        const statusOpt = leadStatusPicklistForConvertHide.find(
          (o) => String(o.key) === String(leadConvertStatusSelectKey)
        );
        const leadConvertStatusPayload =
          leadConvertStatusSelectKey && statusOpt
            ? { valueKey: String(statusOpt.key), label: String(statusOpt.value || statusOpt.key) }
            : null;
        const convResponse = await dataApi.post('/lead/conversion-config', {
          redirectPreference: redirectPreference,
          defaultOpportunityStage: defaultOpportunityStage || null,
          mappingObjects: enabledObjectKeys,
          dontMarkConverted: dontMarkConverted === true,
          leadConvertStatus: leadConvertStatusPayload
        });
        const convPayload = convResponse?.data;
        if (convPayload?.success && Array.isArray(convPayload.data?.mappingObjects)) {
          setConfiguredMappingObjects(convPayload.data.mappingObjects);
        }
        if (convPayload?.success && convPayload.data) {
          setSavedLeadConvertStatus(normalizeLeadConvertStatusFromApiData(convPayload.data));
        }
      } catch (configError) {
        console.error('Error saving lead conversion config:', configError);
        // Continue with field mappings even if config save fails
      }

      // Prepare create mappings
      const createMappings = [];
      const removeMappingIdSet = new Set();
      const allTargetFields = mappingTargets.length > 0
        ? mappingTargets.flatMap(target => target.fields || [])
        : [...accountFields, ...contactFields, ...opportunityFields];
      const enabledTargetObjectIds = new Set(
        mappingTargets
          .filter((t) => enabledMappingObjects[t.key] && objectIds[t.key])
          .map((t) => objectIds[t.key])
      );
      const disabledTargetObjectIds = new Set(
        mappingTargets
          .filter((t) => !enabledMappingObjects[t.key] && objectIds[t.key])
          .map((t) => objectIds[t.key])
      );

      // Remove any existing mappings for disabled target objects
      fieldMappings.forEach(mapping => {
        if (disabledTargetObjectIds.has(mapping.target_object_id)) {
          removeMappingIdSet.add(mapping.id);
        }
      });

      // Drop mappings for lead fields that are not eligible for mapping (e.g. converted lookups)
      fieldMappings.forEach((mapping) => {
        const leadField = fields.find((f) => String(f.id) === String(mapping.lead_field_id));
        if (leadField && isLeadFieldExcludedFromMapping(leadField, { allowIdField: dontMarkConverted })) {
          removeMappingIdSet.add(mapping.id);
        }
      });

      // first_name / last_name → Account, Contact, Opportunity are not stored as field mappings
      fieldMappings.forEach((mapping) => {
        const leadField = fields.find((f) => String(f.id) === String(mapping.lead_field_id));
        const targetKey = Object.keys(objectIds).find((key) => objectIds[key] === mapping.target_object_id);
        if (leadField && isStandardConversionSkipsLeadNameMapping(leadField, targetKey)) {
          removeMappingIdSet.add(mapping.id);
        }
      });

      // Process mapping data
      Object.keys(mappingData).forEach(leadFieldId => {
        const leadField = fields.find((f) => String(f.id) === String(leadFieldId));
        if (leadField && isLeadFieldExcludedFromMapping(leadField, { allowIdField: dontMarkConverted })) {
          return;
        }
        const fieldMappingData = mappingData[leadFieldId];

        Object.keys(fieldMappingData).forEach(targetObjectId => {
          if (!enabledTargetObjectIds.has(targetObjectId)) {
            return;
          }
          const targetKey = Object.keys(objectIds).find((key) => objectIds[key] === targetObjectId);
          if (leadField && isStandardConversionSkipsLeadNameMapping(leadField, targetKey)) {
            const existingMapping = fieldMappings.find(
              (m) =>
                String(m.lead_field_id) === String(leadFieldId) &&
                m.target_object_id === targetObjectId
            );
            if (existingMapping) {
              removeMappingIdSet.add(existingMapping.id);
            }
            return;
          }
          const targetFieldId = fieldMappingData[targetObjectId];

          if (targetFieldId) {
            // Check if this mapping already exists
            const existingMapping = fieldMappings.find(mapping =>
              mapping.lead_field_id === leadFieldId &&
              mapping.target_object_id === targetObjectId
            );

            if (existingMapping) {
              // If field changed, remove old and create new
              if (existingMapping.target_field_id !== targetFieldId) {
                removeMappingIdSet.add(existingMapping.id);

                // Find field names for debugging
                const leadField = fields.find(f => f.id === leadFieldId);
                const targetField = allTargetFields.find(f => f.id === targetFieldId);
                // Reverse lookup object name from UUID
                const targetObjectName = Object.keys(objectIds).find(key => objectIds[key] === targetObjectId) || 'Unknown';

                createMappings.push({
                  leadFieldId,
                  targetObjectId,
                  targetFieldId,
                  leadFieldName: leadField?.field_name || 'Unknown',
                  leadFieldLabel: leadField?.field_label || 'Unknown',
                  targetFieldName: targetField?.field_name || 'Unknown',
                  targetFieldLabel: targetField?.field_label || 'Unknown',
                  targetObjectName: targetObjectName
                });
              }
            } else {
              // New mapping
              // Find field names for debugging
              const leadField = fields.find(f => f.id === leadFieldId);
              const targetField = allTargetFields.find(f => f.id === targetFieldId);
              // Reverse lookup object name from UUID
              const targetObjectName = Object.keys(objectIds).find(key => objectIds[key] === targetObjectId) || 'Unknown';

              createMappings.push({
                leadFieldId,
                targetObjectId,
                targetFieldId,
                leadFieldName: leadField?.field_name || 'Unknown',
                leadFieldLabel: leadField?.field_label || 'Unknown',
                targetFieldName: targetField?.field_name || 'Unknown',
                targetFieldLabel: targetField?.field_label || 'Unknown',
                targetObjectName: targetObjectName
              });
            }
          } else {
            // Field was cleared, remove existing mapping if any
            const existingMapping = fieldMappings.find(mapping =>
              mapping.lead_field_id === leadFieldId &&
              mapping.target_object_id === targetObjectId
            );

            if (existingMapping) {
              removeMappingIdSet.add(existingMapping.id);
            }
          }
        });
      });

      console.log('## createMappings', createMappings);
      const removeMappingIds = Array.from(removeMappingIdSet);
      console.log('## removeMappingIds', removeMappingIds);

      const dataApi = new DataApi('leadmapping');
      const response = await dataApi.post('', {
        createMappings,
        removeMappingIds
      });

      console.log('## Save response:', response.data);

      if (response.data.success) {
        showToastMessage('Field mappings saved successfully!', 'success');
        // Fetch updated mappings
        await fetchFieldMappings();

        // Reset mapping data
        setMappingData({});
        // Close the modal
        handleCloseFieldMappingModal();

        // Refresh the page to ensure all data is up to date
        setTimeout(() => {
          window.location.reload();
        }, 500); // Small delay to show success message
      } else {
        showToastMessage(response.data.message || 'Failed to save mappings', 'danger');
      }
    } catch (error) {
      console.error('Error saving field mappings:', error);
      showToastMessage('Failed to save field mappings', 'danger');
    } finally {
      setSavingMappings(false);
    }
  };

  const fetchFields = async () => {
    try {
      setLoading(true);
      setError('');

      // Use /api/objectconfig/:objectName which returns object + fields
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      let fields = [];
      let objectConfig = null;

      if (response?.success && response?.data) {
        fields = Array.isArray(response.data.fields) ? response.data.fields : [];
        objectConfig = response.data; // Store the full object config including display_config
      } else if (response?.data?.success && response?.data?.data) {
        // handle shape if wrapped
        fields = Array.isArray(response.data.data.fields) ? response.data.data.fields : [];
        objectConfig = response.data.data; // Store the full object config including display_config
      } else {
        setError('Failed to load fields');
        return;
      }

      // Store the object config so we can access display_config later
      setCurrentObjectConfig(objectConfig);

      console.log('🔍 Loaded fields data:', fields);
      // Check if any calculated fields have calculated_expression
      const calculatedFields = fields.filter(f => f.data_type === 'calculated');
      console.log('🔍 Calculated fields:', calculatedFields);
      calculatedFields.forEach(field => {
        console.log(`🔍 Field ${field.field_name} calculated_expression:`, field.calculated_expression);
      });

      // Load picklist values for picklist fields from the new table
      const picklistFields = fields.filter(f => f.data_type === 'picklist' || f.data_type === 'multipicklist');
      if (picklistFields.length > 0) {
        try {
          const PicklistApi = require('../../api/PicklistApi').default;
          const picklistApi = new PicklistApi();

          for (const field of picklistFields) {
            try {
              const picklistResponse = await picklistApi.getPicklistValues(field.id);
              if (picklistResponse.data.success && picklistResponse.data.data) {
                // Convert picklist values to the format expected by the display logic
                const picklistValues = picklistResponse.data.data.map(pv => ({
                  key: pv.value_key,
                  value: pv.value_label,
                  path_color: pv.path_color || ''
                }));
                field.picklist_values = picklistValues;
              }
            } catch (error) {
              console.error(`Error loading picklist values for field ${field.field_name}:`, error);
              // Keep existing picklist_values if any, otherwise leave empty
            }
          }
        } catch (error) {
          console.error('Error loading picklist values:', error);
        }
      }

      setFields(fields);
    } catch (error) {
      console.error('Error fetching fields:', error);
      setError('Failed to load fields: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchTrashedFields = async () => {
    try {
      setLoadingTrashedFields(true);
      const response = await ObjectConfigApi.getTrashedFields(objectName);

      if (response?.success) {
        setTrashedFields(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch trashed fields');
      }
    } catch (error) {
      console.error('Error fetching trashed fields:', error);
      const msg = 'Failed to fetch trashed fields: ' + (error.message || 'Unknown error');
      setError(msg);
    } finally {
      setLoadingTrashedFields(false);
    }
  };

  const handlePermanentlyDeleteField = (field) => {
    if (permanentDeleteModalDeleting || showPermanentDeleteModal) return;
    setFieldToPermanentlyDelete(field);
    setPermanentDeleteModalError(null);
    setShowPermanentDeleteModal(true);
  };

  const resetPermanentDeleteModal = () => {
    setShowPermanentDeleteModal(false);
    setFieldToPermanentlyDelete(null);
    setPermanentDeleteModalDeleting(false);
    setPermanentDeleteModalError(null);
  };

  const confirmPermanentlyDeleteField = async () => {
    if (!fieldToPermanentlyDelete || permanentDeleteModalDeleting) return;

    setPermanentDeleteModalError(null);
    setPermanentDeleteModalDeleting(true);

    try {
      const response = await ObjectConfigApi.permanentlyDeleteField(
        objectName,
        fieldToPermanentlyDelete.field_id
      );

      if (response?.success) {
        setTrashedFields(trashedFields.filter(f => f.field_id !== fieldToPermanentlyDelete.field_id));
        showToastMessage('Field permanently deleted successfully!');
        if (trashedFields.length <= 1) {
          setShowTrashModal(false);
        }
        resetPermanentDeleteModal();
      } else {
        setPermanentDeleteModalError(response?.message || 'Failed to permanently delete field');
      }
    } catch (error) {
      console.error('Error permanently deleting field:', error);
      setPermanentDeleteModalError(error.message || 'Failed to permanently delete field');
    } finally {
      setPermanentDeleteModalDeleting(false);
    }
  };

  const handleRestoreField = async (fieldId) => {
    try {
      setLoadingTrashedFields(true);

      const response = await ObjectConfigApi.restoreField(objectName, fieldId);

      if (response?.success) {
        // Remove the field from the trashed fields list
        setTrashedFields(trashedFields.filter(f => f.field_id !== fieldId));
        showToastMessage('Field restored successfully!');
        // Refresh the main fields list to show the restored field
        fetchFields();
        // Close the modal if no more trashed fields
        if (trashedFields.length <= 1) {
          setShowTrashModal(false);
        }
      } else {
        setError(response?.message || 'Failed to restore field');
      }
    } catch (error) {
      console.error('Error restoring field:', error);
      setError('Failed to restore field: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedFields(false);
    }
  };

  const handleCreateField = () => {
    if (!allowFieldCreate) {
      showToastMessage('Field creation is disabled for this tenant.', 'danger');
      return;
    }
    if (standardObjectBlocksNewFields) {
      showToastMessage(
        'Field creation is disabled for this object (standard_object.config.create_fields is false).',
        'danger'
      );
      return;
    }
    if (isCustomFieldLimitReached) {
      showToastMessage(`Custom field limit reached (${maxCustomFields}).`, 'danger');
      return;
    }
    setEditingField(null);
    setFieldForm({
      field_name: '',
      field_label: '',
      data_type: 'text',
      is_required: false,
      is_readonly: false,
      is_unique: false,
      is_reference: false,
      reference_object: '',
      picklist_values: [],
      picklist_default_index: null,
      max_length: '',
      calculated_expression: '',
      precision: '',
      scale: '',
      start_number: 1,
      prefix: '',
      format_mask: '',
      lookup_search_fields: '',
      display_fields: '',
      lookup_filter: [],
      rollup_field: '',
      rollup_operation: '',
      rollup_criteria: { logic: 'AND', conditions: [] },
      is_standard: false,
      is_path_field: false,
      display_config: {},
      config: {},
      calculated_has_return_type: false
    });
    setModalError('');
    setModalTouchedSave(false);
    setTouched({});
    setFormulaValidationResult(null);
    setBulkPicklistValues('');
    setBulkPicklistPathColor('');
    // Clear any pending auto-validation
    if (autoValidationTimeout) {
      clearTimeout(autoValidationTimeout);
      setAutoValidationTimeout(null);
    }
    setShowFieldModal(true);
  };

  const handleEditField = (field) => {
    console.log('🔍 Editing field data:', field);
    console.log('🔍 Calculated expression value:', field.calculated_expression);
    setEditingField(field);

    // Parse picklist values for form (editing uses Manage Values for live picklist; grid is for reference)
    let picklistValues = [];
    let picklistDefaultIndex = null;
    if (field.picklist_values) {
      if (Array.isArray(field.picklist_values)) {
        picklistValues = field.picklist_values.map(normalizePicklistRowForForm);
        const di = field.picklist_values.findIndex(
          (v) => v && (v.is_default === true || v.isDefault === true)
        );
        if (di >= 0) picklistDefaultIndex = di;
      } else if (typeof field.picklist_values === 'string') {
        try {
          const parsed = JSON.parse(field.picklist_values);
          if (Array.isArray(parsed)) {
            picklistValues = parsed.map(normalizePicklistRowForForm);
            const di = parsed.findIndex(
              (v) => v && (v.is_default === true || v.isDefault === true)
            );
            if (di >= 0) picklistDefaultIndex = di;
          }
        } catch (e) {
          const s = field.picklist_values;
          picklistValues = [normalizePicklistRowForForm({ key: s, value: s })];
        }
      }
    }

    // Parse lookup fields properly
    let lookupSearchFields = [];
    let displayFields = [];
    if (field.lookup_search_fields) {
      if (Array.isArray(field.lookup_search_fields)) {
        lookupSearchFields = field.lookup_search_fields;
      } else if (typeof field.lookup_search_fields === 'string') {
        lookupSearchFields = field.lookup_search_fields.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    if (field.display_fields) {
      if (Array.isArray(field.display_fields)) {
        displayFields = field.display_fields;
      } else if (typeof field.display_fields === 'string') {
        displayFields = field.display_fields.split(',').map(s => s.trim()).filter(Boolean);
      }
    }

    // Parse lookup_filter properly
    // Structure: { logic: "AND|OR|custom", filters: [...] }
    let lookupFilter = { logic: 'AND', filters: [] };
    const mapLookupFilterRowForUi = (row) =>
      withInferredFilterValueType({
        field: row?.field || row?.fieldName || '',
        operator: row?.operator || 'equals',
        value: row?.value == null ? '' : row.value,
        valueType: row?.valueType || row?.value_type,
        displayValue: row?.displayValue || '',
      });
    const parseLookupFilterLogic = (rawLogic, fallback = 'AND') =>
      normalizeStoredLookupFilterLogic(rawLogic || fallback);

    if (field.lookup_filter) {
      if (typeof field.lookup_filter === 'string') {
        try {
          const parsed = JSON.parse(field.lookup_filter);
          if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.filters) {
            lookupFilter = {
              logic: parseLookupFilterLogic(parsed.logic),
              filters: Array.isArray(parsed.filters)
                ? parsed.filters.map(mapLookupFilterRowForUi)
                : []
            };
          } else if (Array.isArray(parsed)) {
            lookupFilter = {
              logic: parseLookupFilterLogic(field.lookup_filter_logic),
              filters: parsed.map(mapLookupFilterRowForUi)
            };
          }
        } catch (e) {
          console.error('Failed to parse lookup_filter:', e);
        }
      } else if (typeof field.lookup_filter === 'object' && !Array.isArray(field.lookup_filter) && field.lookup_filter.filters) {
        lookupFilter = {
          logic: parseLookupFilterLogic(field.lookup_filter.logic),
          filters: Array.isArray(field.lookup_filter.filters)
            ? field.lookup_filter.filters.map(mapLookupFilterRowForUi)
            : []
        };
      } else if (Array.isArray(field.lookup_filter)) {
        lookupFilter = {
          logic: parseLookupFilterLogic(field.lookup_filter_logic),
          filters: field.lookup_filter.map(mapLookupFilterRowForUi)
        };
      }
    }

    // Parse rollup_criteria: UI uses ConditionRow shape { fieldName, operator, value }
    let rollupCriteria = { logic: 'AND', conditions: [] };
    if (field.rollup_criteria) {
      try {
        const parsed = typeof field.rollup_criteria === 'string'
          ? JSON.parse(field.rollup_criteria)
          : field.rollup_criteria;
        if (parsed && typeof parsed === 'object') {
          const conditions = Array.isArray(parsed.conditions)
            ? parsed.conditions
            : (Array.isArray(parsed.filters) ? parsed.filters : []);
          rollupCriteria = {
            logic: parsed.logic && (String(parsed.logic).toUpperCase() === 'OR' || String(parsed.logic).toUpperCase() === 'AND')
              ? String(parsed.logic).toUpperCase()
              : 'AND',
            conditions: conditions.map((c) => ({
              fieldName: c.fieldName || c.field || '',
              operator: normalizeRollupOperatorForUi(c.operator),
              value: c.value == null ? '' : c.value,
              valueType: c.valueType || c.value_type || FILTER_VALUE_TYPE_STATIC,
            }))
          };
        }
      } catch (e) {
        console.error('Failed to parse rollup_criteria:', e);
      }
    }

    // Parse display_config for barcode, qrcode, and image fields
    let displayConfig = {};
    if (field.display_config) {
      if (typeof field.display_config === 'string') {
        try {
          displayConfig = JSON.parse(field.display_config);
        } catch (e) {
          console.error('Failed to parse display_config:', e);
          displayConfig = {};
        }
      } else if (typeof field.display_config === 'object') {
        displayConfig = field.display_config;
      }
    }

    let calculatedDisplayConfig = displayConfig;
    let calculatedHasReturnType = false;

    if ((field.data_type || '').toLowerCase() === 'calculated') {
      const { displayConfig: nextConfig, hasReturnType } = buildCalculatedDisplayConfigForState(displayConfig);
      calculatedDisplayConfig = nextConfig;
      calculatedHasReturnType = hasReturnType;
    }

    const formData = {
      field_name: field.field_name,
      field_label: field.field_label,
      data_type: (field.data_type || 'text').toLowerCase(),
      is_required: field.is_required || false,
      is_readonly: field.is_readonly || false,
      is_unique:
        (field.data_type || '').toLowerCase() === 'autonumber' || !!field.is_unique,
      is_reference: field.is_reference || false,
      reference_object: field.reference_object || '',
      picklist_values: picklistValues,
      picklist_default_index: picklistDefaultIndex,
      max_length:
        field.max_length ||
        (field.data_type === 'picklist' || field.data_type === 'multipicklist'
          ? String(PICKLIST_FIELD_MAX_LENGTH)
          : ''),
      calculated_expression: field.calculated_expression || '',
      precision: field.precision || '',
      scale: field.scale || '',
      start_number: field.start_number || 1,
      prefix: field.prefix || '',
      format_mask: field.format_mask || '',
      lookup_search_fields: lookupSearchFields,
      display_fields: displayFields,
      lookup_filter: lookupFilter,
      rollup_field: field.rollup_field || '',
      rollup_operation: field.rollup_operation || '',
      rollup_criteria: rollupCriteria,
      is_standard: field.is_standard || false,
      is_path_field: field.is_path_field || false,
      display_config: calculatedDisplayConfig,
      config: parseFieldConfig(field),
      calculated_has_return_type: calculatedHasReturnType
    };
    console.log('🔍 Setting form data:', formData);
    console.log('🔍 Form calculated_expression:', formData.calculated_expression);
    setFieldForm(formData);
    setModalError('');
    setModalTouchedSave(false);
    setTouched({});
    setFormulaValidationResult(null);
    setBulkPicklistValues('');
    setBulkPicklistPathColor('');
    // Clear any pending auto-validation
    if (autoValidationTimeout) {
      clearTimeout(autoValidationTimeout);
      setAutoValidationTimeout(null);
    }
    setShowFieldModal(true);
  };

  const handleSaveField = async (saveAndNew = false) => {
    try {
      setLoading(true);
      setError(''); // page-level
      setModalError('');
      setModalTouchedSave(true);

      if (!editingField && standardObjectBlocksNewFields) {
        const msg =
          'Field creation is disabled for this object (standard_object.config.create_fields is false).';
        if (showFieldModal) setModalError(msg);
        else setError(msg);
        return;
      }

      if (!fieldForm.field_name || !fieldForm.field_label) {
        if (showFieldModal) setModalError('Field name and label are required'); else setError('Field name and label are required');
        return;
      }

      // Do not allow __x in field name; backend adds it automatically for custom fields
      if (!editingField && fieldForm.field_name.includes('__x')) {
        const msg = 'Field name must not contain __x; it is added automatically for custom fields.';
        if (showFieldModal) setModalError(msg); else setError(msg);
        return;
      }

      // Data type change validation for editing
      if (editingField && editingField.data_type !== fieldForm.data_type) {
        const originalType = editingField.data_type.toLowerCase();
        const newType = fieldForm.data_type.toLowerCase();

        // Define allowed data type changes
        const allowedChanges = {
          'currency': ['text', 'textarea', 'richtext', 'number', 'percent'],
          'percent': ['text', 'textarea', 'richtext', 'number', 'currency'],
          'number': ['text', 'textarea', 'richtext', 'currency', 'percent'],
          'decimal': ['text', 'textarea', 'richtext', 'currency', 'percent'],
          'float': ['text', 'textarea', 'richtext', 'currency', 'percent'],
          'text': ['textarea', 'richtext', 'email', 'phone'],
          'textarea': ['text', 'richtext'],
          'richtext': ['text', 'textarea'],
          'email': ['text', 'textarea'],
          'phone': ['text', 'textarea'],
          'url': ['text', 'textarea'],
          'date': ['datetime'],
          'datetime': ['date'],
          'picklist': ['multipicklist'],
          'multipicklist': ['picklist'],
          'autonumber': ['text', 'textarea', 'richtext', 'number']
        };

        const allowedTargetTypes = allowedChanges[originalType] || [];
        if (!allowedTargetTypes.includes(newType)) {
          const errorMsg = `Cannot change data type from "${originalType}" to "${newType}". Allowed changes: ${allowedTargetTypes.join(', ')}`;
          if (showFieldModal) setModalError(errorMsg); else setError(errorMsg);
          return;
        }
      }

      // Additional validations
      if ((fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && !editingField) {
        if (!Array.isArray(fieldForm.picklist_values) || fieldForm.picklist_values.length === 0) {
          if (showFieldModal) setModalError('Picklist values are required for picklist types'); else setError('Picklist values are required for picklist types');
          return;
        }
        const labels = fieldForm.picklist_values.map((v) =>
          v && v.value != null ? String(v.value).trim() : ''
        );
        if (labels.some((l) => !l)) {
          if (showFieldModal) setModalError('Each picklist value needs a label'); else setError('Each picklist value needs a label');
          return;
        }
        const keys = fieldForm.picklist_values.map((v) =>
          v && v.key != null ? String(v.key).trim() : ''
        );
        if (keys.some((k) => !k)) {
          if (showFieldModal) setModalError('Each picklist value needs an API name'); else setError('Each picklist value needs an API name');
          return;
        }
        const seen = new Set();
        for (const k of keys) {
          const lower = k.toLowerCase();
          if (seen.has(lower)) {
            if (showFieldModal) setModalError('Picklist API names must be unique'); else setError('Picklist API names must be unique');
            return;
          }
          seen.add(lower);
        }
      }
      if (fieldForm.data_type === 'lookup') {
        // Allow editing of label and other properties when reference_object is null/blank (for existing fields)
        if (!fieldForm.reference_object) {
          if (editingField) {
            // If editing an existing field and reference_object is null/blank,
            // allow label changes without validation
            console.log('⚠️ Editing lookup field with null/blank reference_object - allowing label changes only');
          } else {
            // For new fields, reference_object is required
            if (showFieldModal) setModalError('Reference object is required for lookup'); else setError('Reference object is required for lookup');
            return;
          }
        }

        // Only validate search and display fields if reference_object is present
        if (fieldForm.reference_object) {
          const searchArr = Array.isArray(fieldForm.lookup_search_fields)
            ? fieldForm.lookup_search_fields
            : (typeof fieldForm.lookup_search_fields === 'string' && fieldForm.lookup_search_fields.trim() !== ''
              ? fieldForm.lookup_search_fields.split(',').map(s => s.trim()).filter(Boolean)
              : []);
          const displayArr = Array.isArray(fieldForm.display_fields)
            ? fieldForm.display_fields
            : (typeof fieldForm.display_fields === 'string' && fieldForm.display_fields.trim() !== ''
              ? fieldForm.display_fields.split(',').map(s => s.trim()).filter(Boolean)
              : []);
          if (searchArr.length === 0 || displayArr.length === 0) {
            if (showFieldModal) setModalError('Lookup search fields and display fields are required'); else setError('Lookup search fields and display fields are required');
            return;
          }

          fieldForm.lookup_search_fields = searchArr;
          fieldForm.display_fields = displayArr;
        }
      }
      if (fieldForm.data_type === 'rollupsummary') {
        // Allow editing of label and other properties when reference_object is null/blank (for existing fields)
        if (!fieldForm.reference_object) {
          if (editingField) {
            // If editing an existing field and reference_object is null/blank,
            // allow label changes without validation
            console.log('⚠️ Editing rollup summary field with null/blank reference_object - allowing label changes only');
          } else {
            // For new fields, reference_object is required
            if (showFieldModal) setModalError('Reference object is required for rollup summary'); else setError('Reference object is required for rollup summary');
            return;
          }
        }

        // Only validate rollup configuration if reference_object is present
        if (fieldForm.reference_object) {
          if (!fieldForm.rollup_field) {
            if (showFieldModal) setModalError('Rollup field is required for rollup summary'); else setError('Rollup field is required for rollup summary');
            return;
          }

          // Validate that rollup field is not a calculated field
          const validateRollupField = async () => {
            try {
              const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(fieldForm.reference_object);
              const flds = res?.data?.fields || res?.fields || [];

              const rollupField = flds.find(f => f.field_name === fieldForm.rollup_field);
              if (rollupField && (rollupField.data_type || '').toLowerCase() === 'calculated') {
                const errorMsg = `Cannot use calculated field "${fieldForm.rollup_field}" as rollup field`;
                if (showFieldModal) setModalError(errorMsg); else setError(errorMsg);
                return false;
              }

              return true;
            } catch (error) {
              console.error('Error validating rollup field:', error);
              return true; // Allow to proceed if validation fails
            }
          };

          const isValidRollupField = await validateRollupField();
          if (!isValidRollupField) {
            return;
          }
          if (!fieldForm.rollup_operation) {
            if (showFieldModal) setModalError('Rollup operation is required for rollup summary'); else setError('Rollup operation is required for rollup summary');
            return;
          }
        }
      }

      // Validate calculated expression for calculated fields
      if (fieldForm.data_type === 'calculated') {
        if (!fieldForm.calculated_expression || fieldForm.calculated_expression.trim() === '') {
          if (showFieldModal) setModalError('Calculated expression is required for calculated fields'); else setError('Calculated expression is required for calculated fields');
          return;
        }
      }

      // Validate max length for text and picklist fields
      if (['text', 'textarea', 'richtext', 'password', 'picklist', 'multipicklist'].includes(fieldForm.data_type.toLowerCase())) {
        if (!fieldForm.max_length || fieldForm.max_length === '' || Number(fieldForm.max_length) <= 0) {
          const msg =
            fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist'
              ? 'Max length is required for picklist fields'
              : 'Max length is required for text fields';
          if (showFieldModal) setModalError(msg); else setError(msg);
          return;
        }

        // Additional validation for text field max lengths
        if (fieldForm.data_type === 'password' && Number(fieldForm.max_length) > 255) {
          if (showFieldModal) setModalError('Password max length cannot exceed 255 characters'); else setError('Password max length cannot exceed 255 characters');
          return;
        }
        if (fieldForm.data_type === 'textarea' && Number(fieldForm.max_length) > 150000) {
          if (showFieldModal) setModalError('Text Area max length cannot exceed 150,000 characters'); else setError('Text Area max length cannot exceed 150,000 characters');
          return;
        }
        if (fieldForm.data_type === 'richtext' && Number(fieldForm.max_length) > 150000) {
          if (showFieldModal) setModalError('Rich Text max length cannot exceed 150,000 characters'); else setError('Rich Text max length cannot exceed 150,000 characters');
          return;
        }
        if (
          (fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') &&
          Number(fieldForm.max_length) > PICKLIST_FIELD_MAX_LENGTH
        ) {
          if (showFieldModal) {
            setModalError(`Picklist max length cannot exceed ${PICKLIST_FIELD_MAX_LENGTH.toLocaleString()} characters`);
          } else {
            setError(`Picklist max length cannot exceed ${PICKLIST_FIELD_MAX_LENGTH.toLocaleString()} characters`);
          }
          return;
        }
      }

      // Validate start number for autonumber fields
      if (fieldForm.data_type === 'autonumber') {
        if (!fieldForm.start_number || Number(fieldForm.start_number) < 1) {
          if (showFieldModal) setModalError('Start number is required and must be at least 1'); else setError('Start number is required and must be at least 1');
          return;
        }
      }

      // Validate is_path_field is only set for picklist fields
      if (fieldForm.is_path_field && fieldForm.data_type !== 'picklist' && fieldForm.data_type !== 'multipicklist') {
        if (showFieldModal) setModalError('Path Field can only be enabled for Picklist or Multi Picklist fields'); else setError('Path Field can only be enabled for Picklist or Multi Picklist fields');
        return;
      }

      const {
        calculated_has_return_type,
        display_config,
        picklist_default_index,
        ...formWithoutFlag
      } = fieldForm;

      if (
        !editingField &&
        (fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') &&
        Array.isArray(formWithoutFlag.picklist_values) &&
        formWithoutFlag.picklist_values.length > 0
      ) {
        const n = formWithoutFlag.picklist_values.length;
        let idx = null;
        if (picklist_default_index !== null && picklist_default_index !== undefined && picklist_default_index !== '') {
          const parsed = Number(picklist_default_index);
          if (Number.isFinite(parsed) && parsed >= 0 && parsed < n) {
            idx = parsed;
          }
        }
        formWithoutFlag.picklist_values = formWithoutFlag.picklist_values.map((item, i) => {
          const row = {
            key: String(item.key || '').trim(),
            value: String(item.value || '').trim(),
            is_default: idx !== null && i === idx
          };
          const pc = normalizePicklistPathColorForPayload(item.path_color);
          if (pc) row.path_color = pc;
          return row;
        });
      }

      // Normalize lookup_filter - ensure it's in the correct structure { logic: "AND|OR|custom", filters: [...] }
      if (fieldForm.data_type === 'lookup') {
        if (formWithoutFlag.lookup_filter) {
          const normalizeLookupFilters = (rawFilters) =>
            (Array.isArray(rawFilters) ? rawFilters : [])
              .filter(isCompleteListViewFilter)
              .map((f) => {
                const entry = {
                  field: f.field || f.fieldName,
                  operator: f.operator,
                };
                if (isOperatorValueRequired(f.operator)) {
                  entry.value = f.value;
                }
                const valueType = getFilterValueType(f);
                if (valueType && valueType !== FILTER_VALUE_TYPE_STATIC) {
                  entry.valueType = valueType;
                }
                return entry;
              });

          if (
            typeof formWithoutFlag.lookup_filter === 'object' &&
            !Array.isArray(formWithoutFlag.lookup_filter) &&
            formWithoutFlag.lookup_filter.filters
          ) {
            const validFilters = normalizeLookupFilters(formWithoutFlag.lookup_filter.filters);
            if (validFilters.length === 0) {
              formWithoutFlag.lookup_filter = null;
            } else {
              formWithoutFlag.lookup_filter = {
                logic: normalizeStoredLookupFilterLogic(
                  formWithoutFlag.lookup_filter.logic || 'AND'
                ),
                filters: validFilters,
              };
            }
          } else if (Array.isArray(formWithoutFlag.lookup_filter)) {
            const validFilters = normalizeLookupFilters(formWithoutFlag.lookup_filter);
            if (validFilters.length === 0) {
              formWithoutFlag.lookup_filter = null;
            } else {
              formWithoutFlag.lookup_filter = {
                logic: 'AND',
                filters: validFilters,
              };
            }
          }
        }
      }

      // Normalize rollup_criteria - persist { logic, conditions:[{ field, operator, value }] }
      if (fieldForm.data_type === 'rollupsummary') {
        const criteria = formWithoutFlag.rollup_criteria;
        if (criteria && typeof criteria === 'object' && !Array.isArray(criteria)) {
          const rawConditions = Array.isArray(criteria.conditions) ? criteria.conditions : [];
          const validConditions = rawConditions
            .map((c) => {
              const fieldName = c.fieldName || c.field || '';
              const operator = c.operator || '';
              const opNorm = String(operator).toLowerCase().replace(/\s+/g, '_');
              const needsValue = !['is_null', 'is_not_null'].includes(opNorm);
              const entry = { field: fieldName, operator };
              if (needsValue) {
                let value = c.value;
                if ((opNorm === 'in' || opNorm === 'not_in') && typeof value === 'string') {
                  const sep = value.includes('|') ? '|' : ',';
                  value = value.split(sep).map((v) => v.trim()).filter((v) => v !== '');
                }
                entry.value = value;
              }
              return entry;
            })
            .filter((c) => {
              if (!c.field || !c.operator) return false;
              const opNorm = String(c.operator).toLowerCase().replace(/\s+/g, '_');
              if (['is_null', 'is_not_null'].includes(opNorm)) return true;
              if (c.value === undefined || c.value === null || c.value === '') return false;
              if (Array.isArray(c.value) && c.value.length === 0) return false;
              return true;
            });
          formWithoutFlag.rollup_criteria = validConditions.length > 0
            ? { logic: criteria.logic === 'OR' ? 'OR' : 'AND', conditions: validConditions }
            : null;
        } else {
          formWithoutFlag.rollup_criteria = null;
        }
      } else {
        delete formWithoutFlag.rollup_criteria;
      }

      let displayConfigPayload = null;
      if (fieldForm.data_type === 'calculated') {
        displayConfigPayload = calculated_has_return_type
          ? sanitizeCalculatedDisplayConfigForSave(display_config)
          : null;
      } else if (fieldForm.data_type === 'rollupsummary') {
        // For rollup summary, preserve rollupSummary config and strip calculated config
        const baseConfig = stripCalculatedDisplayConfig(display_config);
        const rollupConfig = ensurePlainObject(display_config).rollupSummary;
        if (rollupConfig && Object.keys(rollupConfig).length > 0) {
          displayConfigPayload = {
            ...baseConfig,
            rollupSummary: rollupConfig
          };
        } else if (baseConfig && Object.keys(baseConfig).length > 0) {
          displayConfigPayload = baseConfig;
        }
      } else {
        displayConfigPayload = stripCalculatedDisplayConfig(display_config);
        // For number/currency, persist allow_negative in display_config (validation stored in object_config_fields.display_config)
        const dt = (fieldForm.data_type || '').toLowerCase();
        if (dt === 'number' || dt === 'currency' || dt === 'percent') {
          const allowNegative = fieldForm.display_config && fieldForm.display_config.allow_negative !== undefined
            ? fieldForm.display_config.allow_negative
            : true;
          displayConfigPayload = displayConfigPayload && typeof displayConfigPayload === 'object'
            ? { ...displayConfigPayload, allow_negative: allowNegative }
            : { allow_negative: allowNegative };
        }
      }

      const payload = {
        ...formWithoutFlag
      };
      if (fieldForm.data_type === 'url' || fieldForm.data_type === 'image') {
        payload.max_length = Number(fieldForm.max_length) || URL_IMAGE_FIELD_MAX_LENGTH;
      }
      // Field API name is always stored in lowercase
      if (payload.field_name) {
        payload.field_name = payload.field_name.trim().toLowerCase();
      }
      if (!allowStandardField) {
        payload.is_standard = editingField?.is_standard ? true : false;
      }
      // Custom objects: user fields are never standard; backend appends __x (schema.model.js).
      if (!editingField && !objectIsStandard) {
        payload.is_standard = false;
      }

      const forceReadOnlyTypes = ['calculated', 'autonumber', 'rollupsummary'];
      payload.is_readonly = forceReadOnlyTypes.includes(fieldForm.data_type)
        ? true
        : !!fieldForm.is_readonly;

      if (displayConfigPayload !== null && displayConfigPayload !== undefined) {
        payload.display_config = displayConfigPayload;
      }

      const configPayload = buildFieldConfigPayload(fieldForm.data_type, fieldForm.config);
      if (configPayload) {
        payload.config = configPayload;
      } else if (editingField) {
        payload.config = null;
      }

      let response;
      if (editingField) {
        // Update config only
        response = await SchemaApi.updateField(objectName, editingField.id, payload);
      } else {
        // Create field via schema API
        response = await SchemaApi.addField(objectName, payload);
      }

      if (response?.success || response?.data?.success) {
        showToastMessage(`Field ${editingField ? 'updated' : 'created'} successfully!`);

        if (saveAndNew && !editingField) {
          // Reset form for new field creation
          setFieldForm({
            field_name: '',
            field_label: '',
            data_type: 'text',
            is_required: false,
            is_readonly: false,
            is_unique: false,
            is_reference: false,
            reference_object: '',
            picklist_values: [],
            picklist_default_index: null,
            max_length: '255', // Default for text
            calculated_expression: '',
            precision: '',
            scale: '',
            start_number: 1,
            prefix: '',
            format_mask: '',
            lookup_search_fields: '',
            display_fields: '',
            lookup_filter: [],
            lookup_filter_logic: 'AND',
            rollup_field: '',
            rollup_operation: '',
            rollup_criteria: { logic: 'AND', conditions: [] },
            is_standard: false,
            is_path_field: false,
            display_config: {},
            config: {},
            calculated_has_return_type: false
          });
          setModalError('');
          setModalTouchedSave(false);
          setTouched({});
          setBulkPicklistValues('');
          setBulkPicklistPathColor('');
          // Keep modal open for new field
        } else {
          setShowFieldModal(false);
        }

        fetchFields();
      } else {
        const msg = response?.message || response?.data?.message || 'Failed to save field';
        if (showFieldModal) setModalError(msg); else setError(msg);
      }
    } catch (error) {
      console.error('Error saving field:', error);
      const msg = 'Failed to save field: ' + (error.message || 'Unknown error');
      if (showFieldModal) setModalError(msg); else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!field || !objectName || deleteFlowActiveRef.current) {
      return;
    }

    deleteFlowActiveRef.current = true;
    setDeleteFlowBusy(true);
    setFieldToDelete(field);
    setDeleteModalError(null);
    setDeleteModalBlockedReferences(null);
    setDeleteModalChecking(true);
    setShowDeleteModal(true);

    try {
      const response = await ObjectConfigApi.getFieldReferences(objectName, field.id);

      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to check field references');
      }

      const referenceData = response?.data || response;
      if (referenceData?.hasReferences) {
        setDeleteModalBlockedReferences(referenceData.references || {});
        return;
      }
    } catch (error) {
      console.error('Failed to check field references:', error);
      setDeleteModalError(error.message || 'Failed to check field references');
    } finally {
      setDeleteModalChecking(false);
      deleteFlowActiveRef.current = false;
      setDeleteFlowBusy(false);
    }
  };

  const resetDeleteModal = () => {
    setShowDeleteModal(false);
    setFieldToDelete(null);
    setDeleteModalError(null);
    setDeleteModalBlockedReferences(null);
    setDeleteModalChecking(false);
    setDeleteModalDeleting(false);
    deleteFlowActiveRef.current = false;
    setDeleteFlowBusy(false);
  };

  const confirmDeleteField = async () => {
    if (!fieldToDelete || deleteModalDeleting) return;

    setDeleteModalError(null);
    setDeleteModalDeleting(true);

    try {
      const response = await ObjectConfigApi.trashField(objectName, fieldToDelete.id);

      if (response?.success) {
        setFields(fields.filter(f => f.id !== fieldToDelete.id));
        showToastMessage('Field moved to trash successfully!');
        fetchTrashedFields();
        resetDeleteModal();
      } else if (response?.details?.hasReferences) {
        setDeleteModalBlockedReferences(response.details.references || {});
      } else {
        setDeleteModalError(response?.message || 'Failed to move field to trash');
      }
    } catch (error) {
      console.error('Error trashing field:', error);
      setDeleteModalError(error.message || 'Failed to move field to trash');
    } finally {
      setDeleteModalDeleting(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFields(items);
    // Here you would typically save the new order to the backend
  };

  const getFieldTypeIcon = (dataType) => {
    const icons = {
      text: '📝',
      number: '🔢',
      date: '📅',
      datetime: '🕒',
      boolean: '☑️',
      picklist: '📋',
      lookup: '🔍',
      textarea: '📄',
      email: '📧',
      phone: '📞',
      url: '🔗',
      currency: '💰',
      percent: '📊',
      calculated: '⚡'
    };
    return icons[dataType] || '📝';
  };

  const getFieldTypeBadge = (dataType) => {
    const variants = {
      text: 'primary',
      number: 'info',
      date: 'warning',
      datetime: 'warning',
      boolean: 'success',
      picklist: 'secondary',
      lookup: 'dark',
      textarea: 'primary',
      email: 'info',
      phone: 'info',
      url: 'info',
      currency: 'success',
      percent: 'warning',
      calculated: 'danger'
    };
    return <Badge bg={variants[dataType] || 'secondary'}>{dataType}</Badge>;
  };

  // Formula validation functions
  const validateFormulaSyntax = (expression) => {
    const errors = [];

    // Check for balanced brackets
    const bracketPairs = [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: '{', close: '}' }
    ];

    bracketPairs.forEach(({ open, close }) => {
      let openCount = 0;
      for (let char of expression) {
        if (char === open) openCount++;
        if (char === close) openCount--;
        if (openCount < 0) {
          errors.push(`Unmatched closing bracket '${close}'`);
          break;
        }
      }
      if (openCount > 0) {
        errors.push(`Unmatched opening bracket '${open}' (${openCount} missing closing bracket${openCount > 1 ? 's' : ''})`);
      }
    });

    // Check for proper string quotes
    const singleQuotes = (expression.match(/'/g) || []).length;
    const doubleQuotes = (expression.match(/"/g) || []).length;

    if (singleQuotes % 2 !== 0) {
      errors.push('Unmatched single quotes');
    }
    if (doubleQuotes % 2 !== 0) {
      errors.push('Unmatched double quotes');
    }

    // Check for common SQL function patterns
    const sqlFunctions = ['CONCAT', 'SUBSTRING', 'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'COALESCE', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END'];
    const upperExpression = expression.toUpperCase();

    // Check for incomplete CASE statements
    const caseMatches = upperExpression.match(/\bCASE\b/g);
    const endMatches = upperExpression.match(/\bEND\b/g);
    if ((caseMatches?.length || 0) !== (endMatches?.length || 0)) {
      errors.push('Incomplete CASE statement - CASE and END must match');
    }

    // Check for incomplete WHEN statements without THEN
    const whenMatches = upperExpression.match(/\bWHEN\b/g);
    const thenMatches = upperExpression.match(/\bTHEN\b/g);
    if ((whenMatches?.length || 0) !== (thenMatches?.length || 0)) {
      errors.push('Incomplete WHEN statement - every WHEN must have a THEN');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const extractFieldNames = (expression) => {
    // Extract field names from the expression
    // Remove function calls first, then extract identifiers
    let cleanedExpression = expression;

    // Remove function calls like CONCAT(...), TRIM(...), etc.
    // This regex matches function names followed by parentheses
    cleanedExpression = cleanedExpression.replace(/\b[A-Z_][A-Z0-9_]*\s*\(/gi, '(');

    // Remove string literals (single and double quotes)
    cleanedExpression = cleanedExpression.replace(/('([^'\\]|\\.)*'|"([^"\\]|\\.)*")/g, '');

    // Extract field names using regex - including parent.field syntax
    const fieldPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)\b/g;
    const matches = [];
    let match;
    while ((match = fieldPattern.exec(cleanedExpression)) !== null) {
      const value = match[1];
      const startIndex = match.index;
      const preceding = cleanedExpression.slice(Math.max(0, startIndex - 2), startIndex);
      if (preceding === '::') {
        continue;
      }
      matches.push(value);
    }

    // Filter out SQL keywords and operators
    const sqlKeywords = [
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'AND', 'OR', 'NOT', 'IS', 'NULL',
      'TRUE', 'FALSE', 'SELECT', 'FROM', 'WHERE', 'GROUP', 'BY', 'HAVING',
      'ORDER', 'LIMIT', 'OFFSET', 'AS', 'IN', 'LIKE', 'BETWEEN', 'EXISTS',
      'NULLIF', 'IFNULL', 'CAST', 'EXTRACT', 'DATE', 'TIME', 'TIMESTAMP'
    ];

    return matches.filter(field => !sqlKeywords.includes(field.toUpperCase()));
  };

  // Preload all parent object fields when component loads
  const preloadParentObjectFields = async () => {
    console.log('🔍 Preloading parent object fields for:', objectName);
    if (!objectName) return;

    try {
      // Get current object fields to identify lookup fields
      const ObjectConfigApi = require('../../api/ObjectConfigApi').default;
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      if (response?.data?.success) {
        const fields = response.data.data?.fields || response.data.data?.data?.fields || [];

        // Find all lookup fields to identify parent objects
        const lookupFields = fields.filter(f => f.data_type === 'lookup' && f.reference_object);
        const allParentObjects = lookupFields.map(f => f.reference_object);
        const uniqueParentObjects = [...new Set(allParentObjects)];

        console.log(`🔍 All lookup fields:`, lookupFields.map(f => ({ field: f.field_name, reference: f.reference_object })));
        console.log(`🔍 All parent objects (with duplicates):`, allParentObjects);
        console.log(`🔍 Unique parent objects (deduplicated):`, uniqueParentObjects);
        console.log(`🔍 Preloading fields for ${uniqueParentObjects.length} unique parent objects`);

        if (uniqueParentObjects.length === 0) {
          console.log('🔍 No parent objects to preload');
          return;
        }

        // Fetch all parent object configurations in a single request using DataApi
        const dataApi = new DataApi('objectconfig');
        const configsResponse = await dataApi.post('/fetchconfigs', {
          objectNames: uniqueParentObjects
        });

        if (configsResponse?.data?.success) {
          const configs = configsResponse.data.data || [];

          // Update cache with all parent object fields (excluding calculated fields)
          const newCache = {};
          configs.forEach(config => {
            if (config.fields && Array.isArray(config.fields)) {
              // Store full field objects with data_type to filter out calculated fields
              newCache[config.object_name] = config.fields.map(f => ({
                field_name: f.field_name,
                data_type: f.data_type
              }));
            }
          });

          setParentObjectFieldsCache(newCache);
          console.log(`🔍 Preloaded parent object fields cache in single request (excluding calculated fields):`, newCache);

          // Log summary of excluded calculated fields
          configs.forEach(config => {
            const calculatedFields = config.fields?.filter(f => f.data_type === 'calculated') || [];
            if (calculatedFields.length > 0) {
              console.log(`🔍 Excluded ${calculatedFields.length} calculated fields from ${config.object_name}:`, calculatedFields.map(f => f.field_name));
            }
          });
        } else {
          console.error('Failed to fetch parent object configurations:', configsResponse?.data?.message);
        }
      }
    } catch (error) {
      console.error('Error preloading parent object fields:', error);
    }
  };

  // Get fields of parent objects from cache (no database query)
  // Excludes calculated fields to prevent circular dependencies
  const getParentObjectFields = (parentObjectName) => {
    const fields = parentObjectFieldsCache[parentObjectName] || [];
    // Filter out calculated fields and return only field names
    return fields.filter(field => field.data_type !== 'calculated').map(field => field.field_name);
  };

  const validateFormula = async (expression = null) => {
    const rawExpressionInput = expression ?? fieldForm.calculated_expression;
    if (!rawExpressionInput || !objectName) return;

    setValidatingFormula(true);
    setFormulaValidationResult(null);

    try {
      const originalExpression = rawExpressionInput.trim();
      const expression = stripFormulaPlaceholders(originalExpression);
      const expressionWithoutPlaceholders = originalExpression.replace(/\{\{[^}]*\}\}/g, ' ');
      const expressionWithoutStrings = expressionWithoutPlaceholders
        .replace(/'([^'\\]|\\.)*'/g, ' ')
        .replace(/"([^"\\]|\\.)*"/g, ' ');

      // Step 1: Syntax validation
      const syntaxValidation = validateFormulaSyntax(expression);
      if (!syntaxValidation.isValid) {
        setFormulaValidationResult({
          success: false,
          message: 'Syntax validation failed',
          details: syntaxValidation.errors.join(', ')
        });
        return;
      }

      // Step 2: Extract field names and check if they exist
      const fieldNames = extractFieldNames(expression);
      console.log('🔍 Original expression (user input):', originalExpression);
      console.log('🔍 Normalized expression (placeholders removed):', expression);
      console.log('🔍 Extracted field names:', fieldNames);

      // Get current fields to validate field names
      const ObjectConfigApi = require('../../api/ObjectConfigApi').default;
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const fields = response?.data?.fields || response?.data?.data?.fields || [];
      const availableFieldNames = fields.map(f => f.field_name);

      // Separate direct fields and parent fields
      const directFields = fieldNames.filter(fieldName => !fieldName.includes('.'));
      const parentFields = fieldNames.filter(fieldName => fieldName.includes('.'));

      console.log('🔍 Direct fields:', directFields);
      console.log('🔍 Parent fields:', parentFields);

      // Ensure all field references use {{ }} placeholders in the original expression
      const missingPlaceholderDirect = directFields.filter(fieldName => {
        const pattern = new RegExp(`(^|[^A-Za-z0-9_])${escapeRegExp(fieldName)}([^A-Za-z0-9_]|$)`, 'g');
        let match;
        while ((match = pattern.exec(expressionWithoutStrings)) !== null) {
          const leading = match[1] || '';
          const startIndex = match.index + leading.length;
          let cursor = startIndex - 1;
          while (cursor >= 0 && /\s/.test(expressionWithoutStrings[cursor])) {
            cursor--;
          }
          const prevChar = cursor >= 0 ? expressionWithoutStrings[cursor] : '';
          const prevPrevChar = cursor - 1 >= 0 ? expressionWithoutStrings[cursor - 1] : '';
          if (prevPrevChar === ':' && prevChar === ':') {
            continue;
          }
          return true;
        }
        return false;
      });

      const missingPlaceholderParent = parentFields.filter(fieldRef =>
        expressionWithoutStrings.includes(fieldRef)
      );

      const missingPlaceholderFields = Array.from(new Set([
        ...missingPlaceholderDirect,
        ...missingPlaceholderParent
      ]));

      if (missingPlaceholderFields.length > 0) {
        setFormulaValidationResult({
          success: false,
          message: 'Wrap field names in double curly braces',
          details: `Add {{ }} around: ${missingPlaceholderFields.join(', ')}`
        });
        return;
      }

      // Validate direct fields
      const invalidDirectFields = directFields.filter(fieldName => !availableFieldNames.includes(fieldName));

      // Validate parent fields with batch fetching
      const invalidParentFields = [];

      if (parentFields.length > 0) {
        // Group parent fields by reference object to avoid duplicate API calls
        const parentFieldsByObject = {};
        const parentFieldMapping = {};

        for (const parentField of parentFields) {
          const [parentObjectName, childFieldName] = parentField.split('.');

          // Check if parent object exists as a lookup field
          const parentLookupField = fields.find(f => f.field_name === parentObjectName && f.data_type === 'lookup');
          if (!parentLookupField) {
            invalidParentFields.push(`${parentObjectName} (not a lookup field)`);
            continue;
          }

          const referenceObject = parentLookupField.reference_object;

          // Group by reference object
          if (!parentFieldsByObject[referenceObject]) {
            parentFieldsByObject[referenceObject] = [];
          }
          parentFieldsByObject[referenceObject].push({
            parentField,
            parentObjectName,
            childFieldName
          });

          // Store mapping for validation
          parentFieldMapping[parentField] = {
            referenceObject,
            childFieldName
          };
        }

        // Validate parent fields using cached data (no database queries)
        console.log('🔍 Parent objects to validate:', Object.keys(parentFieldsByObject));
        console.log('🔍 Current parent object fields cache:', parentObjectFieldsCache);

        for (const referenceObject of Object.keys(parentFieldsByObject)) {
          const parentObjectFields = getParentObjectFields(referenceObject);

          console.log(`🔍 Validating ${referenceObject}:`, parentObjectFields);

          // Check if we have cached fields for this parent object
          if (parentObjectFields.length === 0) {
            console.warn(`🔍 No cached fields found for parent object: ${referenceObject}`);
            console.warn(`🔍 Cache contents:`, parentObjectFieldsCache);

            // Fallback: Try to load the parent object fields on demand
            console.log(`🔍 Attempting to load ${referenceObject} fields on demand...`);
            try {
              const dataApi = new DataApi('objectconfig');
              const fallbackResponse = await dataApi.post('/fetchconfigs', {
                objectNames: [referenceObject]
              });

              if (fallbackResponse?.data?.success && fallbackResponse.data.data?.length > 0) {
                const config = fallbackResponse.data.data[0];
                if (config.fields && Array.isArray(config.fields)) {
                  // Filter out calculated fields and store full field objects
                  const fallbackFields = config.fields.map(f => ({
                    field_name: f.field_name,
                    data_type: f.data_type
                  }));

                  // Update cache with the loaded fields (excluding calculated fields)
                  setParentObjectFieldsCache(prev => ({
                    ...prev,
                    [referenceObject]: fallbackFields
                  }));

                  const availableFields = fallbackFields.filter(f => f.data_type !== 'calculated').map(f => f.field_name);
                  const excludedCalculatedFields = fallbackFields.filter(f => f.data_type === 'calculated').map(f => f.field_name);
                  console.log(`🔍 Successfully loaded ${referenceObject} fields on demand (excluding calculated):`, availableFields);
                  if (excludedCalculatedFields.length > 0) {
                    console.log(`🔍 Excluded ${excludedCalculatedFields.length} calculated fields from ${referenceObject}:`, excludedCalculatedFields);
                  }

                  // Re-validate with the loaded fields (excluding calculated fields)
                  const fieldInfo = parentFieldsByObject[referenceObject][0];
                  if (!availableFields.includes(fieldInfo.childFieldName)) {
                    invalidParentFields.push(`${fieldInfo.parentField} (${fieldInfo.childFieldName} not found in ${referenceObject})`);
                  }
                  continue;
                }
              }
            } catch (fallbackError) {
              console.error(`🔍 Failed to load ${referenceObject} fields on demand:`, fallbackError);
            }

            invalidParentFields.push(`${referenceObject} (parent object fields not loaded)`);
            continue;
          }

          // Validate all fields for this reference object
          for (const fieldInfo of parentFieldsByObject[referenceObject]) {
            if (!parentObjectFields.includes(fieldInfo.childFieldName)) {
              invalidParentFields.push(`${fieldInfo.parentField} (${fieldInfo.childFieldName} not found in ${referenceObject})`);
            }
          }
        }
      }

      // Combine all invalid fields
      const invalidFields = [...invalidDirectFields, ...invalidParentFields];

      if (invalidFields.length > 0) {
        setFormulaValidationResult({
          success: false,
          message: 'Invalid field names found',
          details: `Fields not found: ${invalidFields.join(', ')}`
        });
        return;
      }

      // Step 3: Runtime validation - test with actual PostgreSQL execution
      try {
        // Create a test API call to validate the formula
        //const ObjectConfigApi = require('../../api/ObjectConfigApi').default;
        const dataApi = new DataApi('objectconfig');

        // Send the formula to backend for validation
        const validationPayload = {
          objectName,
          expression
        };

        if (fieldForm.data_type === 'calculated' && fieldForm.calculated_has_return_type) {
          validationPayload.displayConfig = sanitizeCalculatedDisplayConfigForSave(fieldForm.display_config);
        }

        const validationResponse = await dataApi.post('/validate-calculated-formula', validationPayload);

        if (validationResponse?.data?.success) {
          const directFieldCount = directFields.length;
          const parentFieldCount = parentFields.length;
          let detailsMessage = `Expression syntax is valid, references ${directFieldCount} direct field${directFieldCount !== 1 ? 's' : ''}`;
          if (directFields.length > 0) {
            detailsMessage += `: ${directFields.join(', ')}`;
          }
          if (parentFieldCount > 0) {
            detailsMessage += ` and ${parentFieldCount} parent field${parentFieldCount !== 1 ? 's' : ''}: ${parentFields.join(', ')}`;
          }
          detailsMessage += ', and PostgreSQL execution successful';

          setFormulaValidationResult({
            success: true,
            message: 'Formula validation passed',
            details: detailsMessage,
          });
        } else {
          setFormulaValidationResult({
            success: false,
            message: 'PostgreSQL execution failed',
            details: validationResponse?.data?.message || 'Formula could not be executed by PostgreSQL',
          });
        }
      } catch (validationError) {
        console.error('Formula validation error:', validationError);
        const errorMessage = validationError?.response?.data?.message || validationError.message || 'An unexpected error occurred during PostgreSQL validation';
        setFormulaValidationResult({
          success: false,
          message: 'Validation failed',
          details: errorMessage,
        });
      }

    } catch (error) {
      console.error('Formula validation error:', error);
      setFormulaValidationResult({
        success: false,
        message: 'Validation failed',
        details: error.message || 'An unexpected error occurred during validation'
      });
    } finally {
      setValidatingFormula(false);
    }
  };

  const handleCloseFieldModal = () => {
    setShowFieldModal(false);
    // Clear any pending auto-validation
    if (autoValidationTimeout) {
      clearTimeout(autoValidationTimeout);
      setAutoValidationTimeout(null);
    }
    setFormulaValidationResult(null);
  };

  const handleAddPicklistRowsBulk = () => {
    const lines = String(bulkPicklistValues || '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (lines.length === 0) {
      setModalError('Please enter one or more values to add');
      return;
    }

    const rows = lines
      .map((line) => parsePicklistEditorBulkLine(line, bulkPicklistPathColor))
      .filter(Boolean);
    if (rows.length === 0) {
      setModalError('No valid values found to add');
      return;
    }

    setFieldForm((prev) => ({
      ...prev,
      picklist_values: [...(prev.picklist_values || []), ...rows]
    }));
    setTouched((prev) => ({ ...prev, picklist_values: true }));
    setModalError('');
    setBulkPicklistValues('');
  };

  const renderFieldModal = () => (
    <Modal show={showFieldModal} onHide={handleCloseFieldModal} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingField ? (
            (editingField.data_type === 'lookup' || editingField.data_type === 'rollupsummary') ?
              (editingField.data_type === 'lookup' ? 'Edit Lookup Field (Label, Search Fields, Display Fields & Options)' : 'Edit Rollup Summary Field (Label, Rollup Configuration & Options)') :
              'Edit Field'
          ) : 'Create New Field'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {modalError && (
          <Alert variant="danger" onClose={() => setModalError('')} dismissible>
            {modalError}
          </Alert>
        )}
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Field Label *</Form.Label>
                <Form.Control
                  type="text"
                  value={fieldForm.field_label}
                  readOnly={isEditingStandardField && !canEditLabel}
                  className={(isEditingStandardField && !canEditLabel) ? 'bg-light' : ''}
                  onChange={(e) => {
                    if (isEditingStandardField && !canEditLabel) return;
                    const label = e.target.value;
                    setFieldForm((prev) => ({
                      ...prev,
                      field_label: label,
                      ...(editingField ? {} : { field_name: generateApiName(label) })
                    }));
                  }}
                  placeholder="Enter field label"
                  onBlur={() => setTouched(prev => ({ ...prev, field_label: true }))}
                />
                {(touched.field_label || modalTouchedSave) && !fieldForm.field_label && (
                  <Form.Text className="text-danger">Field label is required</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Field Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={fieldForm.field_name}
                  onChange={(e) => setFieldForm({ ...fieldForm, field_name: (e.target.value || '').toLowerCase() })}
                  placeholder="Auto-generated from label (editable)"
                  onBlur={() => setTouched(prev => ({ ...prev, field_name: true }))}
                  readOnly={editingField !== null}
                  className={editingField !== null ? 'bg-light' : ''}
                />
                {(touched.field_name || modalTouchedSave) && !fieldForm.field_name && (
                  <Form.Text className="text-danger">Field name is required</Form.Text>
                )}
                {!editingField && fieldForm.field_name && fieldForm.field_name.includes('__x') && (
                  <Form.Text className="text-danger">Field name must not contain __x; it is added automatically for custom fields.</Form.Text>
                )}
                <Form.Text className="text-muted">
                  {editingField ? 'Field name cannot be changed after creation.' : 'Lowercase, use letters, numbers, and underscore only. Auto-generated from label. Do not include __x.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data Type</Form.Label>
                <Form.Select
                  value={fieldForm.data_type}
                  onChange={(e) => {
                    const dt = e.target.value;
                    let updatedForm;

                    // If changing away from lookup or rollupsummary, reset lookup-specific fields (only for new fields)
                    if (dt !== 'lookup' && dt !== 'rollupsummary' && (fieldForm.data_type === 'lookup' || fieldForm.data_type === 'rollupsummary') && !editingField) {
                      const nowPickish = dt === 'picklist' || dt === 'multipicklist';
                      updatedForm = {
                        ...fieldForm,
                        data_type: dt,
                        reference_object: '',
                        lookup_search_fields: [],
                        display_fields: [],
                        rollup_field: '',
                        rollup_operation: '',
                        rollup_criteria: { logic: 'AND', conditions: [] },
                        is_path_field: nowPickish ? fieldForm.is_path_field : false,
                        ...(nowPickish
                          ? {
                            picklist_values: [],
                            picklist_default_index: null
                          }
                          : {})
                      };
                    } else {
                      // Set default max_length for text fields
                      let newMaxLength = fieldForm.max_length;
                      if (dt === 'text' && !fieldForm.max_length) {
                        newMaxLength = '255'; // Default for text
                      } else if (dt === 'textarea' && !fieldForm.max_length) {
                        newMaxLength = '255'; // Default for textarea
                      } else if (dt === 'richtext' && !fieldForm.max_length) {
                        newMaxLength = '30000'; // Default for richtext
                      }

                      // Set default max_length for password fields
                      if (dt === 'password' && !fieldForm.max_length) {
                        newMaxLength = '255'; // Default for password
                      }
                      if ((dt === 'picklist' || dt === 'multipicklist') && !fieldForm.max_length) {
                        newMaxLength = String(PICKLIST_FIELD_MAX_LENGTH);
                      }
                      if ((dt === 'url' || dt === 'image') && !fieldForm.max_length) {
                        newMaxLength = String(URL_IMAGE_FIELD_MAX_LENGTH);
                      }

                      const wasPickish =
                        fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist';
                      const nowPickish = dt === 'picklist' || dt === 'multipicklist';
                      let newPrecision = fieldForm.precision;
                      let newScale = fieldForm.scale;
                      if (!editingField && (dt === 'currency' || dt === 'percent')) {
                        if (!newPrecision) newPrecision = 18;
                        if (newScale === '' || newScale === null || newScale === undefined) newScale = 2;
                      }
                      updatedForm = {
                        ...fieldForm,
                        data_type: dt,
                        precision: newPrecision,
                        scale: newScale,
                        max_length: newMaxLength,
                        // Reset is_path_field if changing away from picklist
                        is_path_field: nowPickish ? fieldForm.is_path_field : false,
                        ...(nowPickish && !wasPickish && !editingField
                          ? {
                            picklist_values: [],
                            picklist_default_index: null
                          }
                          : {})
                      };
                    }

                    if (dt === 'calculated' || dt === 'autonumber') {
                      updatedForm.is_readonly = true;
                    } else if (!editingField) {
                      updatedForm.is_readonly = false;
                    }

                    if (dt === 'autonumber') {
                      updatedForm.is_unique = true;
                    } else if (dt !== 'text' && dt !== 'email') {
                      updatedForm.is_unique = false;
                    }

                    if (dt === 'calculated') {
                      const { displayConfig: nextConfig, hasReturnType } = buildCalculatedDisplayConfigForState(updatedForm.display_config);
                      updatedForm.display_config = nextConfig;
                      updatedForm.calculated_has_return_type = hasReturnType || !editingField;
                    } else {
                      const stripped = stripCalculatedDisplayConfig(updatedForm.display_config);
                      updatedForm.display_config = stripped === null ? null : stripped;
                      updatedForm.calculated_has_return_type = false;
                    }

                    setFieldForm(updatedForm);

                    // Show warning for numeric to text conversion
                    if (editingField && ['currency', 'percent', 'number', 'decimal', 'float'].includes(fieldForm.data_type.toLowerCase()) &&
                      ['text', 'textarea', 'richtext'].includes(dt.toLowerCase())) {
                      const warningMsg = `⚠️ Warning: Changing from ${fieldForm.data_type} to ${dt} will prevent you from converting back to numeric types. This action cannot be undone.`;
                      setModalError(warningMsg);
                    } else if (editingField && ['currency', 'percent', 'number', 'decimal', 'float'].includes(fieldForm.data_type.toLowerCase()) &&
                      !['text', 'textarea', 'richtext'].includes(dt.toLowerCase())) {
                      setModalError(''); // Clear warning when selecting non-text types
                    } else if (!editingField) {
                      setModalError(''); // Clear warning for new fields
                    }
                  }}
                  disabled={(isEditingStandardField && !canEditStandard) || (editingField && (editingField.data_type === 'lookup' || fieldForm.data_type === 'lookup' || editingField.data_type === 'rollupsummary' || fieldForm.data_type === 'rollupsummary'))}
                  className={((isEditingStandardField && !canEditStandard) || (editingField && (editingField.data_type === 'lookup' || fieldForm.data_type === 'lookup' || editingField.data_type === 'rollupsummary' || fieldForm.data_type === 'rollupsummary'))) ? 'bg-light' : ''}
                >
                  {(() => {
                    // Define allowed data type changes for editing
                    const allowedChanges = {
                      'currency': ['text', 'textarea', 'richtext', 'number', 'percent'],
                      'percent': ['text', 'textarea', 'richtext', 'number', 'currency'],
                      'number': ['text', 'textarea', 'richtext', 'currency', 'percent'],
                      'decimal': ['text', 'textarea', 'richtext', 'currency', 'percent'],
                      'float': ['text', 'textarea', 'richtext', 'currency', 'percent'],
                      'text': ['textarea', 'richtext', 'email', 'phone', 'password'],
                      'textarea': ['text', 'richtext'],
                      'richtext': ['text', 'textarea'],
                      'email': ['text', 'textarea'],
                      'phone': ['text', 'textarea'],
                      'password': ['text', 'textarea'],
                      'url': ['text', 'textarea'],
                      'date': ['datetime'],
                      'datetime': ['date'],
                      'picklist': ['multipicklist'],
                      'multipicklist': ['picklist'],
                      'checkbox': ['text', 'textarea'],
                      'boolean': ['text', 'textarea']
                    };

                    // If editing, show only allowed data types + current type (sorted A–Z)
                    if (editingField) {
                      const currentType = editingField.data_type.toLowerCase();
                      const allowedTargetTypes = allowedChanges[currentType] || [];

                      // Include current type and allowed changes
                      const validTypes = [currentType, ...allowedTargetTypes];

                      return FIELD_DATA_TYPE_OPTIONS.filter(type =>
                        validTypes.includes(type.value)
                      ).map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} {type.value === currentType ? '(Current)' : ''}
                        </option>
                      ));
                    }

                    // For new fields, show all data types
                    return FIELD_DATA_TYPE_OPTIONS.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ));
                  })()}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              {(fieldForm.data_type === 'lookup' || fieldForm.data_type === 'rollupsummary') && (
                <Form.Group className="mb-3">
                  <Form.Label>Reference Object</Form.Label>
                  <Form.Select
                    value={fieldForm.reference_object}
                    onChange={(e) => {
                      const newObj = e.target.value;
                      // Reset selected lookup fields when object changes (only for new fields)
                      if (!editingField) {
                        setFieldForm({
                          ...fieldForm,
                          reference_object: newObj,
                          lookup_search_fields: [],
                          display_fields: []
                        });
                      } else {
                        setFieldForm({
                          ...fieldForm,
                          reference_object: newObj
                        });
                      }
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, reference_object: true }))}
                    isInvalid={(touched.reference_object || modalTouchedSave) && !fieldForm.reference_object}
                    disabled={!canEditFieldProperties || (editingField && (editingField.data_type === 'lookup' || fieldForm.data_type === 'lookup' || editingField.data_type === 'rollupsummary' || fieldForm.data_type === 'rollupsummary'))}
                    className={(!canEditFieldProperties || (editingField && (editingField.data_type === 'lookup' || fieldForm.data_type === 'lookup' || editingField.data_type === 'rollupsummary' || fieldForm.data_type === 'rollupsummary'))) ? 'bg-light' : ''}
                  >
                    <option value="">-- Select Object --</option>
                    {objectOptions.map(o => (
                      <option key={o.name} value={o.name}>{o.label}</option>
                    ))}
                  </Form.Select>
                  {(touched.reference_object || modalTouchedSave) && !fieldForm.reference_object && (
                    <Form.Text className="text-danger">Reference object is required</Form.Text>
                  )}
                  {editingField && (editingField.data_type === 'lookup' || fieldForm.data_type === 'lookup' || editingField.data_type === 'rollupsummary' || fieldForm.data_type === 'rollupsummary') && (
                    <Form.Text className="text-muted">Reference object cannot be changed for existing lookup or rollup summary fields.</Form.Text>
                  )}
                </Form.Group>
              )}
              {(fieldForm.data_type === 'text' || fieldForm.data_type === 'textarea' || fieldForm.data_type === 'richtext' || fieldForm.data_type === 'password' || fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && fieldForm.data_type !== 'calculated' && (
                <Form.Group className="mb-3">
                  <Form.Label>Max Length *</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={
                      fieldForm.data_type === 'text' ? 255
                        : fieldForm.data_type === 'password' ? 255
                          : fieldForm.data_type === 'textarea' ? 150000
                            : fieldForm.data_type === 'richtext' ? 150000
                              : fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist'
                                ? PICKLIST_FIELD_MAX_LENGTH
                                : undefined
                    }
                    value={fieldForm.max_length}
                    readOnly={!canEditFieldProperties}
                    onChange={(e) => {
                      if (!canEditFieldProperties) return;
                      const raw = e.target.value;
                      const num = Number(raw);
                      if (fieldForm.data_type === 'text' && num > 255) {
                        setFieldForm({ ...fieldForm, max_length: 255 });
                      } else if (fieldForm.data_type === 'password' && num > 255) {
                        setFieldForm({ ...fieldForm, max_length: 255 });
                      } else if (fieldForm.data_type === 'textarea' && num > 150000) {
                        setFieldForm({ ...fieldForm, max_length: 150000 });
                      } else if (fieldForm.data_type === 'richtext' && num > 150000) {
                        setFieldForm({ ...fieldForm, max_length: 150000 });
                      } else if (
                        (fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') &&
                        num > PICKLIST_FIELD_MAX_LENGTH
                      ) {
                        setFieldForm({ ...fieldForm, max_length: PICKLIST_FIELD_MAX_LENGTH });
                      } else {
                        setFieldForm({ ...fieldForm, max_length: raw });
                      }
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, max_length: true }))}
                    className={!canEditFieldProperties ? 'bg-light' : ''}
                  />
                  {(touched.max_length || modalTouchedSave) && (!fieldForm.max_length || Number(fieldForm.max_length) <= 0) && (
                    <Form.Text className="text-danger d-block">Max length is required</Form.Text>
                  )}
                  {fieldForm.data_type === 'text' && (touched.max_length || modalTouchedSave) && Number(fieldForm.max_length) > 255 && (
                    <Form.Text className="text-danger d-block">Max length cannot exceed 255 for Text</Form.Text>
                  )}
                  {fieldForm.data_type === 'password' && (touched.max_length || modalTouchedSave) && Number(fieldForm.max_length) > 255 && (
                    <Form.Text className="text-danger d-block">Max length cannot exceed 255 for Password</Form.Text>
                  )}
                  {fieldForm.data_type === 'textarea' && (touched.max_length || modalTouchedSave) && Number(fieldForm.max_length) > 150000 && (
                    <Form.Text className="text-danger d-block">Max length cannot exceed 150,000 for Text Area</Form.Text>
                  )}
                  {fieldForm.data_type === 'richtext' && (touched.max_length || modalTouchedSave) && Number(fieldForm.max_length) > 150000 && (
                    <Form.Text className="text-danger d-block">Max length cannot exceed 150,000 for Rich Text</Form.Text>
                  )}
                  {(fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') &&
                    (touched.max_length || modalTouchedSave) &&
                    Number(fieldForm.max_length) > PICKLIST_FIELD_MAX_LENGTH && (
                    <Form.Text className="text-danger d-block">
                      Max length cannot exceed {PICKLIST_FIELD_MAX_LENGTH.toLocaleString()} for Picklist
                    </Form.Text>
                  )}
                  {(fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && (
                    <Form.Text className="text-muted">
                      Stored picklist values use TEXT (up to {PICKLIST_FIELD_MAX_LENGTH.toLocaleString()} characters). Default is {PICKLIST_FIELD_MAX_LENGTH.toLocaleString()}.
                    </Form.Text>
                  )}
                  {fieldForm.data_type === 'textarea' && (
                    <Form.Text className="text-muted">
                      Text Area fields support up to 150,000 characters. Default is 255 characters.
                    </Form.Text>
                  )}
                  {fieldForm.data_type === 'richtext' && (
                    <Form.Text className="text-muted">
                      Rich Text fields support up to 150,000 characters. Default is 30,000 characters.
                    </Form.Text>
                  )}
                </Form.Group>
              )}
              {fieldForm.data_type === 'number' && fieldForm.data_type !== 'calculated' && (
                <div className="d-flex gap-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Total Length (≤ 18) *</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={18}
                      value={fieldForm.precision}
                      readOnly={!canEditFieldProperties}
                      className={!canEditFieldProperties ? 'bg-light' : ''}
                      onChange={(e) => {
                        if (!canEditFieldProperties) return;
                        const val = Math.min(18, Math.max(1, Number(e.target.value || 0)));
                        setFieldForm({ ...fieldForm, precision: val });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, precision: true }))}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Decimal Places (≤ {MAX_NUMERIC_DECIMAL_PLACES}) *</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={fieldForm.precision
                        ? Math.min(Number(fieldForm.precision), MAX_NUMERIC_DECIMAL_PLACES)
                        : MAX_NUMERIC_DECIMAL_PLACES}
                      value={fieldForm.scale}
                      readOnly={!canEditFieldProperties}
                      className={!canEditFieldProperties ? 'bg-light' : ''}
                      onChange={(e) => {
                        if (!canEditFieldProperties) return;
                        const p = Number(fieldForm.precision || 0);
                        let s = Number(e.target.value || 0);

                        // ✅ PostgreSQL: scale cannot exceed precision
                        if (p && s > p) s = p;

                        const maxScale = p
                          ? Math.min(p, MAX_NUMERIC_DECIMAL_PLACES)
                          : MAX_NUMERIC_DECIMAL_PLACES;
                        if (s > maxScale) s = maxScale;

                        // ✅ Ensure non-negative
                        if (s < 0) s = 0;

                        setFieldForm({ ...fieldForm, scale: s });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, scale: true }))}
                    />
                  </Form.Group>
                </div>
              )}
              {(fieldForm.data_type === 'number' || fieldForm.data_type === 'currency' || fieldForm.data_type === 'percent') && fieldForm.data_type !== 'calculated' && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="field-allow-negative"
                    label="Allow negative values"
                    checked={getDisplayConfigValue('allow_negative', true)}
                    onChange={(e) => setDisplayConfigValue('allow_negative', e.target.checked)}
                    disabled={!canEditFieldProperties}
                    className={!canEditFieldProperties ? 'text-muted' : ''}
                  />
                  <Form.Text className="text-muted">
                    When unchecked, only zero or positive values can be entered for this field.
                  </Form.Text>
                </Form.Group>
              )}
              {fieldForm.data_type === 'autonumber' && fieldForm.data_type !== 'calculated' && (
                <div className="autonumber-config">
                  {editingField && (
                    <Alert variant="info" className="mb-3">
                      <strong>Note:</strong> Autonumber configuration cannot be changed after field creation to maintain data integrity.
                    </Alert>
                  )}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Number *</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          value={fieldForm.start_number || 1}
                          onChange={(e) => {
                            const val = Math.max(1, Number(e.target.value || 1));
                            setFieldForm({ ...fieldForm, start_number: val });
                          }}
                          onBlur={() => setTouched(prev => ({ ...prev, start_number: true }))}
                          placeholder="Enter starting number (e.g., 1)"
                          readOnly={editingField !== null || !canEditFieldProperties}
                          disabled={editingField !== null || !canEditFieldProperties}
                          className={(editingField !== null || !canEditFieldProperties) ? 'bg-light' : ''}
                        />
                        <Form.Text className="text-muted">
                          {editingField
                            ? 'Start number cannot be changed after field creation.'
                            : 'The first record will get this number, and subsequent records will increment by 1.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prefix</Form.Label>
                        <Form.Control
                          type="text"
                          value={fieldForm.prefix || ''}
                          onChange={(e) => setFieldForm({ ...fieldForm, prefix: e.target.value })}
                          placeholder="e.g., ACC-, CUST-, INV-"
                          readOnly={editingField !== null || !canEditFieldProperties}
                          disabled={editingField !== null || !canEditFieldProperties}
                          className={(editingField !== null || !canEditFieldProperties) ? 'bg-light' : ''}
                        />
                        <Form.Text className="text-muted">
                          {editingField
                            ? 'Prefix cannot be changed after field creation.'
                            : 'Optional prefix for the autonumber (e.g., ACC-, CUST-).'
                          }
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Format Mask</Form.Label>
                        <Form.Control
                          type="text"
                          value={fieldForm.format_mask || ''}
                          onChange={(e) => setFieldForm({ ...fieldForm, format_mask: e.target.value })}
                          placeholder="e.g., 0000, 00000"
                          readOnly={editingField !== null || !canEditFieldProperties}
                          disabled={editingField !== null || !canEditFieldProperties}
                          className={(editingField !== null || !canEditFieldProperties) ? 'bg-light' : ''}
                        />
                        <Form.Text className="text-muted">
                          {editingField
                            ? 'Format mask cannot be changed after field creation.'
                            : 'Optional format mask to pad numbers (e.g., 0000 makes 1 → 0001).'
                          }
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Preview</Form.Label>
                        <Form.Control
                          type="text"
                          value={generateAutonumberPreview(
                            fieldForm.prefix,
                            fieldForm.format_mask,
                            fieldForm.start_number || 1
                          )}
                          readOnly
                          className="bg-light"
                          placeholder="Preview will appear here"
                        />
                        <Form.Text className="text-muted">
                          Preview of how the autonumber will look.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Barcode Configuration */}
              {(fieldForm.data_type === 'barcode' || (isCalculatedField && calculatedReturnType === 'barcode')) && (
                <div className="barcode-config">
                  <Alert variant="info" className="mb-3">
                    <strong>Barcode Configuration:</strong> Customize how the barcode is displayed
                  </Alert>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Barcode Format</Form.Label>
                        <Form.Select
                          value={getDisplayConfigValue('format', 'CODE128') || 'CODE128'}
                          onChange={(e) => setDisplayConfigValue('format', e.target.value)}
                        >
                          <option value="CODE128">CODE128 (Default)</option>
                          <option value="EAN13">EAN13</option>
                          <option value="UPC">UPC</option>
                          <option value="CODE39">CODE39</option>
                          <option value="ITF14">ITF14</option>
                          <option value="MSI">MSI</option>
                          <option value="pharmacode">Pharmacode</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Choose the barcode format for encoding
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Barcode Height (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('height') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('height', val === '' ? null : Number(val));
                          }}
                          placeholder="100 (default)"
                        />
                        <Form.Text className="text-muted">
                          Height of barcode (default: 100)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Bar Width</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={5}
                          step={0.5}
                          value={getDisplayConfigValue('width') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('width', val === '' ? null : Number(val));
                          }}
                          placeholder="2 (default)"
                        />
                        <Form.Text className="text-muted">
                          Width of individual bars (1-5, default: 2)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Show Value Below Barcode"
                          checked={getDisplayConfigValue('displayValue', true) !== false}
                          onChange={(e) => setDisplayConfigValue('displayValue', e.target.checked)}
                        />
                        <Form.Text className="text-muted">
                          Display the actual value text below the barcode
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* QR Code Configuration */}
              {(fieldForm.data_type === 'qrcode' || (isCalculatedField && calculatedReturnType === 'qrcode')) && (
                <div className="qrcode-config">
                  <Alert variant="info" className="mb-3">
                    <strong>QR Code Configuration:</strong> Customize how the QR code is displayed
                  </Alert>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>QR Code Size (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('size') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('size', val === '' ? null : Number(val));
                          }}
                          placeholder="200 (default)"
                        />
                        <Form.Text className="text-muted">
                          Size of QR code (100-500px, default: 200)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Error Correction Level</Form.Label>
                        <Form.Select
                          value={getDisplayConfigValue('errorCorrectionLevel', 'M') || 'M'}
                          onChange={(e) => setDisplayConfigValue('errorCorrectionLevel', e.target.value)}
                        >
                          <option value="L">L - Low (7%)</option>
                          <option value="M">M - Medium (15%)</option>
                          <option value="Q">Q - Quartile (25%)</option>
                          <option value="H">H - High (30%)</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Higher levels allow more damage tolerance
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Image Configuration */}
              {(fieldForm.data_type === 'image' || (isCalculatedField && calculatedReturnType === 'image')) && (
                <div className="image-config">
                  <Alert variant="info" className="mb-3">
                    <strong>Image Configuration:</strong> Set dimensions for desktop and mobile displays
                  </Alert>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Desktop Width (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('width') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('width', val === '' ? null : Number(val));
                          }}
                          placeholder="300 (default)"
                        />
                        <Form.Text className="text-muted">
                          Width on desktop (default: 300)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Desktop Height (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('height') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('height', val === '' ? null : Number(val));
                          }}
                          placeholder="300 (default)"
                        />
                        <Form.Text className="text-muted">
                          Height on desktop (default: 300)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Width (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('mobileWidth') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('mobileWidth', val === '' ? null : Number(val));
                          }}
                          placeholder="150 (default)"
                        />
                        <Form.Text className="text-muted">
                          Width on mobile (default: 150)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Height (px)</Form.Label>
                        <Form.Control
                          type="number"
                          value={getDisplayConfigValue('mobileHeight') ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDisplayConfigValue('mobileHeight', val === '' ? null : Number(val));
                          }}
                          placeholder="150 (default)"
                        />
                        <Form.Text className="text-muted">
                          Height on mobile (default: 150)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Object Fit</Form.Label>
                        <Form.Select
                          value={getDisplayConfigValue('objectFit', 'contain') || 'contain'}
                          onChange={(e) => setDisplayConfigValue('objectFit', e.target.value)}
                        >
                          <option value="contain">Contain (Default)</option>
                          <option value="cover">Cover</option>
                          <option value="fill">Fill</option>
                          <option value="scale-down">Scale Down</option>
                          <option value="none">None</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          How image fits within dimensions
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {(fieldForm.data_type === 'currency' || fieldForm.data_type === 'percent') && (
                <div className="d-flex gap-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Total Length (≤ 18) *</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={18}
                      value={fieldForm.precision}
                      readOnly={!canEditFieldProperties}
                      className={!canEditFieldProperties ? 'bg-light' : ''}
                      onChange={(e) => {
                        if (!canEditFieldProperties) return;
                        const val = Math.min(18, Math.max(1, Number(e.target.value || 0)));
                        let s = Number(fieldForm.scale || 0);
                        const maxScale = Math.min(MAX_NUMERIC_DECIMAL_PLACES, val);
                        if (s > maxScale) s = maxScale;
                        setFieldForm({ ...fieldForm, precision: val, scale: s });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, precision: true }))}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Decimal Places (≤ {MAX_NUMERIC_DECIMAL_PLACES}) *</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={Math.min(
                        MAX_NUMERIC_DECIMAL_PLACES,
                        Number(fieldForm.precision || 18)
                      )}
                      value={fieldForm.scale}
                      readOnly={!canEditFieldProperties}
                      className={!canEditFieldProperties ? 'bg-light' : ''}
                      onChange={(e) => {
                        if (!canEditFieldProperties) return;
                        const p = Number(fieldForm.precision || 18);
                        let s = Number(e.target.value || 0);
                        const maxScale = Math.min(MAX_NUMERIC_DECIMAL_PLACES, p);
                        if (s > maxScale) s = maxScale;
                        if (s < 0) s = 0;
                        setFieldForm({ ...fieldForm, scale: s });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, scale: true }))}
                    />
                  </Form.Group>
                </div>
              )}
            </Col>
          </Row>

          {/* Removed duplicate Max Length block; single Max Length control lives next to Data Type */}

          {fieldForm.data_type === 'lookup' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lookup Search Fields *</Form.Label>
                  <MultiSelectDropdown
                    selectedValues={Array.isArray(fieldForm.lookup_search_fields) ? fieldForm.lookup_search_fields : (fieldForm.lookup_search_fields || '').split(',').map(s => s.trim()).filter(Boolean)}
                    picklistValues={lookupSearchFieldOptions}
                    onChange={(selected) => setFieldForm({ ...fieldForm, lookup_search_fields: selected })}
                    placeholder="Select search fields"
                    disabled={!fieldForm.reference_object || !canEditFieldProperties}
                  />
                  {modalTouchedSave && (!fieldForm.lookup_search_fields || (Array.isArray(fieldForm.lookup_search_fields) && fieldForm.lookup_search_fields.length === 0)) && (
                    <Form.Text className="text-danger">Select at least one search field</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Fields *</Form.Label>
                  <MultiSelectDropdown
                    selectedValues={Array.isArray(fieldForm.display_fields) ? fieldForm.display_fields : (fieldForm.display_fields || '').split(',').map(s => s.trim()).filter(Boolean)}
                    picklistValues={lookupFieldOptions}
                    onChange={(selected) => setFieldForm({ ...fieldForm, display_fields: selected })}
                    placeholder="Select display fields"
                    disabled={!fieldForm.reference_object || !canEditFieldProperties}
                  />
                  {modalTouchedSave && (!fieldForm.display_fields || (Array.isArray(fieldForm.display_fields) && fieldForm.display_fields.length === 0)) && (
                    <Form.Text className="text-danger">Select at least one display field</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}

          {fieldForm.data_type === 'lookup' && Array.isArray(fieldForm.display_fields) && fieldForm.display_fields.length > 0 && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Value Preview</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    value={fieldForm.display_fields.join(' ')}
                  />
                  <Form.Text className="text-muted">Preview shows how display value will be constructed.</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Lookup Filter Configuration */}
          {fieldForm.data_type === 'lookup' && fieldForm.reference_object && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Lookup Filters (Optional)</Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        if (!canEditFieldProperties) return;
                        setFieldForm(prev => {
                          const currentFilters = prev.lookup_filter?.filters || [];
                          const currentLogic = prev.lookup_filter?.logic || 'AND';
                          return {
                            ...prev,
                            lookup_filter: {
                              logic: currentLogic,
                              filters: [...currentFilters, emptyLookupFilterRow()]
                            }
                          };
                        });
                      }}
                      disabled={!fieldForm.reference_object || !canEditFieldProperties}
                    >
                      <FaPlus className="me-1" />
                      Add Filter
                    </Button>
                  </div>

                  {(() => {
                    const lookupFilters = fieldForm.lookup_filter?.filters || [];
                    const validFilterCount = lookupFilters.filter(isCompleteListViewFilter).length;
                    const logicSelectValue = isSimpleFilterLogic(fieldForm.lookup_filter?.logic)
                      ? String(fieldForm.lookup_filter?.logic || 'AND').trim().toUpperCase()
                      : 'CUSTOM';

                    return (
                      <>
                        {lookupFilters.length > 0 && (
                          <div className="mb-3">
                            <Form.Label className="small">Filter Logic</Form.Label>
                            <Form.Select
                              size="sm"
                              value={logicSelectValue}
                              disabled={!canEditFieldProperties}
                              onChange={(e) => {
                                if (!canEditFieldProperties) return;
                                const v = e.target.value;
                                setFieldForm(prev => ({
                                  ...prev,
                                  lookup_filter: {
                                    ...(prev.lookup_filter || { filters: [] }),
                                    logic:
                                      v === 'CUSTOM'
                                        ? defaultCustomFilterLogicExpression(validFilterCount)
                                        : v,
                                  }
                                }));
                              }}
                              className="mb-2"
                            >
                              <option value="AND">AND (All conditions must match)</option>
                              <option value="OR">OR (Any condition can match)</option>
                              <option value="CUSTOM">Custom (e.g. (1 OR 2) AND 3)</option>
                            </Form.Select>
                            {logicSelectValue === 'CUSTOM' && (
                              <>
                                <Form.Label className="small text-muted mb-1">Custom expression</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  size="sm"
                                  value={String(fieldForm.lookup_filter?.logic ?? '')}
                                  disabled={!canEditFieldProperties}
                                  onChange={(e) => {
                                    if (!canEditFieldProperties) return;
                                    setFieldForm(prev => ({
                                      ...prev,
                                      lookup_filter: {
                                        ...(prev.lookup_filter || { filters: [] }),
                                        logic: e.target.value,
                                      }
                                    }));
                                  }}
                                  placeholder="(1 OR 2) AND 3"
                                  className="font-monospace small"
                                />
                                <Form.Text className="text-muted small d-block mt-1">
                                  Use numbers 1, 2, 3… for each filter row below, with AND, OR, and parentheses.
                                </Form.Text>
                              </>
                            )}
                          </div>
                        )}

                        <Form.Text className="text-muted d-block mb-2">
                          Filter parent records when selecting from this lookup field.
                          {lookupFilters.length > 1
                            ? isSimpleFilterLogic(fieldForm.lookup_filter?.logic)
                              ? ` Conditions will be combined with ${fieldForm.lookup_filter?.logic || 'AND'} logic.`
                              : ' Conditions use the custom logic expression above.'
                            : ' Only records matching all conditions will be shown.'}{' '}
                          In the value column, choose <strong>Field</strong> to bind another field on this object
                          (stored as <code className="small">{'{{field_name}}'}</code> and resolved at search time).
                        </Form.Text>

                        {lookupFilters.length === 0 ? (
                          <Alert variant="info" className="mb-0">
                            No filters configured. All records from {fieldForm.reference_object} will be available.
                          </Alert>
                        ) : (
                          <div className="lookup-filters-container">
                            {lookupFilters.map((filter, index) => (
                              <div key={index} className="border rounded p-3 mb-3 bg-light">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="mb-0 small">Filter {index + 1}</h6>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={!canEditFieldProperties}
                                      onClick={() => {
                                        if (!canEditFieldProperties) return;
                                        setFieldForm(prev => {
                                          const currentFilters = (prev.lookup_filter?.filters || []).filter(
                                            (_, i) => i !== index
                                          );
                                          return {
                                            ...prev,
                                            lookup_filter: {
                                              logic: prev.lookup_filter?.logic || 'AND',
                                              filters: currentFilters
                                            }
                                          };
                                        });
                                      }}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                  <ConditionRow
                                    condition={filter}
                                    fields={lookupFilterFields}
                                    index={index}
                                    readOnly={!canEditFieldProperties}
                                    options={{
                                      fieldKey: 'field',
                                      nameKey: 'field_name',
                                      labelKey: 'field_label',
                                      fieldColSize: 4,
                                      operatorColSize: 3,
                                      valueColSize: 5,
                                      useSearchableDropdown: true,
                                      valueFields: lookupFilterValueFields.length
                                        ? lookupFilterValueFields
                                        : fields,
                                      allowFieldValue: true,
                                      allowLoggedUserValue: false,
                                      layoutObjectName: fieldForm.reference_object,
                                      onPatch: (idx, patch) => {
                                        setFieldForm(prev => {
                                          const currentFilters = [...(prev.lookup_filter?.filters || [])];
                                          currentFilters[idx] = {
                                            ...currentFilters[idx],
                                            ...patch,
                                          };
                                          return {
                                            ...prev,
                                            lookup_filter: {
                                              logic: prev.lookup_filter?.logic || 'AND',
                                              filters: currentFilters,
                                            },
                                          };
                                        });
                                      },
                                    }}
                                    onChange={(idx, key, val) => {
                                      setFieldForm(prev => {
                                        const currentFilters = [...(prev.lookup_filter?.filters || [])];
                                        currentFilters[idx] = {
                                          ...currentFilters[idx],
                                          [key]: val,
                                        };
                                        return {
                                          ...prev,
                                          lookup_filter: {
                                            logic: prev.lookup_filter?.logic || 'AND',
                                            filters: currentFilters,
                                          },
                                        };
                                      });
                                    }}
                                  />
                                </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </Form.Group>
              </Col>
            </Row>
          )}

          {fieldForm.data_type === 'rollupsummary' && (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rollup Field *</Form.Label>
                    <Form.Select
                      value={fieldForm.rollup_field || ''}
                      onChange={(e) => setFieldForm({ ...fieldForm, rollup_field: e.target.value })}
                      onBlur={() => setTouched(prev => ({ ...prev, rollup_field: true }))}
                      isInvalid={(touched.rollup_field || modalTouchedSave) && !fieldForm.rollup_field}
                      disabled={!fieldForm.reference_object || editingField || !canEditFieldProperties}
                    >
                      <option value="">-- Select Field --</option>
                      {lookupFieldOptions.map(field => (
                        <option key={field.key} value={field.key}>{field.value}</option>
                      ))}
                    </Form.Select>
                    {(touched.rollup_field || modalTouchedSave) && !fieldForm.rollup_field && (
                      <Form.Text className="text-danger">Rollup field is required</Form.Text>
                    )}
                    <Form.Text className="text-muted">
                      {editingField
                        ? 'Rollup field cannot be changed after creation'
                        : 'Select the field to aggregate from the related object'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rollup Operation *</Form.Label>
                    <Form.Select
                      value={fieldForm.rollup_operation || ''}
                      onChange={(e) => setFieldForm({ ...fieldForm, rollup_operation: e.target.value })}
                      onBlur={() => setTouched(prev => ({ ...prev, rollup_operation: true }))}
                      isInvalid={(touched.rollup_operation || modalTouchedSave) && !fieldForm.rollup_operation}
                      disabled={fieldForm.rollup_field === 'id' || editingField || !canEditFieldProperties}
                    >
                      <option value="">-- Select Operation --</option>
                      {fieldForm.rollup_field === 'id' ? (
                        <option value="COUNT">Count</option>
                      ) : (
                        <>
                          <option value="SUM">Sum</option>
                          <option value="COUNT">Count</option>
                          <option value="MIN">Minimum</option>
                          <option value="MAX">Maximum</option>
                          <option value="AVG">Average</option>
                        </>
                      )}
                    </Form.Select>
                    {(touched.rollup_operation || modalTouchedSave) && !fieldForm.rollup_operation && (
                      <Form.Text className="text-danger">Rollup operation is required</Form.Text>
                    )}
                    <Form.Text className="text-muted">
                      {editingField
                        ? 'Rollup operation cannot be changed after creation'
                        : (fieldForm.rollup_field === 'id'
                          ? 'ID field selected - only COUNT operation is available'
                          : 'Select the aggregation operation to perform')
                      }
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Output Type for Rollup Summary */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Output Type *</Form.Label>
                    <Form.Select
                      value={(() => {
                        const displayConfig = ensurePlainObject(fieldForm.display_config);
                        const rollupConfig = ensurePlainObject(displayConfig.rollupSummary);
                        return rollupConfig.outputType || 'number';
                      })()}
                      onChange={(e) => {
                        const outputType = e.target.value;
                        const currentDisplayConfig = ensurePlainObject(fieldForm.display_config);
                        setFieldForm({
                          ...fieldForm,
                          display_config: {
                            ...currentDisplayConfig,
                            rollupSummary: {
                              ...ensurePlainObject(currentDisplayConfig.rollupSummary),
                              outputType: outputType
                            }
                          }
                        });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, rollup_output_type: true }))}
                    >
                      <option value="number">Number</option>
                      <option value="currency">Currency</option>
                      <option value="percent">Percent</option>
                      <option value="date">Date</option>
                      <option value="datetime">Date/Time</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Select how the rollup summary value should be formatted. Auto-detected based on selected rollup field.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Decimal Places (0-6)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="6"
                      value={fieldForm.scale || ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : Math.max(0, Math.min(6, Number(e.target.value)));
                        setFieldForm({ ...fieldForm, scale: val, precision: val ? 18 : '' });
                      }}
                      onBlur={() => setTouched(prev => ({ ...prev, scale: true }))}
                      placeholder="e.g., 2 for 123.45"
                      disabled={(() => {
                        const displayConfig = ensurePlainObject(fieldForm.display_config);
                        const rollupConfig = ensurePlainObject(displayConfig.rollupSummary);
                        const outputType = rollupConfig.outputType || 'number';
                        return outputType === 'date' || outputType === 'datetime';
                      })()}
                    />
                    <Form.Text className="text-muted">
                      Number of digits after decimal point. Leave empty for whole numbers. Max 6 decimal places. Not applicable for date/datetime.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Preview</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={(() => {
                        const displayConfig = ensurePlainObject(fieldForm.display_config);
                        const rollupConfig = ensurePlainObject(displayConfig.rollupSummary);
                        const outputType = rollupConfig.outputType || 'number';
                        const scale = fieldForm.scale && fieldForm.scale !== '' ? Number(fieldForm.scale) : 0;

                        if (outputType === 'currency') {
                          return `Example: ₹${(1234.56789).toFixed(scale)}`;
                        } else if (outputType === 'date') {
                          return 'Example: 2024-01-15';
                        } else if (outputType === 'datetime') {
                          return 'Example: 2024-01-15 10:30 AM';
                        } else {
                          return scale > 0
                            ? `Example: ${(1234.56789).toFixed(scale)}`
                            : 'Example: 1234';
                        }
                      })()}
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      How the rollup summary value will be displayed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Optional filter criteria — only matching child records are aggregated */}
              {fieldForm.reference_object && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="mb-1">Filter Criteria (Optional)</Form.Label>
                      <Form.Text className="text-muted d-block mb-2">
                        Only child records matching these conditions are included in the rollup.
                      </Form.Text>
                      {(fieldForm.rollup_criteria?.conditions || []).length > 1 && (
                        <Row className="mb-2">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="small">Filter Logic</Form.Label>
                              <Form.Select
                                size="sm"
                                value={fieldForm.rollup_criteria?.logic || 'AND'}
                                disabled={!canEditFieldProperties}
                                onChange={(e) => {
                                  if (!canEditFieldProperties) return;
                                  setFieldForm((prev) => ({
                                    ...prev,
                                    rollup_criteria: {
                                      ...(prev.rollup_criteria || { conditions: [] }),
                                      logic: e.target.value
                                    }
                                  }));
                                }}
                              >
                                <option value="AND">AND (All conditions must match)</option>
                                <option value="OR">OR (Any condition can match)</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      <SharingRuleCriteriaConfig
                        criteriaList={fieldForm.rollup_criteria?.conditions || []}
                        onAdd={() => {
                          setFieldForm((prev) => {
                            const currentConditions = prev.rollup_criteria?.conditions || [];
                            const currentLogic = prev.rollup_criteria?.logic || 'AND';
                            return {
                              ...prev,
                              rollup_criteria: {
                                logic: currentLogic,
                                conditions: [...currentConditions, emptyRollupCriterion()]
                              }
                            };
                          });
                        }}
                        onDelete={(index) => {
                          setFieldForm((prev) => {
                            const currentConditions = (prev.rollup_criteria?.conditions || [])
                              .filter((_, i) => i !== index);
                            return {
                              ...prev,
                              rollup_criteria: {
                                logic: prev.rollup_criteria?.logic || 'AND',
                                conditions: currentConditions
                              }
                            };
                          });
                        }}
                        onChange={(index, key, value) => {
                          setFieldForm((prev) => {
                            const currentConditions = [...(prev.rollup_criteria?.conditions || [])];
                            currentConditions[index] = {
                              ...currentConditions[index],
                              [key]: value
                            };
                            return {
                              ...prev,
                              rollup_criteria: {
                                logic: prev.rollup_criteria?.logic || 'AND',
                                conditions: currentConditions
                              }
                            };
                          });
                        }}
                        onPatch={(index, patch) => {
                          setFieldForm((prev) => {
                            const currentConditions = [...(prev.rollup_criteria?.conditions || [])];
                            currentConditions[index] = {
                              ...currentConditions[index],
                              ...patch
                            };
                            return {
                              ...prev,
                              rollup_criteria: {
                                logic: prev.rollup_criteria?.logic || 'AND',
                                conditions: currentConditions
                              }
                            };
                          });
                        }}
                        objectFields={rollupCriteriaFields}
                        readOnly={!canEditFieldProperties}
                        disabled={!fieldForm.reference_object || !canEditFieldProperties}
                        allowFieldValue={false}
                        allowLoggedUserValue={false}
                        compact
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </>
          )}

          {(fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && !editingField && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <Form.Label className="mb-0">Picklist values *</Form.Label>
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setFieldForm((prev) => ({
                          ...prev,
                          picklist_values: [...(prev.picklist_values || []), emptyPicklistEditorRow()]
                        }));
                      }}
                    >
                      <FaPlus className="me-1" />
                      Add row
                    </Button>
                  </div>
                  <div className="border rounded p-2 mb-2 bg-light">
                    <Form.Label className="mb-1">Add multiple values (one per line)</Form.Label>
                    <div className="d-flex gap-2 align-items-start flex-wrap">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={bulkPicklistValues}
                        onChange={(e) => setBulkPicklistValues(e.target.value)}
                        placeholder={'Open\nqualified|Qualified\nClosed #2e7d32'}
                      />
                      <div className="d-flex justify-content-end gap-2" style={{ minWidth: '100%' }}>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={handleAddPicklistRowsBulk}
                          disabled={!bulkPicklistValues.trim()}
                        >
                          Add All
                        </Button>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setBulkPicklistValues('')}
                          disabled={!bulkPicklistValues}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                      <Form.Label className="mb-0 small text-muted">Default path color (new lines)</Form.Label>
                      <Form.Control
                        type="color"
                        value={
                          /^#([0-9A-Fa-f]{6})$/.test((bulkPicklistPathColor || '').trim())
                            ? bulkPicklistPathColor.trim().toLowerCase()
                            : '#6c757d'
                        }
                        onChange={(e) => setBulkPicklistPathColor(e.target.value)}
                        title="Applied unless a line ends with its own #hex color"
                        style={{ width: '36px', height: '30px', padding: '2px' }}
                      />
                      <Form.Control
                        size="sm"
                        placeholder="#RRGGBB"
                        value={bulkPicklistPathColor}
                        onChange={(e) => setBulkPicklistPathColor(e.target.value)}
                        style={{ width: '88px' }}
                      />
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setBulkPicklistPathColor('')}
                      >
                        Clear
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      Same label and API unless you use <code>api_name|Label</code>. Optional color: end a line with{' '}
                      <code>#RRGGBB</code>.
                    </Form.Text>
                  </div>
                  {(touched.picklist_values || modalTouchedSave) &&
                    (!fieldForm.picklist_values || fieldForm.picklist_values.length === 0) && (
                      <Form.Text className="text-danger d-block mb-2">Add at least one picklist value</Form.Text>
                    )}
                  {fieldForm.picklist_values && fieldForm.picklist_values.length > 0 ? (
                    <div className="table-responsive border rounded">
                      <Table borderless hover size="sm" className="mb-0 field-manager-picklist-table">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '56px' }} className="text-muted small">Order</th>
                            <th>Value label</th>
                            <th style={{ minWidth: '140px' }} className="text-muted small">API name</th>
                            <th style={{ width: '88px' }} className="text-center text-muted small">Default</th>
                            <th style={{ minWidth: '120px' }} className="text-muted small">Path color</th>
                            <th style={{ width: '44px' }} />
                          </tr>
                        </thead>
                        <tbody>
                          {fieldForm.picklist_values.map((row, index) => {
                            const n = fieldForm.picklist_values.length;
                            const swapRows = (i, j) => {
                              setFieldForm((prev) => {
                                const vals = [...(prev.picklist_values || [])];
                                if (i < 0 || j < 0 || i >= vals.length || j >= vals.length) return prev;
                                [vals[i], vals[j]] = [vals[j], vals[i]];
                                let def = prev.picklist_default_index;
                                if (def === i) def = j;
                                else if (def === j) def = i;
                                return {
                                  ...prev,
                                  picklist_values: vals,
                                  picklist_default_index: def
                                };
                              });
                            };
                            return (
                              <tr key={index}>
                                <td className="align-middle">
                                  <div className="d-flex flex-column gap-0 field-manager-picklist-reorder">
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="p-0 lh-1 text-secondary"
                                      disabled={index === 0}
                                      aria-label="Move up"
                                      onClick={() => swapRows(index, index - 1)}
                                    >
                                      <FaChevronUp size={12} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="p-0 lh-1 text-secondary"
                                      disabled={index >= n - 1}
                                      aria-label="Move down"
                                      onClick={() => swapRows(index, index + 1)}
                                    >
                                      <FaChevronDown size={12} />
                                    </Button>
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <Form.Control
                                    size="sm"
                                    value={row.value || ''}
                                    placeholder="e.g. Hot"
                                    onBlur={() => setTouched((prev) => ({ ...prev, picklist_values: true }))}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFieldForm((prev) => ({
                                        ...prev,
                                        picklist_values: prev.picklist_values.map((r, idx) => {
                                          if (idx !== index) return r;
                                          const follow = r._apiFollowsLabel !== false;
                                          const key = follow
                                            ? suggestPicklistApiNameFromLabel(value)
                                            : r.key;
                                          return { ...r, value, key };
                                        })
                                      }));
                                    }}
                                  />
                                </td>
                                <td className="align-middle">
                                  <Form.Control
                                    size="sm"
                                    className="field-manager-picklist-api-input"
                                    value={row.key || ''}
                                    placeholder="auto"
                                    onBlur={() => setTouched((prev) => ({ ...prev, picklist_values: true }))}
                                    onChange={(e) => {
                                      setFieldForm((prev) => ({
                                        ...prev,
                                        picklist_values: prev.picklist_values.map((r, idx) =>
                                          idx === index
                                            ? { ...r, key: e.target.value, _apiFollowsLabel: false }
                                            : r
                                        )
                                      }));
                                    }}
                                  />
                                </td>
                                <td className="align-middle text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={index === fieldForm.picklist_default_index}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldForm({ ...fieldForm, picklist_default_index: index });
                                      } else {
                                        setFieldForm({
                                          ...fieldForm,
                                          picklist_default_index: null
                                        });
                                      }
                                    }}
                                    aria-label={`Default value row ${index + 1}`}
                                  />
                                </td>
                                <td className="align-middle">
                                  <div className="d-flex align-items-center gap-1 flex-wrap">
                                    <Form.Control
                                      type="color"
                                      title="Path stage color (optional)"
                                      value={
                                        /^#([0-9A-Fa-f]{6})$/.test((row.path_color || '').trim())
                                          ? (row.path_color || '').trim().toLowerCase()
                                          : '#6c757d'
                                      }
                                      onChange={(e) => {
                                        setFieldForm((prev) => ({
                                          ...prev,
                                          picklist_values: prev.picklist_values.map((r, idx) =>
                                            idx === index ? { ...r, path_color: e.target.value } : r
                                          )
                                        }));
                                      }}
                                      style={{ width: '36px', height: '30px', padding: '2px' }}
                                    />
                                    <Form.Control
                                      size="sm"
                                      placeholder="#RRGGBB"
                                      value={row.path_color || ''}
                                      onChange={(e) => {
                                        setFieldForm((prev) => ({
                                          ...prev,
                                          picklist_values: prev.picklist_values.map((r, idx) =>
                                            idx === index ? { ...r, path_color: e.target.value } : r
                                          )
                                        }));
                                      }}
                                      style={{ width: '88px' }}
                                    />
                                  </div>
                                </td>
                                <td className="align-middle text-end">
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="p-0 text-danger"
                                    title="Remove row"
                                    onClick={() => {
                                      setFieldForm((prev) => {
                                        const vals = [...(prev.picklist_values || [])];
                                        vals.splice(index, 1);
                                        let def = prev.picklist_default_index;
                                        if (def === index) def = null;
                                        else if (def > index) def -= 1;
                                        return {
                                          ...prev,
                                          picklist_values: vals,
                                          picklist_default_index: def
                                        };
                                      });
                                    }}
                                  >
                                    <FaTrash size={14} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted small mb-0">No values yet. Click &quot;Add row&quot; to define options.</p>
                  )}
                  <Form.Text className="text-muted d-block mt-2">
                    API name defaults from the label as-is. Edit it when you need a stable key for integrations.
                    Optional path color uses #RGB or #RRGGBB (shown on path fields).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {(fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && editingField && (
            <Row>
              <Col md={12}>
                <Alert variant="info">
                  <strong>Picklist Values Management:</strong> Use &quot;Manage Values&quot; in the field list to add options,
                  edit API keys and labels, set defaults, reorder, and deactivate or delete values.
                </Alert>
              </Col>
            </Row>
          )}

          {fieldForm.data_type === 'calculated' && (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Formula Return Type *</Form.Label>
                    <Form.Select
                      value={calculatedConfig.returnType}
                      onChange={(e) => updateCalculatedConfig({ returnType: e.target.value })}
                    >
                      {CALCULATED_RETURN_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Decide how the expression result should be stored and validated.
                    </Form.Text>
                  </Form.Group>
                </Col>
                {NUMERIC_RETURN_TYPES.includes(calculatedConfig.returnType) && (
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Decimal Places</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={6}
                        value={calculatedConfig.decimalPlaces ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateCalculatedConfig({ decimalPlaces: null });
                          } else {
                            const parsed = parseInt(value, 10);
                            updateCalculatedConfig({ decimalPlaces: Number.isNaN(parsed) ? null : parsed });
                          }
                        }}
                      />
                      <Form.Text className="text-muted">
                        Choose between 0 and 6 decimal places.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {calculatedConfig.returnType === 'text' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Format Pattern (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Optional formatting hint for UI consumers"
                        value={calculatedConfig.formatPattern || ''}
                        onChange={(e) => updateCalculatedConfig({ formatPattern: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        Provide a display hint, e.g., a template or transformation note.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Calculated Expression *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Enter the calculated expression (e.g., CASE WHEN amount > 0 THEN amount END)"
                      value={fieldForm.calculated_expression || ''}
                      readOnly={!canEditFieldProperties}
                      className={!canEditFieldProperties ? 'bg-light' : ''}
                      onChange={(e) => {
                        if (!canEditFieldProperties) return;
                        const newValue = e.target.value;
                        setFieldForm({ ...fieldForm, calculated_expression: newValue });
                        // Auto-validate after a short delay when user stops typing
                        if (autoValidationTimeout) {
                          clearTimeout(autoValidationTimeout);
                        }
                        const timeout = setTimeout(() => {
                          if (newValue.trim() && newValue.trim().length > 3) {
                            validateFormula(newValue);
                          } else {
                            setFormulaValidationResult(null);
                          }
                        }, 1000); // 1 second delay
                        setAutoValidationTimeout(timeout);
                      }}
                      onBlur={() => {
                        setTouched(prev => ({ ...prev, calculated_expression: true }));
                        // Validate expression on blur if it has content
                        if (fieldForm.calculated_expression && fieldForm.calculated_expression.trim()) {
                          validateFormula(fieldForm.calculated_expression);
                        }
                      }}
                      isInvalid={(touched.calculated_expression || modalTouchedSave) && !fieldForm.calculated_expression}
                    />
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => validateFormula(fieldForm.calculated_expression)}
                        disabled={!fieldForm.calculated_expression || validatingFormula || !canEditFieldProperties}
                      >
                        {validatingFormula ? 'Validating...' : 'Validate Formula'}
                      </Button>
                    </div>
                    {(touched.calculated_expression || modalTouchedSave) && !fieldForm.calculated_expression && (
                      <Form.Text className="text-danger">Calculated expression is required</Form.Text>
                    )}
                    {formulaValidationResult && (
                      <div className={`alert ${formulaValidationResult.success ? 'alert-success' : 'alert-danger'} py-2`}>
                        <small>
                          <strong>{formulaValidationResult.success ? '✓' : '✗'} {formulaValidationResult.message}</strong>
                          {formulaValidationResult.details && (
                            <div className="mt-1">
                              <small>{formulaValidationResult.details}</small>
                            </div>
                          )}
                        </small>
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Enter the expression that will be calculated at runtime. This field will be calculated dynamically based on other field values.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Old text fields removed; multi-selects already provided above for lookup */}

          <div className="mb-3">
            <h6 className="mb-2">Field Options</h6>
            <div className="d-flex flex-wrap align-items-center gap-4">
              {fieldForm.data_type !== 'calculated' && fieldForm.data_type !== 'rollupsummary' && (
                <Form.Check
                  type="checkbox"
                  label="Required"
                  checked={fieldForm.is_required}
                  disabled={!canEditFieldProperties}
                  onChange={(e) => setFieldForm({ ...fieldForm, is_required: e.target.checked })}
                />
              )}
              {(fieldForm.data_type === 'text' || fieldForm.data_type === 'phone'
                || fieldForm.data_type === 'barcode'
                || fieldForm.data_type === 'email' || fieldForm.data_type === 'autonumber') && fieldForm.data_type !== 'calculated' && fieldForm.data_type !== 'rollupsummary' && (
                  <Form.Check
                    type="checkbox"
                    label="Unique"
                    checked={fieldForm.data_type === 'autonumber' ? true : !!fieldForm.is_unique}
                    disabled={!canEditFieldProperties || fieldForm.data_type === 'autonumber'}
                    onChange={(e) => setFieldForm({ ...fieldForm, is_unique: e.target.checked })}
                  />
                )}
              {allowStandardField && canEditStandard && objectIsStandard && (
                <Form.Check
                  type="checkbox"
                  label="Standard Field"
                  checked={fieldForm.is_standard}
                  onChange={(e) =>
                    setFieldForm({ ...fieldForm, is_standard: e.target.checked })
                  }
                />
              )}
              {(fieldForm.data_type === 'picklist' || fieldForm.data_type === 'multipicklist') && (
                <div>
                  <Form.Check
                    type="checkbox"
                    label="Path Field"
                    checked={fieldForm.is_path_field}
                    onChange={(e) => setFieldForm({ ...fieldForm, is_path_field: e.target.checked })}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginTop: '-5px' }}>
                    Enable visual stage tracking for this picklist field
                  </Form.Text>
                </div>
              )}
              {(canEnableFieldMasking(fieldForm.data_type)) && (
                <div>
                  <Form.Check
                    type="checkbox"
                    label="Mask value in UI"
                    checked={!!fieldForm.config?.mask_value}
                    disabled={!canEditFieldProperties}
                    onChange={(e) =>
                      setFieldForm({
                        ...fieldForm,
                        config: {
                          ...(fieldForm.config || {}),
                          mask_value: e.target.checked,
                        },
                      })
                    }
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginTop: '-5px' }}>
                    Hide stored values in detail, list, edit, and report views
                  </Form.Text>
                </div>
              )}
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseFieldModal}>
          Cancel
        </Button>
        {!editingField && (
          <Button
            variant="outline-primary"
            onClick={() => handleSaveField(true)} // true indicates "save and new"
            disabled={loading || !fieldForm.field_name || !fieldForm.field_label}
          >
            {loading ? 'Saving...' : 'Save & New'}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => handleSaveField(false)} // false indicates regular save
          disabled={loading || !fieldForm.field_name || !fieldForm.field_label}
        >
          {loading ? 'Saving...' : (editingField ? 'Update Field' : 'Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // If showing dependent field manager page, render that instead
  if (showDependentFieldManager) {
    return (
      <DependentFieldManager
        objectName={objectName}
        objectId={objectId}
        fields={fields}
        onBack={() => setShowDependentFieldManager(false)}
        onNavigate={onNavigate}
      />
    );
  }

  if (showFieldMappingModal) {
    return (
      <FieldMappingManager
        variant="page"
        show={showFieldMappingModal}
        onClose={handleCloseFieldMappingModal}
        allowMigrationConfig={allowMigrationConfig}
        redirectPreference={redirectPreference}
        setRedirectPreference={setRedirectPreference}
        enabledMappingObjects={enabledMappingObjects}
        onMappingObjectSelection={handleMappingObjectSelection}
        loadingStageOptions={loadingStageOptions}
        defaultOpportunityStage={defaultOpportunityStage}
        setDefaultOpportunityStage={setDefaultOpportunityStage}
        opportunityStageOptions={opportunityStageOptions}
        loadingMappingFields={loadingMappingFields}
        fields={fields}
        objectIds={objectIds}
        mappingTargets={mappingTargets}
        isMappingCompleted={isMappingCompleted}
        getCurrentMapping={getCurrentMapping}
        handleMappingChange={handleMappingChange}
        getAvailableFields={getAvailableFields}
        saveFieldMappings={saveFieldMappings}
        savingMappings={savingMappings}
        dontMarkConverted={dontMarkConverted}
        setDontMarkConverted={setDontMarkConverted}
        leadConvertStatusSelectKey={leadConvertStatusSelectKey}
        setLeadConvertStatusSelectKey={setLeadConvertStatusSelectKey}
        leadStatusPicklistForConvertHide={leadStatusPicklistForConvertHide}
      />
    );
  }

  return (
    <div className="field-manager">
      {!objectName ? (
        <div className="text-center py-4">
          <p>No object selected. Please select an object to manage fields.</p>
        </div>
      ) : (
        <>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {initialLoading && (
            <div className="text-center py-4">
              <div className="loading-spinner mb-3"></div>
              <p>Loading field manager...</p>
            </div>
          )}

          {!initialLoading && (
            <>
              {renderHeader()}

              <div className="field-manager-content mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5>
                      Fields for {objectDisplayLabel}
                      {sortBy && (
                        <small className="text-muted ms-2">
                          - Sorted by {sortBy === 'field_label' ? 'Label' :
                            sortBy === 'field_name' ? 'API Name' :
                              sortBy === 'data_type' ? 'Type' :
                                sortBy === 'is_required' ? 'Required' :
                                  sortBy === 'reference_object' ? 'Reference' :
                                    sortBy === 'createddate' ? 'Created Date' : sortBy}
                          ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
                        </small>
                      )}
                    </h5>
                  </div>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Search fields by label or API name..."
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      style={{ maxWidth: 300 }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setShowTrashModal(true);
                        if (trashedFields.length === 0) {
                          fetchTrashedFields();
                        }
                      }}
                      style={{ minWidth: '160px' }}
                    >
                      <FaTrash className="me-1" />
                      View Trash
                      {trashedFields.length > 0 && (
                        <Badge bg="danger" className="ms-1">
                          {trashedFields.length}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={handleOpenFieldHistory}
                      style={{ minWidth: '180px' }}
                    >
                      <FaHistory className="me-1" />
                      {fieldHistoryEnabled ? 'Edit Field History' : 'Enable Field History'}
                    </Button>
                    {allowFieldCreate && canCreateField && (
                      <Button
                        variant="primary"
                        onClick={handleCreateField}
                        style={{ minWidth: '120px' }}
                      >
                        <FaPlus className="me-1" />
                        Add Field
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {loading && fields.length === 0 ? (
                <div className="text-center py-4">
                  <div className="loading-spinner mb-3"></div>
                  <p>Loading fields...</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <table className="table table-striped table-hover align-middle">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                      <tr>
                        <th style={{ width: 60 }}>Sr.N.</th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('field_label')}
                        >
                          Label {getSortIcon('field_label')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('field_name')}
                        >
                          API Name {getSortIcon('field_name')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('data_type')}
                        >
                          Type {getSortIcon('data_type')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('is_required')}
                        >
                          Required {getSortIcon('is_required')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('reference_object')}
                        >
                          Reference {getSortIcon('reference_object')}
                        </th>
                        <th>Picklist Values</th>
                        <th
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('createddate')}
                        >
                          Created Date {getSortIcon('createddate')}
                        </th>
                        <th style={{ width: 120 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields
                        .filter((f) => {
                          if (!filter.trim()) return true;
                          const q = filter.toLowerCase();
                          return (
                            (f.field_label || '').toLowerCase().includes(q) ||
                            (f.field_name || '').toLowerCase().includes(q) ||
                            (f.data_type || '').toLowerCase().includes(q)
                          );
                        })
                        .sort((a, b) => {
                          if (!sortBy) return 0;

                          let aValue, bValue;

                          switch (sortBy) {
                            case 'field_label':
                              aValue = (a.field_label || '').toLowerCase();
                              bValue = (b.field_label || '').toLowerCase();
                              break;
                            case 'field_name':
                              aValue = (a.field_name || '').toLowerCase();
                              bValue = (b.field_name || '').toLowerCase();
                              break;
                            case 'data_type':
                              aValue = (a.data_type || '').toLowerCase();
                              bValue = (b.data_type || '').toLowerCase();
                              break;
                            case 'is_required':
                              aValue = a.is_required ? 1 : 0;
                              bValue = b.is_required ? 1 : 0;
                              break;
                            case 'reference_object':
                              aValue = (a.reference_object || '').toLowerCase();
                              bValue = (b.reference_object || '').toLowerCase();
                              break;
                            case 'createddate':
                              aValue = new Date(a.createddate || 0).getTime();
                              bValue = new Date(b.createddate || 0).getTime();
                              break;
                            default:
                              return 0;
                          }

                          if (aValue < bValue) {
                            return sortOrder === 'asc' ? -1 : 1;
                          }
                          if (aValue > bValue) {
                            return sortOrder === 'asc' ? 1 : -1;
                          }
                          return 0;
                        })
                        .map((field, index) => (
                          <tr key={field.id}>
                            <td>{index + 1}</td>
                            <td>{field.field_label}</td>
                            <td><code>{field.field_name}</code></td>
                            <td>
                              {(() => {
                                const dataType = (field.data_type || '').toUpperCase();
                                const size = formatFieldSize(field);
                                return size !== null ? `${dataType}(${size})` : dataType;
                              })()}
                            </td>
                            <td>{field.is_required ? 'Yes' : 'No'}</td>
                            <td>{field.reference_object || '-'}</td>
                            <td>
                              {(field.data_type === 'picklist' || field.data_type === 'multipicklist') ? (
                                <div className="picklist-values-display">
                                  {(() => {
                                    let values = [];
                                    if (field.picklist_values) {
                                      if (typeof field.picklist_values === 'string') {
                                        try {
                                          values = JSON.parse(field.picklist_values);
                                        } catch (e) {
                                          values = [{ key: field.picklist_values, value: field.picklist_values }];
                                        }
                                      } else if (Array.isArray(field.picklist_values)) {
                                        values = field.picklist_values;
                                      }
                                    }

                                    return (
                                      <div>
                                        {values.length > 0 ? (
                                          <>
                                            <div className="mb-1">
                                              {values.slice(0, 3).map((val, idx) => {
                                                const displayValue = typeof val === 'object' && val !== null
                                                  ? (val.value || val.key || JSON.stringify(val))
                                                  : String(val);
                                                return (
                                                  <Badge key={idx} bg="light" text="dark" className="me-1">
                                                    {displayValue}
                                                  </Badge>
                                                );
                                              })}
                                            </div>
                                            {values.length > 3 && (
                                              <small className="text-muted">
                                                +{values.length - 3} more values
                                              </small>
                                            )}
                                          </>
                                        ) : (
                                          <small className="text-muted">No values defined</small>
                                        )}
                                        <div className="mt-2">
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            disabled={!canManagePicklistValuesForField(field)}
                                            title={
                                              !canManagePicklistValuesForField(field)
                                                ? 'Only picklist and multipicklist fields support value management.'
                                                : undefined
                                            }
                                            onClick={() => {
                                              if (!canManagePicklistValuesForField(field)) return;
                                              setSelectedPicklistField(field);
                                              setShowPicklistManager(true);
                                            }}
                                          >
                                            Manage Values
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              {field.createddate ? formatDateTime(field.createddate) : '-'}
                            </td>
                            <td>
                              {(() => {
                                const systemFields = ['id', 'createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid', 'ownerid'];
                                const isSystemField = systemFields.includes(field.field_name.toLowerCase());
                                const isStandardField = field.is_standard;// === true;
                                if (isSystemField) {
                                  return (
                                    <span className="text-muted small">
                                      <i>System Field</i>
                                    </span>
                                  );
                                }

                                if (isStandardField && objectIsStandard) {
                                  return (
                                    <div className="d-flex gap-2 align-items-center">
                                      {(allowStandardFieldEdit || canEditLabel) && (
                                        <Button size="sm" variant="outline-secondary" onClick={() => handleEditField(field)} title="Edit field">
                                          <FaEdit />
                                        </Button>
                                      )}
                                      {allowStandardFieldDelete && (
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteField(field)} disabled={isFieldDeleteBusy}>
                                          <FaTrash />
                                        </Button>
                                      )}
                                      <span className="text-muted small">
                                        <i>Standard Field</i>
                                      </span>
                                    </div>
                                  );
                                }

                                return (
                                  <div className="d-flex gap-2">
                                    <Button size="sm" variant="outline-secondary" onClick={() => handleEditField(field)}>
                                      <FaEdit />
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteField(field)} disabled={isFieldDeleteBusy}>
                                      <FaTrash />
                                    </Button>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {renderFieldModal()}

              {/* Picklist Value Manager */}
              {selectedPicklistField && (
                <PicklistValueManager
                  show={showPicklistManager}
                  onHide={() => {
                    setShowPicklistManager(false);
                    fetchFields(); // Always refresh fields when modal is closed
                  }}
                  field={selectedPicklistField}
                  objectName={objectName}
                  allowValueKeyEdit={
                    selectedPicklistField
                      ? canManagePicklistValuesForField(selectedPicklistField)
                      : true
                  }
                  onValuesUpdated={() => {
                    fetchFields();
                    setShowPicklistManager(false);
                  }}
                />
              )}

              {/* Field History Component */}
              <FieldHistoryFieldMapping
                show={showFieldHistoryModal}
                onHide={handleCloseFieldHistory}
                fields={fields}
                objectName={objectName}
                objectConfigId={objectId}
                onSave={handleFieldHistorySave}
              />

              {/* Delete Confirmation Modals */}
              <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={resetDeleteModal}
                onConfirm={confirmDeleteField}
                title="Move Field to Trash"
                blockedTitle="Field In Use"
                message="Are you sure you want to move this field to trash?"
                itemName={fieldToDelete?.field_label || fieldToDelete?.field_name}
                confirmText="Move to Trash"
                cancelText="Cancel"
                variant="warning"
                isChecking={deleteModalChecking}
                checkingMessage="Checking if this field can be deleted..."
                isDeleting={deleteModalDeleting}
                loadingMessage="Moving field to trash..."
                resultMessage={deleteModalError}
                blockedContent={
                  deleteModalBlockedReferences
                    ? renderFieldReferenceSections(
                      deleteModalBlockedReferences,
                      fieldToDelete?.field_label || fieldToDelete?.field_name || 'This field'
                    )
                    : null
                }
              />

              <DeleteConfirmationModal
                show={showPermanentDeleteModal}
                onHide={resetPermanentDeleteModal}
                onConfirm={confirmPermanentlyDeleteField}
                title="Permanently Delete Field"
                message="Are you sure you want to permanently delete this field? This action cannot be undone."
                itemName={fieldToPermanentlyDelete?.field_label || fieldToPermanentlyDelete?.field_name}
                confirmText="Delete Permanently"
                cancelText="Cancel"
                isDeleting={permanentDeleteModalDeleting}
                loadingMessage="Permanently deleting field..."
                resultMessage={permanentDeleteModalError}
              />
              <Modal show={showTrashModal} onHide={() => setShowTrashModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <FaTrash className="me-2 text-danger" />
                    Trashed Fields
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {loadingTrashedFields ? (
                    <div className="text-center py-4">
                      <div className="loading-spinner mb-3"></div>
                      <p>Loading trashed fields...</p>
                    </div>
                  ) : trashedFields.length === 0 ? (
                    <div className="text-center py-4">
                      <FaTrash className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                      <h5 className="text-muted">No trashed fields</h5>
                      <p className="text-muted">Fields you move to trash will appear here</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover align-middle">
                        <thead>
                          <tr>
                            <th>Field Name</th>
                            <th>Trashed Date</th>
                            <th>Trashed By</th>
                            <th style={{ width: 200 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trashedFields.map((field) => (
                            <tr key={field.field_id}>
                              <td>
                                <code>{field.field_name}</code>
                              </td>
                              <td>
                                {field.createddate ? formatDateTime(field.createddate) : '-'}
                              </td>
                              <td>
                                {field.trashed_by_name || field.createdbyid || '-'}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => handleRestoreField(field.field_id)}
                                  >
                                    Restore
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handlePermanentlyDeleteField(field)}
                                    disabled={isFieldDeleteBusy}
                                  >
                                    Delete Permanently
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowTrashModal(false)}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Toast Notification */}
              <AppToast
                show={showToast}
                message={toastMessage}
                variant={toastVariant}
                onClose={handleToastClose}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default FieldManager;
